import React, { useState, useEffect, useRef } from "react";
import InstallPrompt from "./InstallPrompt";
import MobileInstallBanner from "./MobileInstallBanner";
import AnimatedBackground from "./AnimatedBackground";
import HistoryView from "./library/HistoryView";
import MyListView from "./library/MyListView";
import LikedView from "./library/LikedView";
import {
  Pencil,
  Sparkles,
  Film,
  Tv,
  TrendingUp,
  Heart,
  Laugh,
  Ghost,
  Brain,
  Clock,
  Star,
  ChevronRight,
  X,
  Check,
  Zap,
  Crown,
  Play,
  BookmarkCheck,
  PlusCircle,
  User,
  Users,
  LogOut,
  Share2,
  Menu,
  TrendingUp as Fire,
  Flame,
  Trophy,
  Lock,
  Calendar,
  Timer,
  Monitor,
  Layers,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Award,
  Compass,
  Shield,
} from "lucide-react";
import HilltopAd from "./HilltopAd";
import {
  db as firestoreDb,
  useAuth,
  POLICY_VERSION,
} from "../context/AuthContext";
import AuthModal from "./AuthModal";
import PrivacyDataModal from "./PrivacyDataModal";
import RazorpayCheckout from "./RazorpayCheckout";
import { PLAN, PREMIUM_LIVE } from "../config/plan";
import { sendEmailVerification } from "firebase/auth";
import Footer from "./Footer";
import StreakCalendar from "./StreakCalendar";
import ProductDemo from "./ProductDemo";
import FeedbackModal from "./FeedbackModal";
const PLATFORM_COLORS = {
  Netflix: {
    bg: "rgba(229,9,20,0.12)",
    border: "rgba(229,9,20,0.35)",
    text: "#ff6b7a",
  },
  "HBO Max": {
    bg: "rgba(138,43,226,0.12)",
    border: "rgba(138,43,226,0.35)",
    text: "#c084fc",
  },
  "Disney+": {
    bg: "rgba(16,108,218,0.12)",
    border: "rgba(16,108,218,0.35)",
    text: "#60a5fa",
  },
  "Prime Video": {
    bg: "rgba(0,168,225,0.12)",
    border: "rgba(0,168,225,0.35)",
    text: "#38bdf8",
  },
  "Apple TV+": {
    bg: "rgba(255,255,255,0.08)",
    border: "rgba(255,255,255,0.2)",
    text: "#e5e7eb",
  },
  Hulu: {
    bg: "rgba(28,231,131,0.12)",
    border: "rgba(28,231,131,0.3)",
    text: "#4ade80",
  },
};


// The three "library" pages get real URLs so the browser Back button and
// reloads behave. Everything else stays in-app state, as before.
const LIBRARY_VIEW_PATHS = {
  history: "/history",
  mylist: "/my-list",
  liked: "/liked",
};
const PATH_TO_LIBRARY_VIEW = {
  "/history": "history",
  "/my-list": "mylist",
  "/liked": "liked",
};
const normalizePath = (p) => (p || "/").replace(/\/+$/, "").toLowerCase() || "/";
const viewFromLocation = () =>
  PATH_TO_LIBRARY_VIEW[normalizePath(window.location.pathname)] || "home";

const EntertainmentRecommender = () => {
  // ─── TMDB API CONFIGURATION ───────────────────────────────────────
  const TMDB_IMAGE_BASE_URL = "https://image.tmdb.org/t/p/w500";
  // ──────────────────────────────────────────────────────────────────
  // ✅ RECOMMENDATION LIMITS - EASY TO CONFIGURE
  const RECOMMENDATION_LIMITS = {
    FREE_USERS: 2, // Movies per recommendation
    PREMIUM_USERS: 6, // Movies per recommendation
    FREE_TOTAL: 2, // For non-logged users
    FREE_PER_DAY: 5, // For logged-in free users
    PREMIUM_PER_DAY: 999, // Unlimited
  };

  const [step, setStep] = useState(1);
  const totalSteps = 5;
  // Auth
  const {
    user,
    logout,
    isPremium: userIsPremium,
    updateUserData,
    getUserData,

    addToLikedMovies, // ✅ ADD
    removeFromLikedMovies, // ✅ ADD
  } = useAuth();

  // Initialize Firestore

  const [currentView, setCurrentView] = useState(viewFromLocation); // home, quiz, results, history, premium
  const [userPreferences, setUserPreferences] = useState({
    mood: "",
    contentType: "",
    genres: [],
    tone: "",
    watchingWith: "",
  });
  const [recommendations, setRecommendations] = useState([]);
  const [watchHistory, setWatchHistory] = useState([]);
  const [trendingRecommendations, setTrendingRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [showAd, setShowAd] = useState(false);
  const [recommendationCount, setRecommendationCount] = useState(0);
  const [freeRecommendationsLeft, setFreeRecommendationsLeft] = useState(2);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPrivacyData, setShowPrivacyData] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [, setSelectedRecommendation] = useState(null);
  const [todayStreak, setTodayStreak] = useState(0);
  const [myList, setMyList] = useState([]);
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [toast, setToast] = useState(null); // ← ADD THIS
  const [showStreakPreview, setShowStreakPreview] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [streakHistory, setStreakHistory] = useState([]); // ← ADD THIS
  const [recommendationSource, setRecommendationSource] = useState("quiz"); // "quiz" or "smart"
  const [advancedFilters, setAdvancedFilters] = useState({
    minRating: null,
    yearRange: null,
    runtime: null,
    language: null,
    platform: null,
    excludeGenres: [],
  });
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistName, setWaitlistName] = useState("");
  const [waitlistConsent, setWaitlistConsent] = useState(false);
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [, setShowEmailVerificationBanner] = useState(false); // ← ADD THIS
  const [dailyRecommendations, setDailyRecommendations] = useState(0); // ← ADD THIS
  const [, setLastRecommendationDate] = useState(null); // ← ADD THIS
  const [likedMovies, setLikedMovies] = useState([]);
  const [showLikedWelcome, setShowLikedWelcome] = useState(false);
  const [likedSearchQuery, setLikedSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const searchReqRef = useRef(0); // ignores out-of-order search responses
  const viewSyncReady = useRef(false);

  // Keep the address bar in step with the library pages.
  useEffect(() => {
    if (!viewSyncReady.current) {
      viewSyncReady.current = true;
      return;
    }
    const here = normalizePath(window.location.pathname);
    const target = LIBRARY_VIEW_PATHS[currentView];
    if (target) {
      if (here !== target) window.history.pushState({ view: currentView }, "", target);
    } else if (
      currentView === "home" &&
      Object.values(LIBRARY_VIEW_PATHS).includes(here)
    ) {
      window.history.pushState({ view: "home" }, "", "/");
    }
  }, [currentView]);

  // Browser Back / Forward between those pages.
  useEffect(() => {
    const onPop = () => setCurrentView(viewFromLocation());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSessionCount, setFeedbackSessionCount] = useState(0);
  // ── Share card canvas refs ──
  const igCanvasRef = useRef(null);
  const waCanvasRef = useRef(null);
  // ADD this line after waCanvasRef:
  //const trendingLoadedRef = useRef(false);
  // In-memory storage fallback
  const inMemoryStorage = {
    data: {},
    get: function (key) {
      return this.data[key] ? { value: this.data[key] } : null;
    },
    set: function (key, value) {
      this.data[key] = value;
      return { value };
    },
  };

  // Storage helper that falls back to in-memory if window.storage unavailable
  const storage = window.storage || inMemoryStorage;

  // Inject global responsive CSS
  useEffect(() => {
    const styleId = "noryq-responsive-styles";
    if (document.getElementById(styleId)) return;
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; }
      body, #root { overflow-x: hidden; max-width: 100vw; }
      @keyframes noryqPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(245,87,108,0.4); }
  50% { box-shadow: 0 0 0 5px rgba(245,87,108,0); }
}
      @media (max-width: 768px) {
        .noryq-page-pad { padding: 1rem !important; }
        .noryq-header { margin-bottom: 1.5rem !important; }
        .noryq-hero-h2 { font-size: 2.1rem !important; line-height: 1.2 !important; margin-bottom: 1rem !important; }
        .noryq-hero-tagline { font-size: 1rem !important; margin-bottom: 1.5rem !important; }
        .noryq-stats-grid { gap: 0.75rem !important; margin-bottom: 2rem !important; }
        .noryq-trending-grid { grid-template-columns: 1fr 1fr !important; gap: 1rem !important; }
        .noryq-mood-grid { grid-template-columns: 1fr 1fr !important; gap: 0.75rem !important; }
        .noryq-content-grid { grid-template-columns: 1fr !important; gap: 1rem !important; }
        .noryq-duration-grid { grid-template-columns: 1fr 1fr !important; gap: 0.75rem !important; }
        .noryq-vibe-grid { grid-template-columns: 1fr 1fr !important; gap: 0.75rem !important; }
        .noryq-genre-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 0.5rem !important; }
        .noryq-quiz-h2 { font-size: 1.75rem !important; }
        .noryq-results-grid { grid-template-columns: 1fr !important; }
        .noryq-results-heading { font-size: 1.75rem !important; }
        .noryq-premium-heading { font-size: 2rem !important; }
        .noryq-plans-grid { grid-template-columns: 1fr !important; }
        .noryq-modal-inner { padding: 1.5rem !important; }
        .noryq-email-banner-btns { flex-wrap: wrap !important; }
        .noryq-mood-grid button { padding: 1.25rem 0.75rem !important; }
        .noryq-content-grid button { padding: 1.5rem !important; }
        .noryq-duration-grid button { padding: 1.25rem !important; }
        .noryq-vibe-grid button { padding: 1rem 0.75rem !important; }
        .noryq-stats-grid > div { padding: 1rem !important; }
        .noryq-quiz-container { padding: 0 1rem !important; }
      }

      @media (max-width: 480px) {
        .noryq-page-pad { padding: 0.75rem !important; }
        .noryq-hero-h2 { font-size: 1.65rem !important; }
        .noryq-trending-grid { grid-template-columns: 1fr !important; }
        .noryq-genre-grid { grid-template-columns: repeat(2, 1fr) !important; }
        .noryq-quiz-h2 { font-size: 1.5rem !important; }
        .noryq-results-heading { font-size: 1.5rem !important; }
        .noryq-premium-heading { font-size: 1.65rem !important; }
        .noryq-modal-inner { padding: 1.25rem !important; }
        .noryq-mood-grid button { padding: 1rem 0.5rem !important; }
        .noryq-email-icon { display: none !important; }
        .noryq-email-banner-btns button { font-size: 0.8rem !important; padding: 0.5rem 0.875rem !important; }
        .noryq-vibe-grid button { font-size: 0.85rem !important; padding: 0.875rem 0.5rem !important; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const existing = document.getElementById(styleId);
      if (existing) existing.remove();
    };
  }, []);
  //loading animation
  useEffect(() => {
    if (!isLoading) {
      setLoadingProgress(0);
      return;
    }
    setLoadingProgress(0);
    const interval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 99) {
          clearInterval(interval);
          return 99;
        }
        return prev + (99 - prev) * 0.04; // slows down as it approaches 90
      });
    }, 100);
    return () => clearInterval(interval);
  }, [isLoading]);
  // Load data from storage
  useEffect(() => {
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    loadTrendingRecommendations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsPremium(userIsPremium || false);
  }, [userIsPremium]);
  const calculateStreak = (history) => {
    if (!history || history.length === 0) return 0;
    const sorted = [...new Set(history)].sort().reverse(); // dedupe + most recent first
    const now = new Date();
    const todayLocal = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const [ry, rm, rd] = sorted[0].split("-").map(Number);
    const mostRecent = new Date(ry, rm - 1, rd);
    // Streak is dead if most recent entry is more than 1 day ago
    const diffDays = Math.round(
      (todayLocal - mostRecent) / (1000 * 60 * 60 * 24),
    );
    if (diffDays > 1) return 0;
    let streak = 0;
    for (let i = 0; i < sorted.length; i++) {
      const [y, m, d] = sorted[i].split("-").map(Number);
      const checkDate = new Date(y, m - 1, d);
      const expectedDate = new Date(mostRecent);
      expectedDate.setDate(mostRecent.getDate() - i);
      if (checkDate.toDateString() === expectedDate.toDateString()) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  };
  const loadUserData = async () => {
    try {
      if (user) {
        const userData = await getUserData();
        if (userData) {
          setWatchHistory(userData.watchHistory || []);
          setRecommendationCount(userData.recommendationCount || 0);
          setTodayStreak(userData.streak || 0);
          setMyList(userData.myList || []);
          setLikedMovies(userData.likedMovies || []);

          // ✅ Show welcome popup for new users with no liked movies
          if (!userData.likedMovies || userData.likedMovies.length === 0) {
            const hasSeenWelcome = localStorage.getItem(
              "noryq-liked-welcome-seen",
            );
            if (!hasSeenWelcome) {
              setTimeout(() => setShowLikedWelcome(true), 2000);
            }
          }
          // ✅ LOAD DAILY RECOMMENDATION COUNT
          const today = new Date().toDateString();
          const lastRecDate = userData.lastRecommendationDate
            ? new Date(userData.lastRecommendationDate).toDateString()
            : null;

          setLastRecommendationDate(lastRecDate);

          // ✅ FIX: Restore daily count from Firestore on reload
          if (lastRecDate === today) {
            setDailyRecommendations(userData.dailyRecommendations || 0);
          } else {
            setDailyRecommendations(0);
            if (userData.dailyRecommendations > 0) {
              updateUserData({ dailyRecommendations: 0 }).catch(() => {});
            }
          }

          // Load streak history
          // Load streak history
          const history = userData.streakHistory || [];
          setStreakHistory(history);

          // Calculate current streak even if not updated today
          if (history.length > 0) {
            setTodayStreak(calculateStreak(history));
          }
          const count = userData.recommendationCount || 0;
          setFreeRecommendationsLeft(Math.max(0, 2 - count));
        }

        // ✅ CHECK EMAIL VERIFICATION
        if (user && !user.emailVerified) {
          setShowEmailVerificationBanner(true);
        } else {
          setShowEmailVerificationBanner(false);
        }
      } else {
        // Load from storage OR localStorage (fallback)
        const historyResult = await storage.get("watch-history");
        if (historyResult) {
          setWatchHistory(JSON.parse(historyResult.value));
        } else {
          // Fallback to localStorage
          const localHistory = localStorage.getItem("noryq-watch-history");
          if (localHistory) {
            setWatchHistory(JSON.parse(localHistory));
          }
        }

        const countResult = await storage.get("recommendation-count");
        if (countResult) {
          const count = JSON.parse(countResult.value);
          setRecommendationCount(count);
          setFreeRecommendationsLeft(Math.max(0, 2 - count));
        } else {
          // Fallback to localStorage
          const localCount = localStorage.getItem("noryq-recommendation-count");
          if (localCount) {
            const count = JSON.parse(localCount);
            setRecommendationCount(count);
            setFreeRecommendationsLeft(Math.max(0, 2 - count));
          }
        }
      }
    } catch (error) {
      console.log("First time user - starting fresh");
    }
  };
  // ─── STATIC TRENDING MOVIES ───────────────────────────────────────
  // Edit this array to update trending content manually
  const STATIC_TRENDING_MOVIES = [
    {
      title: "Dune: Part Two",
      year: "2024",
      type: "Movie",
      genre: "Sci-Fi/Adventure",
      rating: "8.7",
      imdbRating: "8.7/10",
      platform: "Prime Video",
      matchScore: 95,
      why: "Massive sci-fi sequel still trending globally",
      tmdbID: 693134,
      posterUrl:
        "https://image.tmdb.org/t/p/w500/8b8R8l88Qje9dn9OE8PY05Nxl1X.jpg",
    },
    {
      title: "Godzilla x Kong: The New Empire",
      year: "2024",
      type: "Movie",
      genre: "Action/Sci-Fi",
      rating: "7.0",
      imdbRating: "7.0/10",
      platform: "Netflix",
      matchScore: 92,
      why: "Monsterverse blockbuster trending on OTT and globally",
      tmdbID: 823464,
      posterUrl:
        "https://image.tmdb.org/t/p/w500/z1p34vh7dEOnLDmyCrlUVLuoDzd.jpg",
    },
    {
      title: "Kung Fu Panda 4",
      year: "2024",
      type: "Movie",
      genre: "Animation/Comedy",
      rating: "7.3",
      imdbRating: "7.3/10",
      platform: "Prime Video",
      matchScore: 90,
      why: "Family blockbuster still trending among all age groups",
      tmdbID: 1011985,
      posterUrl:
        "https://image.tmdb.org/t/p/w500/wfENf6cG6g0O8V2Y9F8bTzY3w8Z.jpg",
    },
    {
      title: "Oppenheimer",
      year: "2023",
      type: "Movie",
      genre: "Biography/Drama",
      rating: "8.6",
      imdbRating: "8.6/10",
      platform: "Prime Video",
      matchScore: 94,
      why: "Award-winning film still trending on streaming platforms",
      tmdbID: 872585,
      posterUrl:
        "https://image.tmdb.org/t/p/w500/ptpr0kGAckfQkJeJIt8st5dglvd.jpg",
    },
    {
      title: "Jawan",
      year: "2023",
      type: "Movie",
      genre: "Action/Drama",
      rating: "7.0",
      imdbRating: "7.0/10",
      platform: "Netflix",
      matchScore: 93,
      why: "Massive Indian blockbuster trending on Netflix",
      tmdbID: 872906,
      posterUrl:
        "https://image.tmdb.org/t/p/w500/jFt1gS4BGHlK8xt76Y81Alp4dbt.jpg",
    },
    {
      title: "Fighter",
      year: "2024",
      type: "Movie",
      genre: "Action/Drama",
      rating: "6.8",
      imdbRating: "6.8/10",
      platform: "Netflix",
      matchScore: 89,
      why: "Aerial action film trending in India",
      tmdbID: 1115623,
      posterUrl:
        "https://image.tmdb.org/t/p/w500/6f6z1cQ5xXNjpVv7F3l6Rz6FQ8D.jpg",
    },
    {
      title: "Shaitaan",
      year: "2024",
      type: "Movie",
      genre: "Horror/Thriller",
      rating: "7.2",
      imdbRating: "7.2/10",
      platform: "Netflix",
      matchScore: 91,
      why: "Indian horror thriller gaining strong OTT traction",
      tmdbID: 1194506,
      posterUrl:
        "https://image.tmdb.org/t/p/w500/3rjvZ9Q0s7ZzKzbwQ0imeISFRCW.jpg",
    },
    {
      title: "Article 370",
      year: "2024",
      type: "Movie",
      genre: "Drama/Political",
      rating: "7.8",
      imdbRating: "7.8/10",
      platform: "Netflix",
      matchScore: 90,
      why: "Political drama trending in India",
      tmdbID: 1219685,
      posterUrl:
        "https://image.tmdb.org/t/p/w500/6fTz6G4Y0L5Zc7xQb2dZSmHgwlH.jpg",
    },

    // ... add more movies here
  ];
  // Generates a unique "why" blurb from each movie's actual TMDB data
  const getTMDBWhy = (movie, rank) => {
    const score = parseFloat(movie.vote_average?.toFixed(1));
    const votes = movie.vote_count?.toLocaleString();
    if (rank === 0)
      return `#1 trending this week · ${score}/10 from ${votes} ratings`;
    if (score >= 8)
      return `Critically acclaimed · ${score}/10 from ${votes} ratings`;
    if (score >= 7)
      return `Highly rated this week · ${score}/10 across ${votes} votes`;
    return `Trending #${rank + 1} globally · ${score}/10 from ${votes} ratings`;
  };
  const loadTrendingRecommendations = async () => {
    if (navigator.userAgent.includes("ReactSnap")) {
      setTrendingRecommendations(STATIC_TRENDING_MOVIES);
      return;
    }

    try {
      setIsLoading(true);

      const response = await fetch("/api/tmdb/trending");

      if (!response.ok) {
        throw new Error(
          `TMDB proxy error ${response.status}: ${response.statusText}`,
        );
      }

      const data = await response.json();

      const formattedMovies = await Promise.all(
        data.results.slice(0, 8).map(async (movie, index) => {
          // Fetch watch providers for IN region (change "IN" to your target region if needed)
          let platform = "Check locally";
          try {
            const provRes = await fetch(
              `/api/tmdb/providers?id=${encodeURIComponent(movie.id)}&type=movie`,
            );
            const provData = await provRes.json();

            // Try IN first, fall back to US
            const regionData = provData.results?.IN || provData.results?.US;

            // Check flatrate → rent → buy in order of preference
            const providers =
              regionData?.flatrate || regionData?.rent || regionData?.buy;

            if (providers?.length) {
              platform = providers
                .map((p) => p.provider_name)
                .slice(0, 2)
                .join(" / ");
            }
          } catch {
            // silently skip
          }

          return {
            title: movie.title,
            year: movie.release_date?.split("-")[0] || "N/A",
            type: "Movie",
            genre: "N/A",
            rating: movie.vote_average?.toFixed(1) || "N/A",
            imdbRating: `${movie.vote_average?.toFixed(1) || "N/A"}/10`,
            platform, // ← now real provider names
            matchScore: Math.round(movie.popularity),
            why: getTMDBWhy(movie, index), // ← now unique per movie
            tmdbID: movie.id,
            posterUrl: movie.poster_path
              ? `${TMDB_IMAGE_BASE_URL}${movie.poster_path}`
              : getFallbackPoster(movie.title, "Movie"),
          };
        }),
      );

      setTrendingRecommendations(formattedMovies);
    } catch (error) {
      console.error("Error fetching TMDB trending movies:", error);
      setTrendingRecommendations(STATIC_TRENDING_MOVIES); // fallback
    } finally {
      setIsLoading(false);
    }
  };
  // Fallback poster if AI doesn't provide one
  const getFallbackPoster = (title, type) => {
    // Generate a nice gradient poster with title
    return `https://placehold.co/500x750/667eea/ffffff?text=${encodeURIComponent(title)}`;
  };

  const saveWatchHistory = async (newHistory) => {
    try {
      if (user) {
        await updateUserData({ watchHistory: newHistory });
      } else {
        // Save to both storage AND localStorage
        await storage.set("watch-history", JSON.stringify(newHistory));
        localStorage.setItem("noryq-watch-history", JSON.stringify(newHistory));
      }
    } catch (error) {
      console.error("Failed to save watch history:", error);
    }
  };

  const incrementRecommendationCount = async () => {
    const newCount = recommendationCount + 1;
    const newDailyCount = dailyRecommendations + 1;

    setRecommendationCount(newCount);
    setDailyRecommendations(newDailyCount);
    setFreeRecommendationsLeft(
      Math.max(0, RECOMMENDATION_LIMITS.FREE_TOTAL - newCount),
    );

    try {
      if (user) {
        await updateUserData({
          recommendationCount: newCount,
          dailyRecommendations: newDailyCount,
          lastRecommendationDate: new Date().toISOString(),
        });
      } else {
        await storage.set("recommendation-count", JSON.stringify(newCount));
        localStorage.setItem(
          "noryq-recommendation-count",
          JSON.stringify(newCount),
        );
      }
    } catch (error) {
      console.error("Failed to save count:", error);
    }
    // ── Feedback trigger logic ──
    const nextSession = feedbackSessionCount + 1;
    setFeedbackSessionCount(nextSession);

    const isAccountUser = !!user || isPremium; // logged-in OR premium
    const threshold = isAccountUser ? 3 : 1; // every 3 for account users, every 1 for guests

    if (nextSession % threshold === 0) {
      // Small delay so results render first, THEN the modal appears
      setTimeout(() => setShowFeedback(true), 5000);
    }
  };
  const updateStreak = async () => {
    if (!user) return;

    const now = new Date();
    const todayString = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    // Always fetch fresh data from DB to avoid stale reads
    const userData = await getUserData();
    const existingHistory = userData?.streakHistory || [];

    // Deduplicate existing history
    const historySet = new Set(existingHistory);

    // If today already recorded, just sync UI — no DB write needed
    if (historySet.has(todayString)) {
      const streak = calculateStreak([...historySet]);
      setTodayStreak(streak);
      setStreakHistory([...historySet]);
      return;
    }

    // Add today and recalculate
    historySet.add(todayString);
    const newHistory = [...historySet].sort();
    const currentStreak = calculateStreak(newHistory);

    setTodayStreak(currentStreak);
    setStreakHistory(newHistory);

    try {
      await updateUserData({
        streak: currentStreak,
        lastRecommendationDate: new Date().toISOString(),
        streakHistory: newHistory,
      });
    } catch (error) {
      console.error("❌ Failed to update streak:", error);
    }
  };
  const fetchPosterFromTMDB = async (title, year, type) => {
    if (!title) return null; // ← ADD THIS
    const cacheKey = `noryq-poster-${title.toLowerCase().replace(/\s+/g, "-")}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached === "null" ? null : cached;
    try {
      // ✅ Add a 4-second timeout — previously it waited ~30s before failing
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);

      const res = await fetch(
        `/api/tmdb/search?q=${encodeURIComponent(title)}`,
        {
          signal: controller.signal,
        },
      );
      clearTimeout(timeoutId);

      const data = await res.json();

      if (data.results?.length) {
        const searchType = type?.toLowerCase() === "movie" ? "movie" : "tv";
        const typeMatch = data.results.find(
          (r) => r.media_type === searchType && r.poster_path,
        );
        const anyMatch = data.results.find((r) => r.poster_path);
        const hit = typeMatch || anyMatch;
        if (hit?.poster_path) {
          return `https://image.tmdb.org/t/p/w500${hit.poster_path}`;
        }
      }
      return null;
    } catch (error) {
      // AbortError = timeout, silently return null instead of logging
      if (error.name !== "AbortError") {
        console.error("TMDB API error:", error);
      }
      return null;
    }
  };
  // ============================================
  // WAITLIST SUBMISSION FUNCTION
  // ============================================
  const submitWaitlist = async () => {
    try {
      // Validation
      if (!user && (!waitlistEmail || !waitlistEmail.includes("@"))) {
        showToast("Please enter a valid email", "error");
        return;
      }

      if (!waitlistConsent) {
        showToast(
          "Please tick the box to agree to be contacted about Premium",
          "error",
        );
        return;
      }

      const email = user?.email || waitlistEmail;
      const name = user?.displayName || waitlistName || "Anonymous";

      console.log("📝 Submitting to waitlist:", { email, name });

      // Import Firestore functions
      const { addDoc, collection } = await import("firebase/firestore");

      // Save to Firestore
      if (!firestoreDb) throw new Error("Firebase is not configured");
      await addDoc(collection(firestoreDb, "waitlist"), {
        email: email,
        name: name,
        timestamp: new Date().toISOString(),
        source: user ? "logged-in" : "anonymous",
        userId: user?.uid || null,
        consent: {
          purpose: "Email about NORYQ Premium launch",
          acceptedAt: new Date().toISOString(),
          policyVersion: POLICY_VERSION,
        },
      });

      console.log("✅ Waitlist signup successful!");

      setWaitlistSubmitted(true);
      showToast("You're on the waitlist! We'll notify you soon!", "success");

      // Close modal after 3 seconds
      setTimeout(() => {
        setShowWaitlistModal(false);
        setWaitlistSubmitted(false);
        setWaitlistEmail("");
        setWaitlistName("");
        setWaitlistConsent(false);
      }, 3000);
    } catch (error) {
      console.error("❌ Waitlist submission error:", error);
      showToast("Something went wrong. Please try again!", "error");
    }
  };

  const moods = [
    { id: "happy", icon: Laugh, label: "Happy & Uplifted", color: "#FFD93D" },
    { id: "sad", icon: Heart, label: "Sad & Emotional", color: "#6C9BCF" },
    { id: "thrilled", icon: Zap, label: "Seeking Thrills", color: "#FF6B9D" },
    {
      id: "curious",
      icon: Brain,
      label: "Intellectually Curious",
      color: "#A29BFE",
    },
    { id: "scared", icon: Ghost, label: "Want to Be Scared", color: "#2D3436" },
    {
      id: "nostalgic",
      icon: Clock,
      label: "Nostalgic & Sentimental",
      color: "#81ECEC",
    },
  ];

  const contentTypes = [
    { id: "movie", icon: Film, label: "Movies" },
    { id: "series", icon: Tv, label: "TV Series" },
    { id: "both", icon: TrendingUp, label: "Surprise Me!" },
  ];

  const tones = [
    {
      id: "fast",
      icon: Zap,
      label: "Fast & Intense",
      desc: "High energy, edge of your seat",
      color: "#FF6B9D",
    },
    {
      id: "slowburn",
      icon: Clock,
      label: "Slow-burn & Atmospheric",
      desc: "Rich storytelling, builds tension",
      color: "#A29BFE",
    },
    {
      id: "light",
      icon: Laugh,
      label: "Light & Easy",
      desc: "Fun, feel-good, zero stress",
      color: "#FFD93D",
    },
    {
      id: "mindbending",
      icon: Brain,
      label: "Mind-bending & Complex",
      desc: "Twists, layers, makes you think",
      color: "#81ECEC",
    },
  ];

  const watchingWithOptions = [
    {
      id: "solo",
      label: "Just Me",
      icon: User,
      color: "#A29BFE",
      desc: "Solo session",
    },
    {
      id: "partner",
      label: "Date Night",
      icon: Heart,
      color: "#FF6B9D",
      desc: "With partner",
    },
    {
      id: "family",
      label: "Family Time",
      icon: Users,
      color: "#FFD93D",
      desc: "Kids around",
    },
    {
      id: "friends",
      label: "Friends Group",
      icon: Flame,
      color: "#fd7949",
      desc: "Squad watch",
    },
  ];

  // ── Universal AI caller: Gemini first, Groq fallback ──────────────────
  const callAI = async (prompt, systemMsg, maxTokens = 2500) => {
    try {
      const res = await fetch("/api/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, systemMsg, maxTokens }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `AI API error: ${res.status}`);
      }
      if (!data?.result) throw new Error("AI API returned empty result");
      return String(data.result);
    } catch (e) {
      // Keep UI resilient: surface a clean failure without crashing
      console.error("AI call failed:", e);
      throw e;
    }
  };
  // ──────────────────────────────────────────────────────────────────────

  const getRecommendations = async () => {
    // ✅ CHECK EMAIL VERIFICATION
    if (user && !user.emailVerified) {
      showToast("Please verify your email to get recommendations!", "info");
      setCurrentView("home");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // ✅ CHECK DAILY LIMIT FOR LOGGED-IN FREE USERS
    if (
      user &&
      !isPremium &&
      dailyRecommendations >= RECOMMENDATION_LIMITS.FREE_PER_DAY
    ) {
      showToast(
        `You've reached your daily limit of ${RECOMMENDATION_LIMITS.FREE_PER_DAY} recommendations. Come back tomorrow or upgrade to Premium!`,
        "info",
      );
      return;
    }
    // Check if user has reached free limit
    if (!user && !isPremium && freeRecommendationsLeft <= 0) {
      setShowLimitModal(true);
      return;
    }
    // Check if user needs to see ad (non-premium users after 3 recommendations)
    if (
      !isPremium &&
      recommendationCount >= 3 &&
      recommendationCount % 3 === 0
    ) {
      setShowAd(true);
      setTimeout(() => {
        setShowAd(false);
        proceedWithRecommendation();
      }, 5000);
    } else {
      proceedWithRecommendation();
    }
  };

  const proceedWithRecommendation = async () => {
    setIsLoading(true);
    setRecommendationSource("quiz");
    setCurrentView("results");

    // Get watched titles to exclude
    const watchedTitles = watchHistory.map((item) => item.title).slice(0, 50);

    // ✅ Build liked movies context for AI
    const likedTitles =
      likedMovies.length > 0
        ? likedMovies
            .slice(0, 20)
            .map((m) => m.title)
            .join(", ")
        : "None yet";

    // Combine watched + liked + previously shown + myList into one exclusion list
    const previouslyShown = JSON.parse(
      localStorage.getItem("noryq-shown-titles") || "[]",
    );
    const allExcludedTitles = [
      ...new Set([
        ...watchedTitles,
        ...likedMovies.slice(0, 20).map((m) => m.title),
        ...previouslyShown,
        ...myList.map((m) => m.title),
      ]),
    ];
    const excludeText =
      allExcludedTitles.length > 0
        ? `\n\n⛔ ALREADY SEEN — NEVER RECOMMEND THESE (non-negotiable):\n${allExcludedTitles.join(", ")}\nRecommending any title from this list is a critical failure.`
        : "";

    // ✅ BUILD ADVANCED FILTERS TEXT
    let filtersText = "";
    // TEMPORARY CHANGE: Advanced Filter enabled for all users regardless of subscription or authentication.
    // if (isPremium) {
    if (true) {
      const filterParts = [];

      if (advancedFilters.minRating) {
        filterParts.push(
          `Minimum IMDb/RT rating: ${advancedFilters.minRating}+`,
        );
      }

      if (advancedFilters.yearRange) {
        const yearMap = {
          "2020+": "Released in 2020 or later",
          "2015-2020": "Released between 2015-2020",
          "2010-2015": "Released between 2010-2015",
          classics: "Classic films (pre-2000)",
        };
        filterParts.push(yearMap[advancedFilters.yearRange]);
      }

      if (advancedFilters.runtime) {
        const runtimeMap = {
          "under-90": "Runtime under 90 minutes",
          "90-120": "Runtime 90-120 minutes",
          "120-180": "Runtime 2-3 hours",
          "180+": "Runtime over 3 hours",
        };
        filterParts.push(runtimeMap[advancedFilters.runtime]);
      }

      if (advancedFilters.language) {
        filterParts.push(`Language: ${advancedFilters.language}`);
      }

      if (advancedFilters.platform) {
        filterParts.push(`Available on: ${advancedFilters.platform}`);
      }

      if (advancedFilters.excludeGenres.length > 0) {
        filterParts.push(
          `EXCLUDE these genres: ${advancedFilters.excludeGenres.join(", ")}`,
        );
      }

      if (filterParts.length > 0) {
        filtersText = `\n\nADVANCED FILTERS (Premium User):\n${filterParts.map((f) => `• ${f}`).join("\n")}`;
      }
    }

    const hour = new Date().getHours();
    const timeContext =
      hour >= 6 && hour < 12
        ? "Morning — prefer uplifting or energising content"
        : hour >= 12 && hour < 17
          ? "Afternoon — casual viewing, anything goes"
          : hour >= 17 && hour < 21
            ? "Evening — prime time, open to anything great"
            : "Late night — darker, gripping or immersive content works best";

    const prompt = `You are NORYQ, an elite AI entertainment curator. Recommend ${isPremium ? "6" : "2"} real, existing movies or TV shows perfectly matched to this user's profile.
${excludeText}

━━━ USER TASTE PROFILE ━━━
Liked titles (for taste reference only — do NOT recommend these): ${likedTitles}
${likedMovies.length > 0 ? "→ Find content sharing similar directors, tone, themes, or storytelling style to these titles. Mention the connection in the 'why' field." : "→ No liked titles yet — rely entirely on their session preferences below."}

━━━ SESSION PREFERENCES ━━━
Mood: ${userPreferences.mood}
Wants to watch: ${userPreferences.contentType}
Tone & pace: ${userPreferences.tone}
Genres: ${userPreferences.genres.length > 0 ? userPreferences.genres.join(", ") : "No preference — surprise them creatively"}
Watching with: ${userPreferences.watchingWith}
Time of day: ${timeContext}${filtersText}

━━━ MANDATORY RULES ━━━
1. EXIST CHECK — every title must be a real, verifiable movie or series. No invented titles.
2. DIVERSITY — no two titles from the same director, franchise, cinematic universe, or sub-genre.
3. HIDDEN GEM RULE — at least one recommendation must be an underrated or lesser-known title, not just the most obvious choice.
4. RATINGS — use accurate real IMDb scores only (format: X.X). Do not invent ratings.
5. PLATFORMS — realistic global platforms only: Netflix, Prime Video, Apple TV+, Hotstar, HBO Max, Hulu, Disney+, Mubi, Peacock, Paramount+.
6. TONE MATCH — recommendations MUST reflect the chosen tone: Fast & Intense → thrillers/action; Slow-burn → prestige drama/arthouse; Light & Easy → comedy/feel-good; Mind-bending → sci-fi/psychological mystery.
7. WATCHING WITH — Family: nothing adult or disturbing. Date Night: romantic or thrilling. Friends Group: crowd-pleasers, fun, rewatchable. Just Me: bold, niche, adventurous picks welcome.
8. TIME OF DAY — late night favours darker/gripping content; morning favours lighter/uplifting; evening is open.
9. WHY FIELD — must be specific to THIS user's inputs. Reference their mood, tone, genre, or liked title. Never write a generic plot summary. Always second person: "You'll love this because..." never "The user will..."
10. MATCH SCORE — calculate a genuine, varied score from 70–99 based on how well the title fits ALL their preferences combined. Do NOT hardcode 95 for everything.

━━━ OUTPUT — ONLY THIS JSON, NOTHING ELSE ━━━
{
  "recommendations": [
    {
      "title": "Exact Title",
      "year": "YYYY",
      "type": "Movie or Series",
      "genre": "Primary Genre / Secondary Genre",
      "rating": "X.X",
      "imdbRating": "X.X/10",
      "why": "Specific personalised reason in second person (1-2 sentences)",
      "platform": "Primary streaming platform",
      "matchScore": 87
    }
  ]
}`;

    try {
      const rawText = await callAI(
        prompt,
        "You are NORYQ, an expert entertainment curator. You ONLY output valid JSON — no markdown, no explanation, no code fences. Every title you recommend must genuinely exist. Be specific and personalized, never generic.",
        2500,
      );
      const parsed = JSON.parse(rawText);

      // ✅ Fetch ALL posters in parallel (not one-by-one)
      const baseRecs = parsed.recommendations.map((rec, idx) => ({
        ...rec,
        posterUrl: null, // placeholder, loaded below
        matchScore: rec.matchScore || 95 - idx * 2,
      }));
      setRecommendations(baseRecs);
      const prevShown = JSON.parse(
        localStorage.getItem("noryq-shown-titles") || "[]",
      );
      const newShown = [
        ...new Set([...prevShown, ...baseRecs.map((r) => r.title)]),
      ].slice(0, 100);
      localStorage.setItem("noryq-shown-titles", JSON.stringify(newShown));
      setIsLoading(false); // ← UI unlocks here, cards are visible

      // Posters load silently in the background
      parsed.recommendations.forEach(async (rec, idx) => {
        const posterUrl = await fetchPosterFromTMDB(
          rec.title,
          rec.year,
          rec.type,
        );
        if (posterUrl) {
          setRecommendations((prev) =>
            prev.map((r, i) => (i === idx ? { ...r, posterUrl } : r)),
          );
        }
      });

      // ✅ Side-effects in parallel, don't block UI
      await incrementRecommendationCount();
      await updateStreak();
    } catch (error) {
      console.error("Recommendation error:", error);
      // Fallback recommendations
      setRecommendations([
        {
          title: "3 Idiots",
          year: "2009",
          type: "Movie",
          genre: "Comedy/Drama",
          rating: "8.4/10",
          why: "Uplifting and entertaining, perfect for your mood",
          platform: "Netflix, Amazon Prime",
          matchScore: 95,
        },
        {
          title: "Sacred Games",
          year: "2018",
          type: "Series",
          genre: "Crime/Thriller",
          rating: "8.6/10",
          why: "Gripping Indian thriller series",
          platform: "Netflix",
          matchScore: 92,
        },
        {
          title: "The Shawshank Redemption",
          year: "1994",
          type: "Movie",
          genre: "Drama",
          rating: "9.3/10",
          why: "Inspiring story of hope and friendship",
          platform: "Amazon Prime, Netflix",
          matchScore: 90,
        },
        {
          title: "Breaking Bad",
          year: "2008",
          type: "Series",
          genre: "Crime/Drama",
          rating: "9.5/10",
          why: "Intense and gripping drama",
          platform: "Netflix",
          matchScore: 88,
        },
        {
          title: "Inception",
          year: "2010",
          type: "Movie",
          genre: "Sci-Fi/Thriller",
          rating: "8.8/10",
          why: "Mind-bending thriller that will keep you engaged",
          platform: "Netflix, Amazon Prime",
          matchScore: 85,
        },
      ]);
      setIsLoading(false);
    }
  };
  // ⚡ SKIP QUIZ - Smart Recommendations Based on History
  const getSmartRecommendation = async () => {
    if (!user || !isPremium) {
      showToast("This is a premium feature! Upgrade to unlock.", "info");
      setCurrentView("premium");
      return;
    }

    // Skip Quiz requires ACTUAL watch history (not just recommendations)
    // This ensures we have real viewing patterns to analyze
    if (watchHistory.length < 3) {
      showToast(
        `Skip Quiz needs your watch history! Watch ${3 - watchHistory.length} more items first.`,
        "info",
      );
      // Don't auto-navigate - let user decide
      return;
    }

    // Also need some recommendation experience (5+)
    if (recommendationCount < 5) {
      showToast(
        `Build your profile first! Complete ${5 - recommendationCount} more quizzes to unlock Skip Quiz.`,
        "info",
      );
      // Don't auto-navigate - let user decide
      return;
    }

    setIsLoading(true);
    setRecommendationSource("smart");
    setCurrentView("results");

    // Analyze user's watch history to determine preferences
    const recentHistory = watchHistory.slice(0, 10);
    const watchedTitles = watchHistory.map((item) => item.title).slice(0, 50);
    const likedTitles =
      likedMovies.length > 0
        ? likedMovies
            .slice(0, 20)
            .map((m) => m.title)
            .join(", ")
        : "None yet";
    // Extract patterns from history
    const genres = [
      ...new Set(recentHistory.map((item) => item.genre).filter(Boolean)),
    ];
    const types = [
      ...new Set(recentHistory.map((item) => item.type).filter(Boolean)),
    ];

    // Determine time-based mood
    const hour = new Date().getHours();
    const day = new Date().getDay();

    let timeMood = "";
    if (hour >= 6 && hour < 12) {
      timeMood = "Morning - Looking for something uplifting or informative";
    } else if (hour >= 12 && hour < 17) {
      timeMood = "Afternoon - Casual viewing mood";
    } else if (hour >= 17 && hour < 21) {
      timeMood = "Evening - Prime time entertainment";
    } else {
      timeMood = "Late night - Looking for engaging content";
    }

    let dayMood = "";
    if (day === 0 || day === 6) {
      dayMood = "Weekend - More time for longer content";
    } else if (day === 5) {
      dayMood = "Friday evening - Weekend mood starting";
    } else {
      dayMood = "Weekday - Prefer shorter, lighter content";
    }

    // Build filters text
    const allExcludedSmart = [
      ...new Set([
        ...watchedTitles,
        ...likedMovies.slice(0, 20).map((m) => m.title),
      ]),
    ];
    const excludeText =
      allExcludedSmart.length > 0
        ? `\n\n⛔ ALREADY SEEN — NEVER RECOMMEND THESE (non-negotiable):\n${allExcludedSmart.join(", ")}\nRecommending any title from this list is a critical failure.`
        : "";

    // Build advanced filters if active
    let filtersText = "";
    // TEMPORARY CHANGE: Advanced Filter enabled for all users regardless of subscription or authentication.
    // if (isPremium) {
    if (true) {
      const filterParts = [];

      if (advancedFilters.minRating) {
        filterParts.push(
          `Minimum IMDb/RT rating: ${advancedFilters.minRating}+`,
        );
      }

      if (advancedFilters.yearRange) {
        const yearMap = {
          "2020+": "Released in 2020 or later",
          "2015-2020": "Released between 2015-2020",
          "2010-2015": "Released between 2010-2015",
          classics: "Classic films (pre-2000)",
        };
        filterParts.push(yearMap[advancedFilters.yearRange]);
      }

      if (advancedFilters.runtime) {
        const runtimeMap = {
          "under-90": "Runtime under 90 minutes",
          "90-120": "Runtime 90-120 minutes",
          "120-180": "Runtime 2-3 hours",
          "180+": "Runtime over 3 hours",
        };
        filterParts.push(runtimeMap[advancedFilters.runtime]);
      }

      if (advancedFilters.language) {
        filterParts.push(`Language: ${advancedFilters.language}`);
      }

      if (advancedFilters.platform) {
        filterParts.push(`Available on: ${advancedFilters.platform}`);
      }

      if (advancedFilters.excludeGenres.length > 0) {
        filterParts.push(
          `EXCLUDE these genres: ${advancedFilters.excludeGenres.join(", ")}`,
        );
      }

      if (filterParts.length > 0) {
        filtersText = `\n\nADVANCED FILTERS:\n${filterParts.map((f) => `• ${f}`).join("\n")}`;
      }
    }

    // Create smart prompt based on user's history
    const prompt = `You are NORYQ, an elite AI entertainment curator with deep learning capabilities. Recommend 6 real, existing movies or TV shows based on this user's full viewing history and current context.
${excludeText}

━━━ USER TASTE PROFILE ━━━
Liked titles (for taste reference only — do NOT recommend these): ${likedTitles}
${likedMovies.length > 0 ? "→ Find content sharing similar directors, tone, themes, or storytelling style. Mention the connection in the 'why' field." : "→ No liked titles yet — infer taste entirely from watch history below."}

Recent watches (for taste reference only — do NOT recommend these): ${recentHistory.map((item) => `${item.title} (${item.type}, ${item.genre || "N/A"})`).join(", ") || "No recent history"}
Inferred genres from history: ${genres.join(", ") || "Mixed preferences"}
Inferred content type from history: ${types.join(", ") || "Both movies and series"}
Total sessions completed: ${recommendationCount}

━━━ CURRENT CONTEXT ━━━
Time of day: ${timeMood}
Day: ${dayMood}

━━━ MANDATORY RULES ━━━
1. EXIST CHECK — every title must be real and verifiable. No invented titles.
2. PATTERN ANALYSIS — study their watch history deeply. Recommend what logically comes next in their taste journey, not just more of the same genre.
3. DIVERSITY — no two titles from the same director, franchise, or sub-genre.
4. HIDDEN GEM RULE — at least one recommendation must be underrated or lesser-known.
5. RATINGS — accurate real IMDb scores only (format: X.X).
6. PLATFORMS — realistic global platforms: Netflix, Prime Video, Apple TV+, Hotstar, HBO Max, Hulu, Disney+, Mubi, Peacock, Paramount+.
7. WHY FIELD — must explicitly reference a specific title from their history or liked list. Never generic. Always second person: "You'll love this because..." never "The user will..."
8. ECHO CHAMBER RULE — include one title that gently stretches their taste while still feeling like a natural next step.
9. TIME CONTEXT — late night favours darker/gripping; morning favours lighter; weekend favours longer content; weekday favours tighter runtime.
10. MATCH SCORE — calculate a genuine varied score 70–99 based on how well the title fits their full history. Never hardcode the same number for all.${filtersText}

━━━ OUTPUT — ONLY THIS JSON, NOTHING ELSE ━━━
{
  "recommendations": [
    {
      "title": "Exact Title",
      "year": "YYYY",
      "type": "Movie or Series",
      "genre": "Primary Genre / Secondary Genre",
      "rating": "X.X",
      "imdbRating": "X.X/10",
      "why": "Personalised reason referencing their history or liked titles (1-2 sentences)",
      "platform": "Primary streaming platform",
      "matchScore": 87
    }
  ]
}`;

    try {
      const rawText = await callAI(
        prompt,
        "You are NORYQ, an expert entertainment curator with AI learning capabilities. You ONLY output valid JSON — no markdown, no explanation, no code fences. Every title you recommend must genuinely exist. Be deeply personalized based on the user's history, never generic.",
        2500,
      );
      const parsed = JSON.parse(rawText);

      if (parsed.recommendations && Array.isArray(parsed.recommendations)) {
        // ✅ Fetch ALL posters in parallel (not one-by-one)
        const baseRecs = parsed.recommendations.map((rec, idx) => ({
          ...rec,
          posterUrl: null, // placeholder, loaded below
          matchScore: rec.matchScore || 95 - idx * 2,
        }));
        setRecommendations(baseRecs);
        const prevShown = JSON.parse(
          localStorage.getItem("noryq-shown-titles") || "[]",
        );
        const newShown = [
          ...new Set([...prevShown, ...baseRecs.map((r) => r.title)]),
        ].slice(0, 100);
        localStorage.setItem("noryq-shown-titles", JSON.stringify(newShown));
        setIsLoading(false); // ← UI unlocks here, cards are visible

        // Posters load silently in the background
        parsed.recommendations.forEach(async (rec, idx) => {
          const posterUrl = await fetchPosterFromTMDB(
            rec.title,
            rec.year,
            rec.type,
          );
          if (posterUrl) {
            setRecommendations((prev) =>
              prev.map((r, i) => (i === idx ? { ...r, posterUrl } : r)),
            );
          }
        });
        showToast("Smart recommendations generated!", "success");

        // ✅ Side-effects in parallel, don't block UI
        await incrementRecommendationCount();
        await updateStreak();
      } else {
        throw new Error("Invalid recommendation format");
      }
    } catch (error) {
      console.error("Smart recommendation error:", error);
      setIsLoading(false);
      setCurrentView("home");
      showToast(
        "Failed to generate recommendations. Please try again.",
        "error",
      );
    }
  };
  const addToWatchHistory = async (item, fromMyList = false) => {
    if (!user) {
      setSelectedRecommendation(item);
      setShowAuthModal(true);
      return false;
    }

    // ✅ CHECK IF IN MY LIST (but skip if coming from My List)
    if (
      !fromMyList &&
      myList.some((listItem) => listItem.title === item.title)
    ) {
      showToast(
        "This is in your My List! Mark as watched from there instead.",
        "info",
      );
      return false;
    }

    // ✅ CHECK FOR DUPLICATES
    if (watchHistory.some((historyItem) => historyItem.title === item.title)) {
      showToast("Already in watch history!", "info");
      return true; // it is in history already
    }

    const newHistory = [
      { ...item, watchedAt: new Date().toISOString() },
      ...watchHistory,
    ];
    setWatchHistory(newHistory);
    await saveWatchHistory(newHistory);
    showToast(`Added "${item.title}" to watch history!`);
    return true;
  };
  // ─── helpers ───────────────────────────────────────────────
  const getRating = (rec) =>
    rec.imdbRating
      ? typeof rec.imdbRating === "string" && rec.imdbRating.includes("/")
        ? rec.imdbRating.split("/")[0]
        : rec.imdbRating
      : rec.rating;

  // Wrap text onto canvas, returns final Y
  const wrapText = (ctx, text, x, y, maxW, lineH) => {
    const words = text.split(" ");
    let line = "";
    for (const word of words) {
      const test = line + word + " ";
      if (ctx.measureText(test).width > maxW && line) {
        ctx.fillText(line.trim(), x, y);
        y += lineH;
        line = word + " ";
      } else {
        line = test;
      }
    }
    ctx.fillText(line.trim(), x, y);
    return y + lineH;
  };

  // Draw a rounded rect (polyfill for older browsers)
  const rrect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };

  // ─── Instagram Story Card (1080×1920) ──────────────────────
  // ─── Instagram Story Card (1080×1920) ──────────────────────
  const drawInstagramCard = (canvas, rec, onDone) => {
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    const SITE = window.location.hostname || "noryq.in";
    const rating = getRating(rec);

    const img = new Image();
    img.crossOrigin = "anonymous";

    const render = () => {
      ctx.clearRect(0, 0, W, H);

      // ── Deep purple base (always visible behind poster) ──
      const baseBg = ctx.createLinearGradient(0, 0, W, H);
      baseBg.addColorStop(0, "#0d0b1e");
      baseBg.addColorStop(1, "#1a0a2e");
      ctx.fillStyle = baseBg;
      ctx.fillRect(0, 0, W, H);

      // ── Poster: top 65% of canvas, cover-fit ──
      const posterH = H * 0.65;
      if (img.complete && img.naturalWidth > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, W, posterH);
        ctx.clip();
        const sc = Math.max(W / img.width, posterH / img.height);
        const iw = img.width * sc;
        const ih = img.height * sc;
        ctx.drawImage(img, (W - iw) / 2, 0, iw, ih);
        ctx.restore();
      }

      // ── Poster fade-out at bottom ──
      const posterFade = ctx.createLinearGradient(
        0,
        posterH * 0.45,
        0,
        posterH,
      );
      posterFade.addColorStop(0, "rgba(13,11,30,0)");
      posterFade.addColorStop(1, "rgba(13,11,30,1)");
      ctx.fillStyle = posterFade;
      ctx.fillRect(0, posterH * 0.45, W, posterH * 0.55);

      // ── Subtle dark vignette on poster top ──
      const topVig = ctx.createLinearGradient(0, 0, 0, 220);
      topVig.addColorStop(0, "rgba(0,0,0,0.55)");
      topVig.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = topVig;
      ctx.fillRect(0, 0, W, 220);

      // ── Colorful glow blob behind bottom content ──
      const glow = ctx.createRadialGradient(
        W / 2,
        H * 0.78,
        0,
        W / 2,
        H * 0.78,
        520,
      );
      glow.addColorStop(0, "rgba(102,126,234,0.18)");
      glow.addColorStop(0.5, "rgba(118,75,162,0.10)");
      glow.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, H * 0.55, W, H * 0.45);

      // ── TOP: NORYQ brand pill ──
      const pillW = 220,
        pillH = 60,
        pillX = (W - pillW) / 2,
        pillY = 72;
      ctx.save();
      rrect(ctx, pillX, pillY, pillW, pillH, 30);
      const pg = ctx.createLinearGradient(pillX, 0, pillX + pillW, 0);
      pg.addColorStop(0, "#667eea");
      pg.addColorStop(1, "#764ba2");
      ctx.fillStyle = pg;
      ctx.shadowColor = "rgba(102,126,234,0.8)";
      ctx.shadowBlur = 32;
      ctx.fill();
      ctx.restore();
      ctx.font = "bold 30px Arial, sans-serif";
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.fillText("NORYQ", W / 2, pillY + 40);

      // ── Match % badge — top right ──
      ctx.save();
      rrect(ctx, W - 190, 68, 156, 68, 16);
      const mb = ctx.createLinearGradient(W - 190, 68, W - 34, 136);
      mb.addColorStop(0, "#f093fb");
      mb.addColorStop(1, "#f5576c");
      ctx.fillStyle = mb;
      ctx.shadowColor = "rgba(240,147,251,0.7)";
      ctx.shadowBlur = 28;
      ctx.fill();
      ctx.restore();
      ctx.font = "bold 34px Arial, sans-serif";
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.fillText(`${rec.matchScore || 95}%`, W - 112, 110);
      ctx.font = "bold 16px Arial, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.fillText("MATCH", W - 112, 128);

      // ── BOTTOM CONTENT — floating on dark area ──
      let by = posterH + 60;

      // Type chip
      const typeLabel =
        rec.type === "Movie" || rec.type === "Film"
          ? "🎬  MOVIE"
          : "📺  SERIES";
      const typeW = 180;
      ctx.save();
      rrect(ctx, 70, by, typeW, 50, 12);
      ctx.fillStyle = "rgba(167,139,250,0.15)";
      ctx.strokeStyle = "rgba(167,139,250,0.4)";
      ctx.lineWidth = 1.5;
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      ctx.font = "bold 24px Arial, sans-serif";
      ctx.fillStyle = "#a78bfa";
      ctx.textAlign = "left";
      ctx.fillText(typeLabel, 88, by + 33);
      by += 74;

      // Title — big and bold
      ctx.font = "bold 96px Arial, sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "left";
      ctx.shadowColor = "rgba(0,0,0,0.6)";
      ctx.shadowBlur = 20;
      by = wrapText(ctx, rec.title, 70, by, W - 140, 108);
      ctx.shadowBlur = 0;
      by += 8;

      // Year · Genre row
      ctx.font = "500 32px Arial, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.45)";
      ctx.fillText(`${rec.year}  ·  ${rec.genre}`, 70, by);
      by += 60;

      // ── Divider ──
      const div = ctx.createLinearGradient(70, by, W - 70, by);
      div.addColorStop(0, "transparent");
      div.addColorStop(0.2, "rgba(255,255,255,0.15)");
      div.addColorStop(0.8, "rgba(255,255,255,0.15)");
      div.addColorStop(1, "transparent");
      ctx.beginPath();
      ctx.moveTo(70, by);
      ctx.lineTo(W - 70, by);
      ctx.strokeStyle = div;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      by += 44;

      // IMDb + Platform on same row
      // IMDb pill
      ctx.save();
      rrect(ctx, 70, by, 200, 56, 10);
      ctx.fillStyle = "rgba(245,197,24,0.1)";
      ctx.strokeStyle = "rgba(245,197,24,0.3)";
      ctx.lineWidth = 1.5;
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      ctx.font = "bold 30px Arial, sans-serif";
      ctx.fillStyle = "#F5C518";
      ctx.textAlign = "center";
      ctx.fillText(`★ ${rating}`, 170, by + 37);

      // Platform pill
      ctx.save();
      rrect(ctx, 296, by, 220, 56, 10);
      ctx.fillStyle = "rgba(102,126,234,0.12)";
      ctx.strokeStyle = "rgba(102,126,234,0.35)";
      ctx.lineWidth = 1.5;
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      ctx.font = "bold 26px Arial, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.75)";
      ctx.textAlign = "center";
      ctx.fillText(`▶  ${rec.platform}`, 406, by + 37);
      by += 90;

      // AI reason
      ctx.font = "italic 30px Arial, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.textAlign = "left";
      by = wrapText(ctx, `"${rec.why}"`, 70, by, W - 140, 42);
      by += 50;

      // ── CTA bar — vivid, eye-catching ──
      ctx.save();
      rrect(ctx, 70, by, W - 140, 88, 20);
      const ctaBg = ctx.createLinearGradient(70, by, W - 70, by + 88);
      ctaBg.addColorStop(0, "#667eea");
      ctaBg.addColorStop(1, "#764ba2");
      ctx.fillStyle = ctaBg;
      ctx.shadowColor = "rgba(102,126,234,0.5)";
      ctx.shadowBlur = 30;
      ctx.fill();
      ctx.restore();
      ctx.font = "bold 30px Arial, sans-serif";
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.fillText("✦  Try NORYQ free  →", W / 2, by + 36);
      ctx.font = "500 24px Arial, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.8)";
      ctx.fillText(SITE, W / 2, by + 64);

      onDone && onDone();
    };

    img.onload = render;
    img.onerror = render;
    img.src = rec.posterUrl;
    if (img.complete) render();
  };

  // ─── WhatsApp Share Card (1080×1080 square) ────────────────
  // ─── WhatsApp Share Card (1080×1080 square) ────────────────
  const drawWhatsAppCard = (canvas, rec, onDone) => {
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    const SITE = window.location.hostname || "noryq.in";
    const rating = getRating(rec);

    const img = new Image();
    img.crossOrigin = "anonymous";

    const render = () => {
      ctx.clearRect(0, 0, W, H);

      // ── Base bg ──
      ctx.fillStyle = "#0d0b1e";
      ctx.fillRect(0, 0, W, H);

      // ── Full poster, top-anchored, cover-fit ──
      if (img.complete && img.naturalWidth > 0) {
        const sc = Math.max(W / img.width, H / img.height);
        const iw = img.width * sc;
        const ih = img.height * sc;
        ctx.drawImage(img, (W - iw) / 2, 0, iw, ih);
      }

      // ── Strong bottom scrim — key to readability ──
      const scrim = ctx.createLinearGradient(0, H * 0.28, 0, H);
      scrim.addColorStop(0, "rgba(13,11,30,0)");
      scrim.addColorStop(0.35, "rgba(13,11,30,0.82)");
      scrim.addColorStop(1, "rgba(13,11,30,0.98)");
      ctx.fillStyle = scrim;
      ctx.fillRect(0, 0, W, H);

      // ── Top vignette ──
      const topFade = ctx.createLinearGradient(0, 0, 0, 160);
      topFade.addColorStop(0, "rgba(0,0,0,0.5)");
      topFade.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = topFade;
      ctx.fillRect(0, 0, W, 160);

      // ── Colorful glow at bottom ──
      const glow = ctx.createRadialGradient(W / 2, H, 0, W / 2, H, 600);
      glow.addColorStop(0, "rgba(102,126,234,0.22)");
      glow.addColorStop(0.6, "rgba(118,75,162,0.10)");
      glow.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, H * 0.5, W, H * 0.5);

      // ── TOP LEFT: NORYQ pill ──
      ctx.save();
      rrect(ctx, 48, 48, 180, 56, 28);
      const pg = ctx.createLinearGradient(48, 48, 228, 104);
      pg.addColorStop(0, "#667eea");
      pg.addColorStop(1, "#764ba2");
      ctx.fillStyle = pg;
      ctx.shadowColor = "rgba(102,126,234,0.8)";
      ctx.shadowBlur = 24;
      ctx.fill();
      ctx.restore();
      ctx.font = "bold 28px Arial, sans-serif";
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.fillText("NORYQ", 138, 85);

      // ── TOP RIGHT: match badge ──
      ctx.save();
      rrect(ctx, W - 194, 44, 150, 64, 14);
      const mb = ctx.createLinearGradient(W - 194, 44, W - 44, 108);
      mb.addColorStop(0, "#f093fb");
      mb.addColorStop(1, "#f5576c");
      ctx.fillStyle = mb;
      ctx.shadowColor = "rgba(240,147,251,0.7)";
      ctx.shadowBlur = 24;
      ctx.fill();
      ctx.restore();
      ctx.font = "bold 30px Arial, sans-serif";
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.fillText(`${rec.matchScore || 95}% MATCH`, W - 119, 83);

      // ── BOTTOM CONTENT ── (anchored from bottom up)
      const pad = 58;
      let by = H - pad;

      // CTA bar
      ctx.save();
      rrect(ctx, pad, by - 78, W - pad * 2, 78, 18);
      const ctaG = ctx.createLinearGradient(pad, by - 78, W - pad, by);
      ctaG.addColorStop(0, "#667eea");
      ctaG.addColorStop(1, "#764ba2");
      ctx.fillStyle = ctaG;
      ctx.shadowColor = "rgba(102,126,234,0.5)";
      ctx.shadowBlur = 24;
      ctx.fill();
      ctx.restore();
      ctx.font = "bold 28px Arial, sans-serif";
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.fillText(`✦ Try NORYQ free  ·  ${SITE}`, W / 2, by - 30);
      by -= 106;

      // Divider
      const divG = ctx.createLinearGradient(pad, by, W - pad, by);
      divG.addColorStop(0, "transparent");
      divG.addColorStop(0.2, "rgba(255,255,255,0.18)");
      divG.addColorStop(0.8, "rgba(255,255,255,0.18)");
      divG.addColorStop(1, "transparent");
      ctx.beginPath();
      ctx.moveTo(pad, by);
      ctx.lineTo(W - pad, by);
      ctx.strokeStyle = divG;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      by -= 46;

      // IMDb pill + platform pill on same row
      ctx.save();
      rrect(ctx, pad, by - 50, 188, 50, 10);
      ctx.fillStyle = "rgba(245,197,24,0.1)";
      ctx.strokeStyle = "rgba(245,197,24,0.3)";
      ctx.lineWidth = 1.5;
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      ctx.font = "bold 26px Arial, sans-serif";
      ctx.fillStyle = "#F5C518";
      ctx.textAlign = "center";
      ctx.fillText(`★  ${rating} / 10`, pad + 94, by - 17);

      ctx.save();
      rrect(ctx, pad + 210, by - 50, 210, 50, 10);
      ctx.fillStyle = "rgba(102,126,234,0.12)";
      ctx.strokeStyle = "rgba(102,126,234,0.3)";
      ctx.lineWidth = 1.5;
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      ctx.font = "bold 24px Arial, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.72)";
      ctx.textAlign = "center";
      ctx.fillText(`▶  ${rec.platform}`, pad + 315, by - 17);
      by -= 74;

      // Year · Genre
      ctx.font = "500 28px Arial, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.4)";
      ctx.textAlign = "left";
      ctx.fillText(`${rec.year}  ·  ${rec.genre}`, pad, by);
      by -= 76;

      // Title — largest element
      ctx.font = "bold 88px Arial, sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "left";
      ctx.shadowColor = "rgba(0,0,0,0.7)";
      ctx.shadowBlur = 16;
      // Measure lines first to place correctly
      const titleWords = rec.title.split(" ");
      let tLine = "",
        titleLines = [];
      for (const w of titleWords) {
        const test = tLine + w + " ";
        if (ctx.measureText(test).width > W - pad * 2 && tLine) {
          titleLines.push(tLine.trim());
          tLine = w + " ";
        } else tLine = test;
      }
      titleLines.push(tLine.trim());
      const titleLineH = 96;
      // draw bottom-up
      for (let i = titleLines.length - 1; i >= 0; i--) {
        ctx.fillText(titleLines[i], pad, by);
        by -= titleLineH;
      }
      ctx.shadowBlur = 0;
      by -= 16;

      // Type chip
      const typeLabel =
        rec.type === "Movie" || rec.type === "Film"
          ? "🎬  MOVIE"
          : "📺  SERIES";
      ctx.save();
      rrect(ctx, pad, by - 46, 180, 46, 10);
      ctx.fillStyle = "rgba(167,139,250,0.15)";
      ctx.strokeStyle = "rgba(167,139,250,0.4)";
      ctx.lineWidth = 1.5;
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      ctx.font = "bold 22px Arial, sans-serif";
      ctx.fillStyle = "#a78bfa";
      ctx.textAlign = "left";
      ctx.fillText(typeLabel, pad + 14, by - 14);

      onDone && onDone();
    };

    img.onload = render;
    img.onerror = render;
    img.src = rec.posterUrl;
    if (img.complete) render();
  };

  // ─── Share actions ──────────────────────────────────────────

  // ─── Share actions ──────────────────────────────────────────
  const shareToInstagram = (rec) => {
    const canvas = igCanvasRef.current;
    if (!canvas) return;
    showToast("✨ Generating your story card…", "info");
    drawInstagramCard(canvas, rec, () => {
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File(
          [blob],
          `noryq-${rec.title.replace(/\s+/g, "-")}.png`,
          { type: "image/png" },
        );
        const rating = getRating(rec);
        const SITE = window.location.origin;
        // Caption text copied to clipboard so user can paste as story sticker/text
        const captionText = `🎬 ${rec.title} (${rec.year})${rec.matchScore ? ` — ⚡ ${rec.matchScore}% AI Match` : ""} | ⭐ ${rating}/10\n📺 On ${rec.platform}\n🎯 Find your perfect watch: ${SITE}`;

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            // Copy caption first so user can paste it as a story text sticker
            try {
              await navigator.clipboard.writeText(captionText);
            } catch (_) {}
            // Share ONLY the image file — Instagram Stories rejects text/url alongside files
            await navigator.share({ files: [file] });
            showToast("📸 Shared! Caption copied — paste as story text 🔥");
          } catch (e) {
            if (e.name === "AbortError") {
              showToast("📋 Share cancelled", "info");
            }
            // Other errors: silently ignore (share sheet may have opened but dismissed)
          }
        } else {
          // Desktop: download
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `noryq-${rec.title.replace(/\s+/g, "-")}.png`;
          a.click();
          URL.revokeObjectURL(url);
          showToast("🖼️ Story card downloaded! Upload it to Instagram 📸");
        }
      }, "image/png");
    });
  };

  const shareToWhatsApp = (rec) => {
    const canvas = waCanvasRef.current;
    if (!canvas) return;
    showToast("✨ Generating share card…", "info");
    drawWhatsAppCard(canvas, rec, () => {
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File(
          [blob],
          `noryq-${rec.title.replace(/\s+/g, "-")}.png`,
          { type: "image/png" },
        );
        const rating = getRating(rec);
        const SITE = window.location.origin;
        const text = `🎬 *My ${rec.type} tonight: ${rec.title}* (${rec.year})\n⚡ ${rec.matchScore ? rec.matchScore + "% AI Match | " : ""}⭐ ${rating}/10\n📺 On ${rec.platform}\n\n🎯 Recommended by *NORYQ* — the AI that finds your perfect watch!\n\n👉 Get YOUR free picks: ${SITE}`;
        // Share ONLY the image file — including text/url blocks WhatsApp Status option
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            // Pre-copy text so user can paste it as a caption or Status text
            try {
              await navigator.clipboard.writeText(text);
            } catch (_) {}
            await navigator.share({ files: [file] });
            showToast("✅ Card shared! Caption copied — paste it as text 📋");
            return;
          } catch (e) {
            if (e.name === "AbortError") return; // user cancelled — don't fallback
            /* other error: fall through to URL share */
          }
        }
        // Desktop / fallback: open WhatsApp with text + link
        window.open(
          `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`,
          "_blank",
        );
        showToast("💬 WhatsApp opened with your card text!");
      }, "image/png");
    });
  };

  const shareRecommendation = (rec) => {
    const rating = getRating(rec);
    const SITE = window.location.origin;
    const shareText = `🎬 ${rec.title} (${rec.year}) — ${rec.matchScore ? rec.matchScore + "% Match | " : ""}⭐ ${rating}/10. Recommended by NORYQ 🎯 ${SITE}`;
    if (navigator.share) {
      navigator
        .share({
          title: `${rec.title} — Recommended by NORYQ`,
          text: shareText,
          url: SITE,
        })
        .catch(() => {});
    } else {
      navigator.clipboard.writeText(shareText);
      showToast("🔗 Link copied to clipboard!");
    }
  };
  const addToMyList = async (item) => {
    // Require login to save to list
    if (!user) {
      setSelectedRecommendation(item);
      setShowAuthModal(true);
      return;
    }

    // Check if already in list
    if (myList.some((listItem) => listItem.title === item.title)) {
      showToast("Already in your list!", "info");
      return;
    }

    const newList = [{ ...item, addedAt: new Date().toISOString() }, ...myList];
    setMyList(newList);

    // ✅ PRIORITY SYSTEM: Remove from watch history if exists
    const isInHistory = watchHistory.some(
      (historyItem) => historyItem.title === item.title,
    );

    if (isInHistory) {
      const newHistory = watchHistory.filter(
        (historyItem) => historyItem.title !== item.title,
      );
      setWatchHistory(newHistory);
      await saveWatchHistory(newHistory);
    }

    try {
      if (user) {
        await updateUserData({ myList: newList });
      }
      showToast(`Added "${item.title}" to My List!`);
    } catch (error) {
      console.error("Failed to save to list:", error);
    }
  };
  // ✅ ═══════════════════════════════════════════════════════════
  // ✅ LIKED MOVIES FUNCTIONS
  // ✅ ═══════════════════════════════════════════════════════════

  const handleLikeMovie = async (movie) => {
    if (isLiking) return false; // Prevent double-click
    if (!user) {
      showToast("Sign in to like movies!");
      setShowAuthModal(true);
      return false;
    }
    setIsLiking(true);
    let liked = false;

    try {
      const result = await addToLikedMovies(movie);
      if (result.success) {
        setLikedMovies(result.likedMovies);
        liked = true;

        // ✅ AUTO-ADD TO WATCH HISTORY
        const alreadyWatched = watchHistory.some(
          (item) => item.title.toLowerCase() === movie.title.toLowerCase(),
        );

        if (!alreadyWatched) {
          const newHistory = [
            { ...movie, watchedAt: new Date().toISOString() },
            ...watchHistory,
          ];
          setWatchHistory(newHistory);
          await saveWatchHistory(newHistory);
          showToast(`❤️ Liked "${movie.title}" & added to watch history!`);
        } else {
          showToast(`❤️ Liked "${movie.title}"!`);
        }
      } else {
        showToast(result.message);
      }
    } catch (error) {
      console.error("Error liking movie:", error);
      showToast("Failed to like movie");
    } finally {
      setIsLiking(false);
    }
    return liked;
  };

  const handleUnlikeMovie = async (movieTitle) => {
    try {
      const result = await removeFromLikedMovies(movieTitle);
      setLikedMovies(result.likedMovies);
      showToast(`Removed "${movieTitle}" from liked movies`);
    } catch (error) {
      console.error("Error unliking movie:", error);
      showToast("Failed to remove");
    }
  };

  const checkIfLiked = (movieTitle) => {
    return likedMovies.some(
      (m) => m.title.toLowerCase() === movieTitle.toLowerCase(),
    );
  };

  // Manual search for liked movies
  const handleLikedMovieSearch = async () => {
    if (!likedSearchQuery.trim()) return;

    const searchMovie = {
      title: likedSearchQuery.trim(),
      type: "Movie",
      posterUrl: "",
      imdbRating: "N/A",
      genre: "",
    };

    await handleLikeMovie(searchMovie);
    setLikedSearchQuery("");
  };
  // ✅ ═══════════════════════════════════════════════════════════
  // ✅ TMDB AUTOCOMPLETE SEARCH
  // ✅ ═══════════════════════════════════════════════════════════

  const searchMoviesFromTMDB = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const reqId = ++searchReqRef.current;
    setIsSearching(true);

    try {
      const response = await fetch(
        `/api/tmdb/search?q=${encodeURIComponent(query)}`,
      );

      if (!response.ok) {
        throw new Error(`TMDB API error: ${response.status}`);
      }

      const data = await response.json();
      if (reqId !== searchReqRef.current) return; // a newer search replaced this one

      const suggestions = data.results
        .filter(
          (item) => item.media_type === "movie" || item.media_type === "tv",
        )
        .slice(0, 8) // Show top 8 results
        .map((item) => ({
          title: item.title || item.name,
          year: item.release_date
            ? new Date(item.release_date).getFullYear()
            : item.first_air_date
              ? new Date(item.first_air_date).getFullYear()
              : "N/A",
          type: item.media_type === "movie" ? "Movie" : "TV Show",
          posterUrl: item.poster_path
            ? `https://image.tmdb.org/t/p/w500${item.poster_path}`
            : "",
          imdbRating: item.vote_average ? item.vote_average.toFixed(1) : "N/A",
          genre: "", // We can add genre mapping if needed
        }));

      setSearchSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);

      console.log(`✅ Found ${suggestions.length} results for "${query}"`);
    } catch (error) {
      console.error("TMDB search error:", error);
      if (reqId !== searchReqRef.current) return;

      // Fallback: show what user typed
      setSearchSuggestions([
        {
          title: query,
          year: "N/A",
          type: "Movie",
          posterUrl: "",
          imdbRating: "N/A",
          genre: "",
        },
      ]);
      setShowSuggestions(true);
    } finally {
      if (reqId === searchReqRef.current) setIsSearching(false);
    }
  };

  // Handle selecting a suggestion from dropdown
  const handleSelectSuggestion = async (movie) => {
    await handleLikeMovie(movie);
    setLikedSearchQuery("");
    setSearchSuggestions([]);
    setShowSuggestions(false);
  };

  // Handle clicking outside to close dropdown
  const handleClickOutside = () => {
    setShowSuggestions(false);
  };
  const removeFromMyList = async (item) => {
    const newList = myList.filter((listItem) => listItem.title !== item.title);
    setMyList(newList);

    try {
      if (user) {
        await updateUserData({ myList: newList });
      }
    } catch (error) {
      console.error("Failed to remove from list:", error);
    }
  };
  // ✅ ADD THIS NEW FUNCTION
  const removeFromWatchHistory = async (item) => {
    const newHistory = watchHistory.filter(
      (historyItem) => historyItem.title !== item.title,
    );
    setWatchHistory(newHistory);

    try {
      await saveWatchHistory(newHistory);
      showToast(`Removed "${item.title}" from watch history!`, "success");
    } catch (error) {
      console.error("Failed to remove from history:", error);
      showToast("Failed to remove. Please try again.", "error");
    }
  };
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  };
  // IMDb RATING BADGE COMPONENT
  // ============================================

  // ============================================
  // POSTER IMAGE WITH FALLBACK
  // ============================================
  const PosterImage = ({ posterUrl, title, type }) => {
    const [imageError, setImageError] = useState(false);

    // Check if posterUrl is invalid or placeholder
    const isInvalidUrl =
      !posterUrl ||
      posterUrl.includes("xxx.jpg") ||
      posterUrl.includes("placeholder") ||
      imageError;

    if (isInvalidUrl) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background:
              type === "Movie"
                ? "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)"
                : "linear-gradient(135deg, #614385 0%, #516395 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "2.5rem",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Elegant pattern overlay */}
          <div
            style={{
              position: "absolute",
              top: "-50%",
              left: "-50%",
              width: "200%",
              height: "200%",
              background:
                "radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)",
              backgroundSize: "30px 30px",
              opacity: 0.4,
            }}
          />

          {/* Content */}
          <div style={{ position: "relative", zIndex: 1 }}>
            <div
              style={{
                fontSize: "5rem",
                marginBottom: "1.5rem",
                filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.4))",
                animation: "float 3s ease-in-out infinite",
              }}
            >
              {type === "Movie" || type === "Film" ? (
                <Film
                  size={56}
                  color="rgba(167,139,250,0.35)"
                  strokeWidth={1}
                />
              ) : (
                <Tv size={56} color="rgba(167,139,250,0.35)" strokeWidth={1} />
              )}
            </div>
            <div
              style={{
                fontSize: "1.6rem",
                fontWeight: "900",
                textShadow: "0 4px 20px rgba(0,0,0,0.6)",
                lineHeight: "1.25",
                maxWidth: "85%",
                margin: "0 auto",
                letterSpacing: "-0.5px",
              }}
            >
              {title}
            </div>
          </div>

          <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
        `}</style>
        </div>
      );
    }

    return (
      <img
        src={posterUrl}
        alt={title}
        onError={() => setImageError(true)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    );
  };
  // ============================================
  // WAITLIST MODAL COMPONENT
  // ============================================
  const WaitlistModal = () => {
    if (!showWaitlistModal) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.85)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
          padding: "2rem",
        }}
        onClick={() => {
          if (!waitlistSubmitted) setShowWaitlistModal(false);
        }}
      >
        <div
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "24px",
            padding: "3rem",
            maxWidth: "500px",
            width: "100%",
            color: "#fff",
            position: "relative",
            animation: "slideIn 0.3s ease-out",
          }}
          className="noryq-modal-inner"
          onClick={(e) => e.stopPropagation()}
        >
          {!waitlistSubmitted ? (
            <>
              {/* Close button */}
              <button
                onClick={() => setShowWaitlistModal(false)}
                style={{
                  position: "absolute",
                  top: "1rem",
                  right: "1rem",
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "36px",
                  height: "36px",
                  cursor: "pointer",
                  color: "#fff",
                  fontSize: "1.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.background = "rgba(255,255,255,0.3)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.background = "rgba(255,255,255,0.2)")
                }
              >
                ×
              </button>

              {/* Icon */}
              <div
                style={{
                  marginBottom: "1.5rem",
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Crown size={64} style={{ color: "rgba(255,255,255,0.9)" }} />
              </div>

              {/* Title */}
              <h2
                style={{
                  fontSize: "2rem",
                  fontWeight: "800",
                  marginBottom: "1rem",
                  textAlign: "center",
                }}
              >
                Join the Waitlist
              </h2>

              {/* Subtitle */}
              <p
                style={{
                  fontSize: "1rem",
                  opacity: 0.9,
                  marginBottom: "2rem",
                  textAlign: "center",
                }}
              >
                Be among the first to experience NORYQ Premium when we launch!
              </p>

              {/* Form */}
              <div style={{ marginBottom: "1.5rem" }}>
                {/* Show name input only if not logged in */}
                {!user && (
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={waitlistName}
                    onChange={(e) => setWaitlistName(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "1rem",
                      borderRadius: "12px",
                      border: "none",
                      marginBottom: "1rem",
                      fontSize: "1rem",
                      fontFamily: '"DM Sans", sans-serif',
                      boxSizing: "border-box",
                    }}
                  />
                )}

                {/* Show email input only if not logged in */}
                {!user && (
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "1rem",
                      borderRadius: "12px",
                      border: "none",
                      fontSize: "1rem",
                      fontFamily: '"DM Sans", sans-serif',
                      boxSizing: "border-box",
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitWaitlist();
                    }}
                  />
                )}

                {/* If logged in, show their email */}
                {user && (
                  <div
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      padding: "1rem",
                      borderRadius: "12px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.875rem",
                        opacity: 0.8,
                        marginBottom: "0.25rem",
                      }}
                    >
                      Signing up as:
                    </div>
                    <div style={{ fontWeight: "700" }}>{user.email}</div>
                  </div>
                )}
              </div>

              {/* Consent for waitlist emails */}
              <label
                style={{
                  display: "flex",
                  gap: "0.6rem",
                  alignItems: "flex-start",
                  marginBottom: "1.5rem",
                  fontSize: "0.8125rem",
                  lineHeight: 1.5,
                  cursor: "pointer",
                  background: "rgba(255,255,255,0.12)",
                  padding: "0.75rem",
                  borderRadius: "12px",
                }}
              >
                <input
                  type="checkbox"
                  checked={waitlistConsent}
                  onChange={(e) => setWaitlistConsent(e.target.checked)}
                  style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }}
                />
                <span>
                  I agree that NORYQ may use my name and email only to tell me
                  when Premium launches. I can withdraw this any time (see the{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#fff", textDecoration: "underline" }}
                  >
                    Privacy Policy
                  </a>
                  ).
                </span>
              </label>

              {/* Benefits */}
              <div
                style={{
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "12px",
                  padding: "1.5rem",
                  marginBottom: "2rem",
                }}
              >
                <div
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "700",
                    marginBottom: "1rem",
                  }}
                >
                  What you'll get:
                </div>
                {[
                  "Early bird discount",
                  "Exclusive launch perks",
                  "Priority access",
                ].map((benefit, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Sparkles size={18} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: "0.875rem" }}>{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Submit Button */}
              <button
                onClick={submitWaitlist}
                style={{
                  width: "100%",
                  padding: "1.25rem",
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: "700",
                  fontSize: "1.125rem",
                  transition: "transform 0.2s",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.transform = "translateY(-2px)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.transform = "translateY(0)")
                }
              >
                Join Waitlist
              </button>
            </>
          ) : (
            // Success State
            <>
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    marginBottom: "1rem",
                    display: "flex",
                    justifyContent: "center",
                    animation: "bounce 1s infinite",
                  }}
                >
                  <CheckCircle2
                    size={80}
                    style={{ color: "rgba(255,255,255,0.95)" }}
                  />
                </div>
                <h2
                  style={{
                    fontSize: "2rem",
                    fontWeight: "800",
                    marginBottom: "1rem",
                  }}
                >
                  You're In!
                </h2>
                <p
                  style={{
                    fontSize: "1.125rem",
                    opacity: 0.9,
                    marginBottom: "2rem",
                    lineHeight: "1.6",
                  }}
                >
                  We'll notify you at{" "}
                  <strong>{user?.email || waitlistEmail}</strong> when Premium
                  launches!
                </p>
              </div>
            </>
          )}

          {/* CSS Animations */}
          <style>{`
            @keyframes slideIn {
              from {
                opacity: 0;
                transform: translateY(-20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            @keyframes bounce {
              0%, 100% {
                transform: translateY(0);
              }
              50% {
                transform: translateY(-20px);
              }
            }
          `}</style>
        </div>
      </div>
    );
  };

  // ✅ ADD THIS NEW COMPONENT
  const FiltersModal = ({
    show,
    onClose,
    filters,
    setFilters,
    isPremium,
    onApply,
  }) => {
    if (!show) return null;

    const handleFilterChange = (key, value) => {
      setFilters((prev) => ({
        ...prev,
        [key]: prev[key] === value ? null : value,
      }));
    };

    const handleExcludeGenre = (genre) => {
      setFilters((prev) => ({
        ...prev,
        excludeGenres: prev.excludeGenres.includes(genre)
          ? prev.excludeGenres.filter((g) => g !== genre)
          : [...prev.excludeGenres, genre],
      }));
    };

    const clearAllFilters = () => {
      setFilters({
        minRating: null,
        yearRange: null,
        runtime: null,
        language: null,
        platform: null,
        excludeGenres: [],
      });
    };

    const hasActiveFilters = Object.values(filters).some((v) =>
      Array.isArray(v) ? v.length > 0 : v !== null,
    );

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.8)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
          padding: "2rem",
          overflowY: "auto",
        }}
        onClick={onClose}
      >
        <div
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "20px",
            padding: "2.5rem",
            maxWidth: "600px",
            width: "100%",
            maxHeight: "90vh",
            overflowY: "auto",
            color: "#fff",
            position: "relative",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{ marginBottom: "2rem" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <h2
                style={{
                  fontSize: "2rem",
                  fontWeight: "800",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Pencil size={28} /> Advanced Filters
              </h2>
              <button
                onClick={onClose}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  cursor: "pointer",
                  color: "#fff",
                  fontSize: "1.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>

            {/* TEMPORARY CHANGE: Advanced Filter enabled for all users regardless of subscription or authentication. */}
            {/* {!isPremium && (
              <div
                style={{
                  background: "rgba(255, 107, 107, 0.2)",
                  border: "2px solid rgba(255, 107, 107, 0.5)",
                  borderRadius: "12px",
                  padding: "1rem",
                  marginBottom: "1.5rem",
                }}
              >
                <div
                  style={{
                    fontWeight: "700",
                    marginBottom: "0.5rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Lock size={16} /> Premium Feature
                </div>
                <div style={{ fontSize: "0.875rem", opacity: 0.9 }}>
                  Advanced filters are available for Premium users only. Upgrade
                  to unlock!
                </div>
              </div>
            )} */}

            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                // TEMPORARY CHANGE: Advanced Filter enabled for all users regardless of subscription or authentication.
                // disabled={!isPremium}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "8px",
                  padding: "0.5rem 1rem",
                  color: "#fff",
                  cursor: "pointer", // was: isPremium ? "pointer" : "not-allowed"
                  opacity: 1, // was: isPremium ? 1 : 0.5
                  fontSize: "0.875rem",
                }}
              >
                Clear All Filters
              </button>
            )}
          </div>

          {/* Minimum Rating Filter */}
          <div style={{ marginBottom: "2rem" }}>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "700",
                marginBottom: "1rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Star size={18} style={{ flexShrink: 0 }} /> Minimum Rating{" "}
              {!isPremium && <Lock size={14} style={{ opacity: 0.7 }} />}
            </div>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              {[
                { label: "Any", value: null },
                { label: "7.0+", value: 7.0 },
                { label: "8.0+", value: 8.0 },
                { label: "9.0+", value: 9.0 },
              ].map((option) => (
                <button
                  key={option.label}
                  onClick={() =>
                    isPremium && handleFilterChange("minRating", option.value)
                  }
                  // TEMPORARY CHANGE: Advanced Filter enabled for all users regardless of subscription or authentication.
                  disabled={!isPremium}
                  style={{
                    padding: "0.6rem 1rem",
                    fontSize: "0.875rem",
                    borderRadius: "12px",
                    border: "none",
                    background:
                      filters.minRating === option.value
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(255,255,255,0.1)",
                    color: "#fff",
                    cursor: isPremium ? "pointer" : "not-allowed", // was: isPremium ? "pointer" : "not-allowed"
                    opacity: isPremium ? 1 : 0.5, // was: isPremium ? 1 : 0.5
                    fontWeight:
                      filters.minRating === option.value ? "700" : "500",

                    transition: "all 0.2s",
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Year Range Filter */}
          <div style={{ marginBottom: "2rem" }}>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "700",
                marginBottom: "1rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Calendar size={18} style={{ flexShrink: 0 }} /> Release Year{" "}
              {!isPremium && <Lock size={14} style={{ opacity: 0.7 }} />}
            </div>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              {[
                { label: "Any", value: null },
                { label: "2025+", value: "2025+" },
                { label: "2020-25", value: "2020-2025" },
                { label: "2015-20", value: "2015-2020" },
                { label: "2010-15", value: "2010-2015" },
                { label: "2005-10", value: "2005-2010" },
                { label: "Classics", value: "classics" },
              ].map((option) => (
                <button
                  key={option.label}
                  onClick={() =>
                    isPremium && handleFilterChange("yearRange", option.value)
                  }
                  // TEMPORARY CHANGE: Advanced Filter enabled for all users regardless of subscription or authentication.
                  disabled={!isPremium}
                  style={{
                    padding: "0.6rem 1rem",
                    fontSize: "0.875rem",
                    borderRadius: "12px",
                    border: "none",
                    background:
                      filters.yearRange === option.value
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(255,255,255,0.1)",
                    color: "#fff",
                    cursor: isPremium ? "pointer" : "not-allowed", // was: isPremium ? "pointer" : "not-allowed"
                    opacity: isPremium ? 1 : 0.5, // was: isPremium ? 1 : 0.5
                    fontWeight:
                      filters.yearRange === option.value ? "700" : "500",

                    transition: "all 0.2s",
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Runtime Filter */}
          <div style={{ marginBottom: "2rem" }}>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "700",
                marginBottom: "1rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Timer size={18} style={{ flexShrink: 0 }} /> Runtime{" "}
              {/* TEMPORARY CHANGE: Advanced Filter enabled for all users regardless of subscription or authentication. */}
              {/* {!isPremium && <Lock size={14} style={{ opacity: 0.7 }} />} */}
            </div>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              {[
                { label: "Any", value: null },
                { label: "Under 90 min", value: "under-90" },
                { label: "90-120 min", value: "90-120" },
                { label: "2-3 hours", value: "120-180" },
                { label: "3+ hours", value: "180+" },
              ].map((option) => (
                <button
                  key={option.label}
                  onClick={() =>
                    /* isPremium && */ handleFilterChange(
                      "runtime",
                      option.value,
                    )
                  }
                  //disabled={!isPremium}
                  style={{
                    padding: "0.6rem 1rem",
                    fontSize: "0.875rem",
                    borderRadius: "12px",
                    border: "none",
                    background:
                      filters.runtime === option.value
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(255,255,255,0.1)",
                    color: "#fff",
                    cursor: "pointer", // was: isPremium ? "pointer" : "not-allowed"
                    opacity: 1, // was: isPremium ? 1 : 0.5
                    fontWeight:
                      filters.runtime === option.value ? "700" : "500",
                    transition: "all 0.2s",
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Language Filter */}
          <div style={{ marginBottom: "2rem" }}>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "700",
                marginBottom: "1rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Activity size={18} style={{ flexShrink: 0 }} /> Language{" "}
              {/* TEMPORARY CHANGE: Advanced Filter enabled for all users regardless of subscription or authentication. */}
              {/* {!isPremium && <Lock size={14} style={{ opacity: 0.7 }} />} */}
            </div>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              {[
                { label: "Any", value: null },
                { label: "English", value: "English" },
                { label: "Hindi", value: "Hindi" },
                { label: "Korean", value: "Korean" },
                { label: "Japanese", value: "Japanese" },
                { label: "Spanish", value: "Spanish" },
                { label: "French", value: "French" },
              ].map((option) => (
                <button
                  key={option.label}
                  onClick={() =>
                    /* isPremium && */ handleFilterChange(
                      "language",
                      option.value,
                    )
                  }
                  //disabled={!isPremium}
                  style={{
                    padding: "0.6rem 1rem",
                    fontSize: "0.875rem",
                    borderRadius: "12px",
                    border: "none",
                    background:
                      filters.language === option.value
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(255,255,255,0.1)",
                    color: "#fff",
                    cursor: "pointer", // was: isPremium ? "pointer" : "not-allowed"
                    opacity: 1, // was: isPremium ? 1 : 0.5
                    fontWeight:
                      filters.language === option.value ? "700" : "500",

                    transition: "all 0.2s",
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Platform Filter */}
          <div style={{ marginBottom: "2rem" }}>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "700",
                marginBottom: "1rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Monitor size={18} style={{ flexShrink: 0 }} /> Platform{" "}
              {/* TEMPORARY CHANGE: Advanced Filter enabled for all users regardless of subscription or authentication. */}
              {!isPremium && <Lock size={14} style={{ opacity: 0.7 }} />}
            </div>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              {[
                { label: "Any", value: null },
                { label: "Netflix", value: "Netflix" },
                { label: "Prime Video", value: "Prime" },
                { label: "Disney+", value: "Hotstar" },
                { label: "HBO Max", value: "HBO" },
              ].map((option) => (
                <button
                  key={option.label}
                  onClick={() =>
                    /* isPremium && */ handleFilterChange(
                      "platform",
                      option.value,
                    )
                  }
                  disabled={!isPremium}
                  style={{
                    padding: "0.6rem 1rem",
                    fontSize: "0.875rem",
                    borderRadius: "12px",
                    border: "none",
                    background:
                      filters.platform === option.value
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(255,255,255,0.1)",
                    color: "#fff",
                    cursor: isPremium ? "pointer" : "not-allowed", // was: isPremium ? "pointer" : "not-allowed"
                    opacity: isPremium ? 1 : 0.5, // was: isPremium ? 1 : 0.5
                    fontWeight:
                      filters.platform === option.value ? "700" : "500",
                    transition: "all 0.2s",
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Exclude Genres */}
          <div style={{ marginBottom: "2rem" }}>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "700",
                marginBottom: "1rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <X size={18} style={{ flexShrink: 0 }} /> Exclude Genres{" "}
              {/* TEMPORARY CHANGE: Advanced Filter enabled for all users regardless of subscription or authentication. */}
              {/* {!isPremium && <Lock size={14} style={{ opacity: 0.7 }} />} */}
            </div>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              {[
                "Horror",
                "Romance",
                "Comedy",
                "Documentary",
                "Musical",
                "Action",
                "Drama",
                "Animation",
                "Crime",
              ].map((genre) => (
                <button
                  key={genre}
                  onClick={() => /* isPremium && */ handleExcludeGenre(genre)}
                  //disabled={!isPremium}
                  style={{
                    padding: "0.75rem 1.5rem",
                    borderRadius: "12px",
                    border: "none",
                    background: filters.excludeGenres.includes(genre)
                      ? "rgba(231, 76, 60, 0.3)"
                      : "rgba(255,255,255,0.1)",
                    color: "#fff",
                    cursor: "pointer", // was: isPremium ? "pointer" : "not-allowed"
                    opacity: 1, // was: isPremium ? 1 : 0.5
                    fontWeight: filters.excludeGenres.includes(genre)
                      ? "700"
                      : "500",

                    transition: "all 0.2s",
                  }}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>

          {/* Apply Button */}
          <button
            onClick={() => {
              // TEMPORARY CHANGE: Advanced Filter enabled for all users regardless of subscription or authentication.
              // Free users can only close modal
              // if (!isPremium) {
              //   onClose();
              //   showToast("Advanced Filters are a Premium feature!", "info");
              //   return;
              // }
              // Apply filters for all users
              if (onApply) {
                onApply();
              } else {
                onClose();
              }
            }}
            style={{
              width: "100%",
              padding: "1.125rem",
              background: "#fff", // was: isPremium ? "#fff" : "rgba(255,255,255,0.2)"
              color: "#667eea", // was: isPremium ? "#667eea" : "#fff"
              border: "none", // was: isPremium ? "none" : "2px solid rgba(255,255,255,0.3)"
              borderRadius: "12px",
              fontSize: "1.125rem",
              fontWeight: "700",
              cursor: "pointer",
              transition: "transform 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.target.style.transform = "translateY(-2px)")
            }
            onMouseLeave={(e) => (e.target.style.transform = "translateY(0)")}
          >
            Apply Filters {/* was: {isPremium ? "Apply Filters" : "Close"} */}
          </button>
          {/* TEMPORARY CHANGE: Advanced Filter enabled for all users regardless of subscription or authentication. */}
          {/*{!isPremium && (
            <button
              onClick={() => {
                onClose();
                setCurrentView("premium");
              }}
              style={{
                width: "100%",
                padding: "1rem",
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                fontSize: "1rem",
                fontWeight: "700",
                cursor: "pointer",
                marginTop: "1rem",
              }}
            >
              Upgrade to Premium
            </button>
          )} */}
        </div>
      </div>
    );
  };

  // ── Hidden canvases for share card generation ──
  const hiddenCanvases = (
    <>
      <canvas
        ref={igCanvasRef}
        width={1080}
        height={1920}
        style={{ display: "none" }}
      />
      <canvas
        ref={waCanvasRef}
        width={1080}
        height={1080}
        style={{ display: "none" }}
      />
    </>
  );

  // HOME VIEW
  if (currentView === "home") {
    return (
      <>
        <div
          style={{
            minHeight: "100vh",
            width: "100%",
            fontFamily: '"DM Sans", -apple-system, sans-serif',
            color: "#fff",
            overflow: "hidden",
            position: "relative",
            boxSizing: "border-box",
          }}
        >
          {/* ADD MOBILE BANNER HERE: */}
          <MobileInstallBanner />
          {/* Hidden share canvases */}
          {hiddenCanvases}
          {/* Animated Background */}
          <AnimatedBackground variant="waves" />

          {/* Content Layer */}
          <div style={{ position: "relative", zIndex: 1 }}>
            {/* EMAIL VERIFICATION BANNER - ADD THIS ENTIRE BLOCK */}
            {user && !user.emailVerified && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  background:
                    "linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)",
                  color: "#fff",
                  padding: "1rem 2rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  zIndex: 9999,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                  animation: "slideDown 0.3s ease-out",
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    flex: 1,
                  }}
                >
                  <div
                    className="noryq-email-icon"
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.5rem",
                      flexShrink: 0,
                    }}
                  >
                    <AlertTriangle size={20} />
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: "700",
                        fontSize: "1rem",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Please Verify Your Email
                    </div>
                    <div style={{ fontSize: "0.875rem", opacity: 0.9 }}>
                      Check your inbox/spam for the verification link. You must
                      verify your email to get recommendations.
                    </div>
                  </div>
                </div>

                <div
                  style={{ display: "flex", gap: "0.75rem", flexShrink: 0 }}
                  className="noryq-email-banner-btns"
                >
                  <button
                    onClick={async () => {
                      try {
                        await user.reload();
                        if (user.emailVerified) {
                          setShowEmailVerificationBanner(false);
                          showToast(
                            "Email verified! You can now use all features!",
                          );
                        } else {
                          showToast(
                            "Email not verified yet. Please check your inbox/spam.",
                            "info",
                          );
                        }
                      } catch (error) {
                        console.error("Error checking verification:", error);
                        showToast(
                          "Error checking verification. Please try again.",
                          "error",
                        );
                      }
                    }}
                    style={{
                      padding: "0.625rem 1.25rem",
                      background: "rgba(255,255,255,0.2)",
                      border: "none",
                      borderRadius: "8px",
                      color: "#fff",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "0.875rem",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.background = "rgba(255,255,255,0.3)")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.background = "rgba(255,255,255,0.2)")
                    }
                  >
                    I've Verified
                  </button>

                  <button
                    onClick={async () => {
                      try {
                        await sendEmailVerification(user, {
                          url: window.location.origin,
                          handleCodeInApp: false,
                        });
                        showToast(
                          "Verification email sent! Check your inbox.",
                          "success",
                        );
                      } catch (error) {
                        console.error("Error sending verification:", error);
                        showToast(
                          "Error sending email. Please try again after few minutes.",
                          "error",
                        );
                      }
                    }}
                    style={{
                      padding: "0.625rem 1.25rem",
                      background: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      color: "#ff6b6b",
                      cursor: "pointer",
                      fontWeight: "700",
                      fontSize: "0.875rem",
                      transition: "transform 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.transform = "translateY(-2px)")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.transform = "translateY(0)")
                    }
                  >
                    Resend Email
                  </button>
                </div>
                {toast && (
                  <div
                    style={{
                      position: "fixed",
                      top: "2rem",
                      left: "50%",
                      transform: "translateX(-50%)",
                      background:
                        toast.type === "success"
                          ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                          : toast.type === "info"
                            ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                            : "linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)",
                      color: "#fff",
                      padding: "1rem 2rem",
                      borderRadius: "12px",
                      boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
                      zIndex: 10000,
                      fontSize: "1rem",
                      fontWeight: "600",
                      animation: "slideDown 0.3s ease-out",
                      maxWidth: "calc(100vw - 2rem)",
                      textAlign: "center",
                    }}
                  >
                    {toast.message}
                  </div>
                )}
              </div>
            )}
            {/* END OF EMAIL VERIFICATION BANNER */}

            <div
              style={{ position: "relative", zIndex: 1, padding: "2rem" }}
              className="noryq-page-pad"
            >
              {/* Responsive Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "4rem",
                  position: "relative",
                }}
                className="noryq-header"
              >
                {/* Logo Section - Fixed Size */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    cursor: "pointer",
                    flexShrink: 0, // Prevents logo from shrinking
                  }}
                  onClick={() => setCurrentView("home")}
                >
                  <img
                    src="/logo.png"
                    alt="NORYQ"
                    style={{
                      height: "50px",
                      width: "auto", // Maintains aspect ratio
                      display: "block",
                    }}
                  />
                </div>

                {/* Desktop Navigation - Hidden on Mobile */}
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    alignItems: "center",
                  }}
                  className="desktop-nav"
                >
                  {/* Explore button */}
                  <button
                    onClick={() => {
                      window.location.href = "/explore";
                    }}
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: "rgba(255,255,255,0.2)",
                      border: "none",
                      borderRadius: "12px",
                      color: "#fff",
                      cursor: "pointer",
                      fontWeight: "600",
                      backdropFilter: "blur(10px)",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <Compass size={18} />
                    Explore
                  </button>
                  {/* Trending button */}
                  <button
                    onClick={() => setCurrentView("trending")}
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: "rgba(255,255,255,0.2)",
                      border: "none",
                      borderRadius: "12px",
                      color: "#fff",
                      cursor: "pointer",
                      fontWeight: "600",
                      backdropFilter: "blur(10px)",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <Fire size={18} />
                    Trending
                  </button>

                  {/* History button */}
                  {user && (
                    <button
                      onClick={() => setCurrentView("history")}
                      style={{
                        padding: "0.75rem 1.5rem",
                        background: "rgba(255,255,255,0.2)",
                        border: "none",
                        borderRadius: "12px",
                        color: "#fff",
                        cursor: "pointer",
                        fontWeight: "600",
                        backdropFilter: "blur(10px)",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <Clock size={18} />
                      History
                    </button>
                  )}

                  {/* My List button */}
                  {user && (
                    <button
                      onClick={() => setCurrentView("mylist")}
                      style={{
                        padding: "0.75rem 1.5rem",
                        background: "rgba(255,255,255,0.2)",
                        border: "none",
                        borderRadius: "12px",
                        color: "#fff",
                        cursor: "pointer",
                        fontWeight: "600",
                        backdropFilter: "blur(10px)",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        position: "relative",
                      }}
                    >
                      <BookmarkCheck size={18} />
                      My List
                      {/* Badge - Show count if items exist */}
                      {myList.length > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            top: "-6px",
                            right: "-6px",
                            background:
                              "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                            color: "#fff",
                            borderRadius: "50%",
                            width: "22px",
                            height: "22px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.75rem",
                            fontWeight: "700",
                            border: "2px solid rgba(102,126,234,1)",
                          }}
                        >
                          {myList.length > 9 ? "9+" : myList.length}
                        </div>
                      )}
                    </button>
                  )}
                  {/* Liked Movies button */}
                  {user && (
                    <button
                      onClick={() => setCurrentView("liked")}
                      style={{
                        padding: "0.75rem 1.5rem",
                        background: "rgba(255,255,255,0.2)",
                        border: "none",
                        borderRadius: "12px",
                        color: "#fff",
                        cursor: "pointer",
                        fontWeight: "600",
                        backdropFilter: "blur(10px)",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        position: "relative",
                      }}
                    >
                      <Heart size={18} />
                      Liked
                    </button>
                  )}

                  {/* Premium button */}
                  {user && !isPremium && (
                    <button
                      onClick={() => setCurrentView("premium")}
                      style={{
                        padding: "0.75rem 1.5rem",
                        background:
                          "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        border: "none",
                        borderRadius: "12px",
                        color: "#fff",
                        cursor: "pointer",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <Crown size={18} />
                      Go Premium
                    </button>
                  )}

                  {/* Premium badge */}
                  {user && isPremium && (
                    <div
                      style={{
                        padding: "0.75rem 1.5rem",
                        background:
                          "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        borderRadius: "12px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <Crown size={18} />
                      Member
                    </div>
                  )}

                  {/* User menu or Login button */}
                  {user ? (
                    <div style={{ position: "relative" }}>
                      <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        style={{
                          width: "45px",
                          height: "45px",
                          borderRadius: "50%",
                          background: "rgba(255,255,255,0.2)",
                          border: "2px solid rgba(255,255,255,0.3)",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "700",
                          fontSize: "1.125rem",
                          backdropFilter: "blur(10px)",
                          transition: "all 0.2s",
                          overflow: "hidden",
                        }}
                      >
                        {user.photoURL ? (
                          <img
                            src={user.photoURL}
                            alt="Profile"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <User size={24} />
                        )}
                      </button>

                      {/* Desktop User Dropdown Menu */}
                      {showUserMenu && (
                        <>
                          <div
                            onClick={() => setShowUserMenu(false)}
                            style={{
                              position: "fixed",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              zIndex: 999,
                            }}
                          />

                          <div
                            style={{
                              position: "absolute",
                              top: "calc(100% + 0.5rem)",
                              right: 0,
                              background: "rgba(255,255,255,0.95)",
                              backdropFilter: "blur(10px)",
                              borderRadius: "12px",
                              padding: "0.5rem",
                              minWidth: "220px",
                              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
                              zIndex: 1000,
                            }}
                          >
                            <div
                              style={{
                                padding: "1rem",
                                borderBottom: "1px solid rgba(102,126,234,0.2)",
                                marginBottom: "0.5rem",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "700",
                                  color: "#667eea",
                                  marginBottom: "0.25rem",
                                }}
                              >
                                {user.displayName || "User"}
                              </div>
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#666",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {user.email}
                              </div>
                              {isPremium && (
                                <div
                                  style={{
                                    width: "100%",
                                    padding: "0.5rem 1rem",
                                    background:
                                      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                    borderRadius: "8px",
                                    color: "#fff",
                                    fontWeight: "600",
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: "12px",
                                    gap: "0.75rem",
                                    marginTop: "0.6rem",
                                  }}
                                >
                                  <Crown size={18} />
                                  Premium Member
                                </div>
                              )}
                              {todayStreak > 0 && (
                                <div
                                  style={{
                                    marginTop: "0.5rem",
                                    fontSize: "0.75rem",
                                    color: "#f5576c",
                                    fontWeight: "600",
                                  }}
                                >
                                  <Flame
                                    size={14}
                                    color="#f5576c"
                                    style={{ marginRight: "0.25rem" }}
                                  />{" "}
                                  {todayStreak} day streak!
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                setShowUserMenu(false);

                                setCurrentView("mylist");
                                setShowMobileMenu(false);
                              }}
                              style={{
                                width: "100%",
                                padding: "0.75rem 1rem",
                                background: "transparent",
                                border: "none",
                                borderRadius: "8px",
                                color: "#667eea",
                                cursor: "pointer",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                transition: "background 0.2s",
                                textAlign: "left",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background =
                                  "rgba(102,126,234,0.1)")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background = "transparent")
                              }
                            >
                              <BookmarkCheck size={18} />
                              My List
                            </button>
                            <button
                              onClick={() => {
                                setShowUserMenu(false);
                                setCurrentView("history");
                              }}
                              style={{
                                width: "100%",
                                padding: "0.75rem 1rem",
                                background: "transparent",
                                border: "none",
                                borderRadius: "8px",
                                color: "#667eea",
                                cursor: "pointer",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                transition: "background 0.2s",
                                textAlign: "left",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background =
                                  "rgba(102,126,234,0.1)")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background = "transparent")
                              }
                            >
                              <Clock size={18} />
                              Watch History
                            </button>
                            <button
                              onClick={() => {
                                setShowUserMenu(false);
                                setCurrentView("liked");
                              }}
                              style={{
                                width: "100%",
                                padding: "0.75rem 1rem",
                                background: "transparent",
                                border: "none",
                                borderRadius: "8px",
                                color: "#667eea",
                                cursor: "pointer",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                transition: "background 0.2s",
                                textAlign: "left",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background =
                                  "rgba(102,126,234,0.1)")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background = "transparent")
                              }
                            >
                              <Heart size={18} />
                              Liked Movies
                            </button>
                            {!isPremium && (
                              <button
                                onClick={() => {
                                  setShowUserMenu(false);
                                  setCurrentView("premium");
                                }}
                                style={{
                                  width: "100%",
                                  padding: "0.75rem 1rem",
                                  background: "transparent",
                                  border: "none",
                                  borderRadius: "8px",
                                  color: "#667eea",
                                  cursor: "pointer",
                                  fontWeight: "600",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.75rem",
                                  transition: "background 0.2s",
                                  textAlign: "left",
                                }}
                                onMouseEnter={(e) =>
                                  (e.target.style.background =
                                    "rgba(102,126,234,0.1)")
                                }
                                onMouseLeave={(e) =>
                                  (e.target.style.background = "transparent")
                                }
                              >
                                <Crown size={18} />
                                Upgrade to Premium
                              </button>
                            )}

                            <div
                              style={{
                                height: "1px",
                                background: "rgba(102,126,234,0.2)",
                                margin: "0.5rem 0",
                              }}
                            />

                            <button
                              onClick={() => {
                                setShowUserMenu(false);
                                setShowPrivacyData(true);
                              }}
                              style={{
                                width: "100%",
                                padding: "0.75rem 1rem",
                                background: "transparent",
                                border: "none",
                                borderRadius: "8px",
                                color: "#667eea",
                                cursor: "pointer",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                transition: "background 0.2s",
                                textAlign: "left",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background =
                                  "rgba(102,126,234,0.1)")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background = "transparent")
                              }
                            >
                              <Shield size={18} />
                              Privacy &amp; Data
                            </button>
                            <button
                              onClick={async () => {
                                setShowUserMenu(false);
                                await logout();
                              }}
                              style={{
                                width: "100%",
                                padding: "0.75rem 1rem",
                                background: "transparent",
                                border: "none",
                                borderRadius: "8px",
                                color: "#e74c3c",
                                cursor: "pointer",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                transition: "background 0.2s",
                                textAlign: "left",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background =
                                  "rgba(231,76,60,0.1)")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background = "transparent")
                              }
                            >
                              <LogOut size={18} />
                              Sign Out
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAuthModal(true)}
                      style={{
                        padding: "0.75rem 1.5rem",
                        background: "#fff",
                        color: "#667eea",
                        border: "none",
                        borderRadius: "12px",
                        cursor: "pointer",
                        fontWeight: "700",
                        boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                        transition: "transform 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.transform = "translateY(-2px)")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.transform = "translateY(0)")
                      }
                    >
                      Sign In
                    </button>
                  )}
                </div>

                {/* Mobile Hamburger Menu - Visible Only on Mobile */}
                <button
                  className="mobile-menu-btn"
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  style={{
                    display: "none", // Hidden by default, shown via CSS
                    background: "rgba(255,255,255,0.2)",
                    border: "none",
                    borderRadius: "12px",
                    padding: "0.75rem",
                    cursor: "pointer",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  {showMobileMenu ? (
                    <X size={24} color="#fff" />
                  ) : (
                    <Menu size={24} color="#fff" />
                  )}
                </button>

                {/* Mobile Menu Dropdown */}
                {showMobileMenu && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: 0,
                      marginTop: "1rem",
                      background: "rgba(255,255,255,0.95)",
                      backdropFilter: "blur(10px)",
                      borderRadius: "16px",
                      padding: "1rem",
                      minWidth: "250px",
                      boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
                      animation: "slideDown 0.3s ease-out",
                      zIndex: 1000,
                    }}
                    className="mobile-menu"
                  >
                    {/* User Info - If Logged In */}
                    {user && (
                      <div
                        style={{
                          padding: "1rem",
                          borderBottom: "1px solid rgba(102,126,234,0.2)",
                          marginBottom: "0.5rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                        }}
                      >
                        <div
                          style={{
                            width: "45px",
                            height: "45px",
                            borderRadius: "50%",
                            background: "rgba(102,126,234,0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          {user.photoURL ? (
                            <img
                              src={user.photoURL}
                              alt="Profile"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <User size={24} color="#667eea" />
                          )}
                        </div>
                        <div>
                          <div
                            style={{
                              fontWeight: "700",
                              color: "#667eea",
                              marginBottom: "0.25rem",
                            }}
                          >
                            {user.displayName || "User"}
                          </div>
                          {todayStreak > 0 && (
                            <div
                              style={{
                                fontSize: "0.75rem",
                                color: "#f5576c",
                                fontWeight: "600",
                              }}
                            >
                              <Flame
                                size={14}
                                color="#f5576c"
                                style={{ marginRight: "0.25rem" }}
                              />{" "}
                              {todayStreak} day streak
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Menu Items */}
                    <button
                      onClick={() => {
                        window.location.href = "/explore";
                      }}
                      style={{
                        width: "100%",
                        padding: "0.875rem 1rem",
                        background: "transparent",
                        border: "none",
                        borderRadius: "8px",
                        color: "#667eea",
                        cursor: "pointer",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        textAlign: "left",
                        marginBottom: "0.25rem",
                      }}
                    >
                      <Compass size={18} />
                      Explore
                    </button>
                    <button
                      onClick={() => {
                        setCurrentView("trending");
                        setShowMobileMenu(false);
                      }}
                      style={{
                        width: "100%",
                        padding: "0.875rem 1rem",
                        background: "transparent",
                        border: "none",
                        borderRadius: "8px",
                        color: "#667eea",
                        cursor: "pointer",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        textAlign: "left",
                        marginBottom: "0.25rem",
                      }}
                    >
                      <Fire size={18} />
                      Trending
                    </button>

                    {user && (
                      <>
                        <button
                          onClick={() => {
                            setCurrentView("history");
                            setShowMobileMenu(false);
                          }}
                          style={{
                            width: "100%",
                            padding: "0.875rem 1rem",
                            background: "transparent",
                            border: "none",
                            borderRadius: "8px",
                            color: "#667eea",
                            cursor: "pointer",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            textAlign: "left",
                            marginBottom: "0.25rem",
                          }}
                        >
                          <Clock size={18} />
                          History
                        </button>

                        <button
                          onClick={() => {
                            setCurrentView("mylist");
                            setShowMobileMenu(false);
                          }}
                          style={{
                            width: "100%",
                            padding: "0.875rem 1rem",
                            background: "transparent",
                            border: "none",
                            borderRadius: "8px",
                            color: "#667eea",
                            cursor: "pointer",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            textAlign: "left",
                            marginBottom: "0.25rem",
                            position: "relative",
                          }}
                        >
                          <BookmarkCheck size={18} />
                          My List
                          {/* Badge for mobile menu */}
                          {myList.length > 0 && (
                            <div
                              style={{
                                marginLeft: "auto",
                                background:
                                  "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                color: "#fff",
                                borderRadius: "12px",
                                padding: "0.25rem 0.5rem",
                                fontSize: "0.75rem",
                                fontWeight: "700",
                              }}
                            >
                              {myList.length > 9 ? "9+" : myList.length}
                            </div>
                          )}
                        </button>
                        {/* Liked Movies button */}
                        {user && (
                          <button
                            onClick={() => setCurrentView("liked")}
                            style={{
                              width: "100%",
                              padding: "0.875rem 1rem",
                              background: "transparent",
                              border: "none",
                              borderRadius: "8px",
                              color: "#667eea",
                              cursor: "pointer",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              textAlign: "left",
                              marginBottom: "0.25rem",
                              position: "relative",
                            }}
                          >
                            <Heart size={18} />
                            Liked
                          </button>
                        )}
                        {!isPremium && (
                          <button
                            onClick={() => {
                              setCurrentView("premium");
                              setShowMobileMenu(false);
                            }}
                            style={{
                              width: "100%",
                              padding: "0.875rem 1rem",
                              background:
                                "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                              border: "none",
                              borderRadius: "8px",
                              color: "#fff",
                              cursor: "pointer",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              textAlign: "left",
                              marginBottom: "0.25rem",
                            }}
                          >
                            <Crown size={18} />
                            Go Premium
                          </button>
                        )}

                        {isPremium && (
                          <div
                            style={{
                              width: "100%",
                              padding: "0.875rem 1rem",
                              background:
                                "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                              borderRadius: "8px",
                              color: "#fff",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              marginBottom: "0.25rem",
                            }}
                          >
                            <Crown size={18} />
                            Premium Member
                          </div>
                        )}

                        <div
                          style={{
                            height: "1px",
                            background: "rgba(102,126,234,0.2)",
                            margin: "0.5rem 0",
                          }}
                        />

                        <button
                          onClick={() => {
                            setShowMobileMenu(false);
                            setShowPrivacyData(true);
                          }}
                          style={{
                            width: "100%",
                            padding: "0.875rem 1rem",
                            background: "transparent",
                            border: "none",
                            borderRadius: "8px",
                            color: "#667eea",
                            cursor: "pointer",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            textAlign: "left",
                          }}
                        >
                          <Shield size={18} />
                          Privacy &amp; Data
                        </button>

                        <button
                          onClick={async () => {
                            setShowMobileMenu(false);
                            await logout();
                          }}
                          style={{
                            width: "100%",
                            padding: "0.875rem 1rem",
                            background: "transparent",
                            border: "none",
                            borderRadius: "8px",
                            color: "#e74c3c",
                            cursor: "pointer",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            textAlign: "left",
                          }}
                        >
                          <LogOut size={18} />
                          Sign Out
                        </button>
                      </>
                    )}

                    {/* Sign In Button - If Not Logged In */}
                    {!user && (
                      <button
                        onClick={() => {
                          setShowMobileMenu(false);
                          setShowAuthModal(true);
                        }}
                        style={{
                          width: "100%",
                          padding: "1rem",
                          background: "#667eea",
                          color: "#fff",
                          border: "none",
                          borderRadius: "12px",
                          cursor: "pointer",
                          fontWeight: "700",
                          marginTop: "0.5rem",
                        }}
                      >
                        Sign In
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Backdrop for Mobile Menu */}
              {showMobileMenu && (
                <div
                  onClick={() => setShowMobileMenu(false)}
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(0,0,0,0.5)",
                    zIndex: 999,
                  }}
                />
              )}

              {/* Hero Section */}
              <div
                style={{
                  maxWidth: "900px",
                  margin: "0 auto",
                  textAlign: "center",
                  marginBottom: "4rem",
                }}
              >
                <h2
                  style={{
                    fontSize: "3.5rem",
                    fontWeight: "800",
                    marginBottom: "1.5rem",
                    lineHeight: "1.2",
                    background: "linear-gradient(to right, #fff, #f0f0f0)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                  className="noryq-hero-h2"
                >
                  Find Your Perfect Watch
                  <br />
                  Based on Your Mood
                </h2>
                <p
                  style={{
                    fontSize: "1.25rem",
                    opacity: 0.9,
                    marginBottom: "2.5rem",
                    lineHeight: "1.6",
                  }}
                  className="noryq-hero-tagline"
                >
                  No more endless scrolling. Tell us how you feel, and we'll
                  recommend something you'll actually love.
                </p>

                {/* Free/Daily recommendations counter */}
                {!isPremium && (
                  <div
                    style={{
                      display: "block",
                      padding: "0.5rem 1rem",
                      background: user
                        ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                        : "rgba(255,255,255,0.2)",
                      borderRadius: "8px",
                      width: "fit-content",
                      margin: "0 auto 1.5rem auto",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      boxShadow: user
                        ? "0 4px 15px rgba(240, 147, 251, 0.3)"
                        : "none",
                    }}
                  >
                    {user ? (
                      // Logged-in free users see daily limit
                      <>
                        <Zap
                          size={14}
                          style={{
                            marginRight: "0.25rem",
                            verticalAlign: "middle",
                          }}
                        />{" "}
                        {RECOMMENDATION_LIMITS.FREE_PER_DAY -
                          dailyRecommendations}{" "}
                        searches left today
                      </>
                    ) : (
                      // Non-logged users see total limit
                      <>
                        <Sparkles
                          size={14}
                          style={{
                            marginRight: "0.25rem",
                            verticalAlign: "middle",
                          }}
                        />{" "}
                        {freeRecommendationsLeft} free searches left
                      </>
                    )}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem",
                    width: "100%",
                  }}
                >
                  <button
                    onClick={() => {
                      // ✅ CHECK EMAIL VERIFICATION FIRST
                      if (user && !user.emailVerified) {
                        showToast(
                          "Please verify your email before getting recommendations!",
                          "info",
                        );
                        window.scrollTo({ top: 0, behavior: "smooth" });
                        return;
                      }

                      // ✅ CHECK IF USER HAS LIKED AT LEAST ONE MOVIE
                      if (user && likedMovies.length === 0) {
                        showToast(
                          "Please add at least one movie you like to get personalized recommendations!",
                          "info",
                        );
                        setCurrentView("liked");
                        window.scrollTo({ top: 0, behavior: "smooth" });
                        return;
                      }

                      // ✅ CHECK DAILY LIMIT FOR FREE LOGGED-IN USERS
                      if (
                        user &&
                        !isPremium &&
                        dailyRecommendations >=
                          RECOMMENDATION_LIMITS.FREE_PER_DAY
                      ) {
                        setShowLimitModal(true);
                        return;
                      }

                      // Check if NON-LOGGED user has free recommendations left
                      if (!user && !isPremium && freeRecommendationsLeft <= 0) {
                        setShowLimitModal(true);
                        return;
                      }

                      setCurrentView("quiz");
                      setStep(1);
                      setUserPreferences({
                        mood: "",
                        contentType: "",
                        genres: [],
                        tone: "",
                        watchingWith: "",
                      });
                    }}
                    style={{
                      padding: "1.25rem 3rem",
                      fontSize: "1.125rem",
                      background: "#fff",
                      color: "#667eea",
                      border: "none",
                      borderRadius: "16px",
                      cursor: "pointer",
                      fontWeight: "700",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.75rem",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      width: "min(100%, 340px)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 15px 40px rgba(0,0,0,0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 10px 30px rgba(0,0,0,0.3)";
                    }}
                  >
                    Get Started
                    <ChevronRight size={24} />
                  </button>
                  {/* Skip Quiz Button - Premium Feature */}
                  {user && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "0.75rem",
                        width: "100%",
                      }}
                    >
                      {/* Divider */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                          width: "min(100%, 340px)",
                          opacity: 0.5,
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            height: "1px",
                            background: "rgba(255,255,255,0.4)",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.05em",
                          }}
                        >
                          OR
                        </span>
                        <div
                          style={{
                            flex: 1,
                            height: "1px",
                            background: "rgba(255,255,255,0.4)",
                          }}
                        />
                      </div>

                      <button
                        onClick={() => {
                          if (!isPremium) {
                            showToast(
                              "Skip Quiz is a Premium feature!",
                              "info",
                            );
                            setCurrentView("premium");
                            return;
                          }

                          if (
                            watchHistory.length < 3 ||
                            recommendationCount < 5
                          ) {
                            const needHistory = watchHistory.length < 3;
                            const needRecs = recommendationCount < 5;

                            let message = "";
                            if (needHistory && needRecs) {
                              message = `Skip Quiz needs: Watch ${3 - watchHistory.length} more items and complete ${5 - recommendationCount} more quizzes!`;
                            } else if (needHistory) {
                              message = `Skip Quiz needs: Watch ${3 - watchHistory.length} more items! Mark movies/shows as watched first.`;
                            } else {
                              message = `Complete ${5 - recommendationCount} more quizzes to unlock Skip Quiz!`;
                            }

                            showToast(message, "info");
                            return;
                          }

                          getSmartRecommendation();
                        }}
                        style={{
                          padding: "0.875rem 2rem",
                          fontSize: "0.95rem",
                          background: isPremium
                            ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                            : "rgba(255,255,255,0.07)",
                          color: isPremium ? "#fff" : "rgba(255,255,255,0.5)",
                          border: isPremium
                            ? "none"
                            : "1.5px solid rgba(255,255,255,0.15)",
                          borderRadius: "12px",
                          cursor: isPremium ? "pointer" : "not-allowed",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "0.6rem",
                          transition: "all 0.2s",
                          backdropFilter: "blur(10px)",
                          width: "min(100%, 340px)",
                          letterSpacing: "0.01em",
                        }}
                        onMouseEnter={(e) => {
                          if (!isPremium) return;
                          e.currentTarget.style.background =
                            "rgba(255,255,255,0.2)";
                          e.currentTarget.style.borderColor =
                            "rgba(255,255,255,0.5)";
                          e.currentTarget.style.transform = "translateY(-1px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            "rgba(255,255,255,0.12)";
                          e.currentTarget.style.borderColor =
                            "rgba(255,255,255,0.3)";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        {!isPremium ? (
                          <>
                            <Lock size={14} />
                            <span>Skip Quiz — Premium Only</span>
                          </>
                        ) : (
                          <>
                            <Zap size={15} fill="#fff" strokeWidth={0} />
                            <span>Skip Quiz, Get Smart Picks</span>
                            {isPremium &&
                              watchHistory.length >= 3 &&
                              recommendationCount >= 5 && (
                                <span
                                  style={{
                                    background:
                                      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                    borderRadius: "20px",
                                    padding: "0.15rem 0.6rem",
                                    fontSize: "0.7rem",
                                    fontWeight: "700",
                                    letterSpacing: "0.03em",
                                  }}
                                >
                                  AI
                                </span>
                              )}
                          </>
                        )}
                      </button>

                      {/* Info Text */}
                      <p
                        style={{
                          fontSize: "0.8rem",
                          opacity: isPremium ? 0.65 : 0.45,
                          margin: 0,
                          textAlign: "center",
                        }}
                      >
                        {isPremium
                          ? watchHistory.length >= 3 && recommendationCount >= 5
                            ? "✦ Picks based on your watch history & time of day"
                            : watchHistory.length < 3
                              ? `Watch ${3 - watchHistory.length} more items to unlock`
                              : `Complete ${5 - recommendationCount} more quizzes to unlock`
                          : "Upgrade to Premium to skip the quiz"}
                      </p>
                      {toast && (
                        <div
                          style={{
                            position: "fixed",
                            top: "2rem",
                            left: "50%",
                            transform: "translateX(-50%)",
                            background:
                              toast.type === "success"
                                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                : toast.type === "info"
                                  ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                                  : "linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)",
                            color: "#fff",
                            padding: "1rem 2rem",
                            borderRadius: "12px",
                            boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
                            zIndex: 10000,
                            fontSize: "1rem",
                            fontWeight: "600",
                            animation: "slideDown 0.3s ease-out",
                            maxWidth: "calc(100vw - 2rem)",
                            textAlign: "center",
                          }}
                        >
                          {toast.message}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "1.5rem",
                  maxWidth: "900px",
                  margin: "0 auto 4rem",
                }}
                className="noryq-stats-grid"
              >
                <div
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    backdropFilter: "blur(10px)",
                    padding: "1.5rem",
                    borderRadius: "16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "2rem",
                      fontWeight: "800",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {recommendationCount}
                  </div>
                  <div style={{ opacity: 0.9, fontSize: "0.875rem" }}>
                    Recommendations
                  </div>
                </div>
                <div
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    backdropFilter: "blur(10px)",
                    padding: "1.5rem",
                    borderRadius: "16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "2rem",
                      fontWeight: "800",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {watchHistory.length}
                  </div>
                  <div style={{ opacity: 0.9, fontSize: "0.875rem" }}>
                    Shows Watched
                  </div>
                </div>
                {user && (
                  <div
                    style={{
                      background: "rgba(255,255,255,0.15)",
                      backdropFilter: "blur(10px)",
                      padding: "1.5rem",
                      borderRadius: "16px",
                      textAlign: "center",
                      position: "relative",
                      cursor: isPremium ? "pointer" : "default",
                      transition: "all 0.2s",
                    }}
                    onClick={() => {
                      if (isPremium) {
                        setCurrentView("calendar");
                      } else {
                        setCurrentView("premium");
                      }
                    }}
                    onMouseEnter={() => {
                      if (isPremium) setShowStreakPreview(true);
                    }}
                    onMouseLeave={() => setShowStreakPreview(false)}
                  >
                    <div
                      style={{
                        fontSize: "2rem",
                        fontWeight: "800",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <Flame
                        size={28}
                        color="#f5576c"
                        style={{ marginRight: "0.25rem" }}
                      />{" "}
                      {todayStreak}
                    </div>
                    <div style={{ opacity: 0.9, fontSize: "0.875rem" }}>
                      {isPremium
                        ? "Day Streak - Click to view calendar"
                        : "Day Streak"}
                    </div>

                    {/* Hover Preview - Premium Only */}
                    {showStreakPreview && isPremium && (
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(100% + 1rem)",
                          left: "-2.5%",
                          transform: "translateY(-160%)",
                          background:
                            "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                          borderRadius: "16px",
                          padding: "1.5rem",
                          boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
                          zIndex: 1000,
                          minWidth: "300px",
                          maxWidth: "350px",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          style={{
                            fontSize: "0.875rem",
                            opacity: 0.9,
                            marginBottom: "1rem",
                            textAlign: "center",
                            fontWeight: "600",
                          }}
                        >
                          <Crown
                            size={14}
                            style={{
                              marginRight: "0.3rem",
                              verticalAlign: "middle",
                            }}
                          />{" "}
                          Quick Preview
                        </div>

                        {/* Mini Calendar Preview */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(7, 1fr)",
                            gap: "0.5rem",
                            marginBottom: "0.5rem",
                          }}
                        >
                          {["S", "M", "T", "W", "T", "F", "S"].map((day, i) => (
                            <div
                              key={i}
                              style={{
                                textAlign: "center",
                                fontSize: "0.75rem",
                                opacity: 0.7,
                                fontWeight: "700",
                              }}
                            >
                              {day}
                            </div>
                          ))}

                          {/* Show last 14 days */}
                          {Array.from({ length: 14 }).map((_, i) => {
                            const today = new Date();
                            const dayDate = new Date(today);
                            dayDate.setDate(today.getDate() - (13 - i));
                            const dayNumber = dayDate.getDate();
                            const hasStreak =
                              i >= 14 - Math.min(todayStreak, 14);

                            return (
                              <div
                                key={i}
                                style={{
                                  aspectRatio: "1",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "8px",
                                  fontSize: "0.75rem",
                                  fontWeight: "600",
                                  background: hasStreak
                                    ? "rgba(255,255,255,0.3)"
                                    : "rgba(255,255,255,0.1)",
                                  position: "relative",
                                  border:
                                    i === 13
                                      ? "2px solid rgba(255,255,255,0.8)"
                                      : "none",
                                }}
                              >
                                {dayNumber}
                                {hasStreak && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "2px",
                                      right: "2px",
                                    }}
                                  >
                                    <Flame size={8} color="#f5576c" />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <div
                          style={{
                            marginTop: "1rem",
                            textAlign: "center",
                            fontSize: "0.75rem",
                            opacity: 0.8,
                          }}
                        >
                          Click to view full calendar
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Trending Preview */}
              {trendingRecommendations.length > 0 && (
                <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "2rem",
                    }}
                  >
                    <h3
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        margin: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <Flame size={24} color="#f5576c" /> Trending Now
                    </h3>
                    <button
                      onClick={() => setCurrentView("trending")}
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        border: "none",
                        padding: "0.5rem 1rem",
                        borderRadius: "8px",
                        color: "#fff",
                        cursor: "pointer",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                      }}
                    >
                      View All →
                    </button>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(250px, 1fr))",
                      gap: "1.5rem",
                    }}
                    className="noryq-trending-grid"
                  >
                    {trendingRecommendations.slice(0, 4).map((rec, idx) => {
                      const pc = PLATFORM_COLORS[rec.platform] || {
                        bg: "rgba(255,255,255,0.08)",
                        border: "rgba(255,255,255,0.2)",
                        text: "#e5e7eb",
                      };
                      return (
                        <div
                          key={idx}
                          role="button"
                          tabIndex={0}
                          aria-label={`${rec.title}, open trending list`}
                          onClick={() => setCurrentView("trending")}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setCurrentView("trending");
                            }
                          }}
                          style={{
                            background: "rgba(255,255,255,0.12)",
                            backdropFilter: "blur(10px)",
                            borderRadius: "14px",
                            overflow: "hidden",
                            transition: "transform 0.2s, box-shadow 0.2s",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "stretch",
                            border: "1px solid rgba(255,255,255,0.1)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-4px)";
                            e.currentTarget.style.boxShadow =
                              "0 12px 32px rgba(0,0,0,0.35)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        >
                          {/* Poster strip */}
                          <div
                            style={{
                              width: "88px",
                              flexShrink: 0,
                              position: "relative",
                              overflow: "hidden",
                              minHeight: "120px",
                            }}
                          >
                            {/* Rank badge */}
                            <div
                              style={{
                                position: "absolute",
                                top: "7px",
                                left: "7px",
                                zIndex: 2,
                                background:
                                  "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                borderRadius: "5px",
                                padding: "2px 6px",
                                fontSize: "0.62rem",
                                fontWeight: "800",
                                color: "#fff",
                                letterSpacing: "0.02em",
                              }}
                            >
                              #{idx + 1}
                            </div>
                            {rec.posterUrl &&
                            !rec.posterUrl.includes("xxx.jpg") &&
                            !rec.posterUrl.includes("placeholder") ? (
                              <img
                                src={rec.posterUrl}
                                alt={rec.title}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  display: "block",
                                  position: "absolute",
                                  inset: 0,
                                }}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  e.currentTarget.nextSibling.style.display =
                                    "flex";
                                }}
                              />
                            ) : null}
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                background:
                                  rec.type === "Movie"
                                    ? "linear-gradient(135deg, #0f0c29, #302b63)"
                                    : "linear-gradient(135deg, #614385, #516395)",
                                display:
                                  rec.posterUrl &&
                                  !rec.posterUrl.includes("xxx.jpg") &&
                                  !rec.posterUrl.includes("placeholder")
                                    ? "none"
                                    : "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {rec.type === "Movie" || rec.type === "Film" ? (
                                <Film
                                  size={24}
                                  color="rgba(167,139,250,0.5)"
                                  strokeWidth={1.5}
                                />
                              ) : (
                                <Tv
                                  size={24}
                                  color="rgba(167,139,250,0.5)"
                                  strokeWidth={1.5}
                                />
                              )}
                            </div>
                          </div>

                          {/* Info */}
                          <div
                            style={{
                              padding: "0.9rem 1rem",
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                              minWidth: 0,
                            }}
                          >
                            {/* Top: title + live dot */}
                            <div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "space-between",
                                  gap: "0.4rem",
                                  marginBottom: "0.25rem",
                                }}
                              >
                                <h4
                                  style={{
                                    fontSize: "0.88rem",
                                    fontWeight: "700",
                                    margin: 0,
                                    lineHeight: 1.25,
                                    color: "#fff",
                                    overflow: "hidden",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {rec.title}
                                </h4>
                                <span
                                  style={{
                                    width: "7px",
                                    height: "7px",
                                    borderRadius: "50%",
                                    background: "#f5576c",
                                    flexShrink: 0,
                                    marginTop: "4px",
                                    animation: "noryqPulse 1.8s infinite",
                                  }}
                                />
                              </div>
                              <div
                                style={{
                                  fontSize: "0.68rem",
                                  color: "rgba(255,255,255,0.45)",
                                  marginBottom: "0.55rem",
                                }}
                              >
                                {rec.year} · {rec.type}
                              </div>
                            </div>

                            {/* Bottom: IMDb + platform */}
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.45rem",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                <span
                                  style={{
                                    background: "#F5C518",
                                    borderRadius: "3px",
                                    padding: "1px 5px",
                                    fontSize: "0.58rem",
                                    fontWeight: "900",
                                    color: "#000",
                                    letterSpacing: "0.02em",
                                  }}
                                >
                                  IMDb
                                </span>
                                <span
                                  style={{
                                    fontSize: "0.82rem",
                                    fontWeight: "700",
                                    color: "#F5C518",
                                  }}
                                >
                                  {rec.rating}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignSelf: "flex-start",
                                  alignItems: "center",
                                  gap: "0.35rem",
                                  background: pc.bg,
                                  border: `1px solid ${pc.border}`,
                                  padding: "0.28rem 0.65rem",
                                  borderRadius: "6px",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "0.7rem",
                                    fontWeight: "600",
                                    color: pc.text,
                                    lineHeight: 1.3,
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  {rec.platform}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Privacy & Data (export / delete / consent) */}
            {showPrivacyData && (
              <PrivacyDataModal onClose={() => setShowPrivacyData(false)} />
            )}

            {/* Auth Modal */}
            {showAuthModal && (
              <AuthModal
                onClose={() => setShowAuthModal(false)}
                onSuccess={(isNewUser) => {
                  setShowAuthModal(false);
                  loadUserData();
                  if (isNewUser) {
                    setTimeout(() => setShowPlanSelector(true), 500);
                  }
                }}
              />
            )}

            {/* Limit Modal */}
            {showLimitModal && (
              <div
                onClick={(e) => {
                  if (e.target === e.currentTarget) setShowLimitModal(false);
                }}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(8,6,18,0.88)",
                  backdropFilter: "blur(12px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10000,
                  padding: "1rem",
                  overflowY: "auto",
                }}
              >
                <div
                  className="noryq-modal-inner"
                  style={{
                    background: "rgba(18,14,36,0.95)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "28px",
                    maxWidth: "440px",
                    width: "100%",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow:
                      "0 40px 100px rgba(0,0,0,0.6), 0 0 0 1px rgba(102,126,234,0.15)",
                    margin: "auto",
                  }}
                >
                  {/* ── Purple glow blob ── */}
                  <div
                    style={{
                      position: "absolute",
                      top: "-80px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "340px",
                      height: "340px",
                      borderRadius: "50%",
                      background:
                        "radial-gradient(circle, rgba(102,126,234,0.22) 0%, transparent 70%)",
                      pointerEvents: "none",
                    }}
                  />

                  {/* ── Accent line top ── */}
                  <div
                    style={{
                      height: "2px",
                      background:
                        "linear-gradient(90deg, transparent, #667eea 30%, #f093fb 60%, transparent)",
                    }}
                  />

                  {/* ── Header ── */}
                  <div
                    style={{
                      padding: "2rem 1.75rem 1.25rem",
                      textAlign: "center",
                      position: "relative",
                    }}
                  >
                    {/* close */}
                    <button
                      onClick={() => setShowLimitModal(false)}
                      style={{
                        position: "absolute",
                        top: "1.25rem",
                        right: "1.25rem",
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "50%",
                        width: "34px",
                        height: "34px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        color: "rgba(255,255,255,0.5)",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          "rgba(255,255,255,0.12)";
                        e.currentTarget.style.color = "#fff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          "rgba(255,255,255,0.06)";
                        e.currentTarget.style.color = "rgba(255,255,255,0.5)";
                      }}
                    >
                      <X size={15} />
                    </button>

                    {/* icon */}
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "64px",
                        height: "64px",
                        borderRadius: "20px",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        boxShadow: "0 8px 32px rgba(102,126,234,0.45)",
                        marginBottom: "1.1rem",
                      }}
                    >
                      {user ? (
                        <Zap size={30} color="#fff" fill="#fff" />
                      ) : (
                        <Film size={30} color="#fff" />
                      )}
                    </div>

                    <h2
                      style={{
                        color: "#fff",
                        fontSize: "clamp(1.25rem, 5vw, 1.6rem)",
                        fontWeight: "800",
                        letterSpacing: "-0.02em",
                        lineHeight: 1.25,
                        margin: "0 0 0.5rem",
                      }}
                    >
                      {user && !isPremium
                        ? "Daily Limit Reached!"
                        : "You've Used Your Free Picks!"}
                    </h2>

                    <p
                      style={{
                        color: "rgba(255,255,255,0.45)",
                        fontSize: "0.85rem",
                        lineHeight: 1.6,
                        margin: 0,
                      }}
                    >
                      {user && !isPremium
                        ? "Upgrade to Premium for unlimited daily recommendations."
                        : `Sign in free and get ${RECOMMENDATION_LIMITS.FREE_PER_DAY} recommendations every day.`}
                    </p>
                  </div>

                  {/* ── Body ── */}
                  <div style={{ padding: "0 1.75rem 1.75rem" }}>
                    {/* Usage bar — only for logged-in free users */}
                    {user && !isPremium && (
                      <div
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          borderRadius: "14px",
                          padding: "1rem 1.1rem",
                          marginBottom: "1.1rem",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "0.55rem",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#a78bfa",
                              letterSpacing: "0.06em",
                              textTransform: "uppercase",
                            }}
                          >
                            Daily Usage
                          </span>
                          <span
                            style={{
                              fontSize: "0.78rem",
                              fontWeight: "700",
                              color: "rgba(255,255,255,0.7)",
                            }}
                          >
                            {RECOMMENDATION_LIMITS.FREE_PER_DAY} /{" "}
                            {RECOMMENDATION_LIMITS.FREE_PER_DAY} used
                          </span>
                        </div>
                        <div
                          style={{
                            background: "rgba(255,255,255,0.08)",
                            borderRadius: "999px",
                            height: "6px",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              borderRadius: "999px",
                              background:
                                "linear-gradient(90deg, #667eea, #f093fb)",
                            }}
                          />
                        </div>
                        <p
                          style={{
                            margin: "0.5rem 0 0",
                            fontSize: "0.72rem",
                            color: "rgba(255,255,255,0.28)",
                          }}
                        >
                          Resets at midnight
                        </p>
                      </div>
                    )}

                    {/* Benefits grid */}
                    <div
                      style={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.07)",
                        borderRadius: "16px",
                        padding: "1.1rem",
                        marginBottom: "1.25rem",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.7rem",
                          fontWeight: "700",
                          color: "#a78bfa",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          marginBottom: "0.9rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.4rem",
                        }}
                      >
                        <Zap size={12} color="#a78bfa" />
                        {user && !isPremium
                          ? "Premium Benefits"
                          : "Free Account Includes"}
                      </div>

                      {(user && !isPremium
                        ? [
                            "Unlimited daily recommendations",
                            "6 picks per session (vs 2 on free)",
                            "Skip Quiz with smart AI matching",
                            "Zero ads — ever",
                            "Priority support + early features",
                          ]
                        : [
                            `${RECOMMENDATION_LIMITS.FREE_PER_DAY} recommendations per day`,
                            "Saved watch history across devices",
                            "Daily streak tracking",
                            "Personal watchlist",
                            "Personalised suggestions over time",
                          ]
                      ).map((feat, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.65rem",
                            padding: "0.5rem 0",
                            borderTop:
                              i === 0
                                ? "none"
                                : "1px solid rgba(255,255,255,0.05)",
                          }}
                        >
                          <div
                            style={{
                              width: "20px",
                              height: "20px",
                              borderRadius: "6px",
                              flexShrink: 0,
                              background:
                                "linear-gradient(135deg, #667eea, #764ba2)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Check size={11} color="#fff" strokeWidth={3} />
                          </div>
                          <span
                            style={{
                              fontSize: "0.83rem",
                              color: "rgba(255,255,255,0.65)",
                              lineHeight: 1.4,
                            }}
                          >
                            {feat}
                          </span>
                        </div>
                      ))}
                    </div>
                    {/* ✅ Ad slot — only for non-premium users 
                  {!isPremium && !userIsPremium && (
                    <div
                      style={{
                        marginBottom: "1.25rem",
                        padding: "1rem 0 0.5rem",
                        borderTop: "1px solid rgba(255,255,255,0.07)",
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "0.75rem",
                          color: "rgba(255,255,255,0.3)",
                          marginBottom: "0.5rem",
                        }}
                      >
                        or watch an ad to continue free
                      </p>
                      <HilltopAd
                        scriptSrc="//untimely-hello.com/bbX/Vds.dKGjl-0fYJWbco/berm/9AuwZBUelXk/P/TYYN4oO/DLcZ1/NsTdM/t/NVj_gy4WNBzmU/1ONqwV"
                        width={300}
                        height={250}
                      />
                    </div>
                  )}*/}
                    {/* CTA buttons */}
                    {user && !isPremium ? (
                      <>
                        <button
                          onClick={() => {
                            setShowLimitModal(false);
                            setCurrentView("premium");
                          }}
                          style={{
                            width: "100%",
                            padding: "0.95rem",
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "#fff",
                            border: "none",
                            borderRadius: "14px",
                            cursor: "pointer",
                            fontWeight: "700",
                            fontSize: "0.95rem",
                            letterSpacing: "0.02em",
                            marginBottom: "0.65rem",
                            boxShadow: "0 8px 28px rgba(102,126,234,0.4)",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                            e.currentTarget.style.boxShadow =
                              "0 14px 40px rgba(102,126,234,0.55)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 28px rgba(102,126,234,0.4)";
                          }}
                        >
                          Upgrade to Premium ✦
                        </button>
                        <button
                          onClick={() => setShowLimitModal(false)}
                          style={{
                            width: "100%",
                            padding: "0.85rem",
                            background: "transparent",
                            color: "rgba(255,255,255,0.4)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: "14px",
                            cursor: "pointer",
                            fontWeight: "600",
                            fontSize: "0.85rem",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor =
                              "rgba(255,255,255,0.22)";
                            e.currentTarget.style.color =
                              "rgba(255,255,255,0.65)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor =
                              "rgba(255,255,255,0.1)";
                            e.currentTarget.style.color =
                              "rgba(255,255,255,0.4)";
                          }}
                        >
                          Come back tomorrow
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          setShowLimitModal(false);
                          setShowAuthModal(true);
                        }}
                        style={{
                          width: "100%",
                          padding: "0.95rem",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "14px",
                          cursor: "pointer",
                          fontWeight: "700",
                          fontSize: "0.95rem",
                          letterSpacing: "0.02em",
                          boxShadow: "0 8px 28px rgba(102,126,234,0.4)",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow =
                            "0 14px 40px rgba(102,126,234,0.55)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 8px 28px rgba(102,126,234,0.4)";
                        }}
                      >
                        Sign In / Sign Up — It's Free
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Plan Selector Modal - Shows after signup */}
            {showPlanSelector && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0,0,0,0.85)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                  padding: "1rem",
                  overflowY: "auto",
                }}
              >
                <div
                  className="noryq-modal-inner"
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "24px",
                    padding: "2.5rem",
                    maxWidth: "860px",
                    width: "100%",
                    position: "relative",
                    color: "#fff",
                    margin: "auto",
                    boxShadow: "0 25px 60px rgba(0,0,0,0.4)",
                  }}
                >
                  {/* Close Button */}
                  <button
                    onClick={() => setShowPlanSelector(false)}
                    style={{
                      position: "absolute",
                      top: "1rem",
                      right: "1rem",
                      background: "rgba(255,255,255,0.15)",
                      border: "none",
                      borderRadius: "50%",
                      width: "36px",
                      height: "36px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "#fff",
                      flexShrink: 0,
                    }}
                    aria-label="Close"
                  >
                    <X size={18} />
                  </button>

                  {/* Header */}
                  <div style={{ textAlign: "center", marginBottom: "2rem" }}>
                    <div
                      style={{
                        fontSize: "2.5rem",
                        marginBottom: "0.5rem",
                        lineHeight: 1,
                      }}
                    >
                      🎬
                    </div>
                    <h2
                      style={{
                        fontSize: "clamp(1.5rem, 4vw, 2.25rem)",
                        fontWeight: "800",
                        marginBottom: "0.5rem",
                        lineHeight: 1.2,
                      }}
                    >
                      Welcome to NORYQ!
                    </h2>
                    <p
                      style={{
                        fontSize: "clamp(0.95rem, 2.5vw, 1.1rem)",
                        opacity: 0.85,
                      }}
                    >
                      Choose your plan to get started
                    </p>
                  </div>

                  {/* Plans Grid */}
                  <div
                    className="noryq-plans-grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(240px, 1fr))",
                      gap: "1.25rem",
                    }}
                  >
                    {/* Free Plan */}
                    <div
                      style={{
                        background: "rgba(255,255,255,0.12)",
                        borderRadius: "16px",
                        padding: "1.75rem",
                        border: "1px solid rgba(255,255,255,0.2)",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "1.25rem",
                          fontWeight: "700",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Free
                      </h3>
                      <div
                        style={{
                          fontSize: "2rem",
                          fontWeight: "800",
                          marginBottom: "1.25rem",
                        }}
                      >
                        ₹0
                      </div>
                      <ul
                        style={{
                          listStyle: "none",
                          padding: 0,
                          margin: "0 0 1.5rem 0",
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.6rem",
                          flex: 1,
                        }}
                      >
                        {[
                          "5 searches per day",
                          "2 recommendations per search",
                          "Save watch history",
                          "Track your streak",
                          "Ads supported",
                        ].map((item) => (
                          <li
                            key={item}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              fontSize: "0.9rem",
                            }}
                          >
                            <Check
                              size={14}
                              style={{ flexShrink: 0, opacity: 0.8 }}
                            />
                            {item}
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => setShowPlanSelector(false)}
                        style={{
                          width: "100%",
                          padding: "0.875rem",
                          background: "rgba(255,255,255,0.25)",
                          border: "1px solid rgba(255,255,255,0.3)",
                          borderRadius: "12px",
                          color: "#fff",
                          cursor: "pointer",
                          fontWeight: "700",
                          fontSize: "0.95rem",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background =
                            "rgba(255,255,255,0.35)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background =
                            "rgba(255,255,255,0.25)")
                        }
                      >
                        Continue Free
                      </button>
                    </div>

                    {/* Premium Plan */}
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        borderRadius: "16px",
                        padding: "1.75rem",
                        border: "2px solid rgba(255,255,255,0.5)",
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {/* Recommended Badge */}
                      <div
                        style={{
                          position: "absolute",
                          top: "1rem",
                          right: "1rem",
                          background: "rgba(255,255,255,0.3)",
                          padding: "0.2rem 0.65rem",
                          borderRadius: "20px",
                          fontSize: "0.7rem",
                          fontWeight: "700",
                          letterSpacing: "0.05em",
                        }}
                      >
                        ✦ BEST VALUE
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          marginBottom: "0.25rem",
                        }}
                      >
                        <Crown size={18} />
                        <h3 style={{ fontSize: "1.25rem", fontWeight: "700" }}>
                          Premium
                        </h3>
                      </div>
                      <div
                        style={{
                          fontSize: "2rem",
                          fontWeight: "800",
                          marginBottom: "1.25rem",
                        }}
                      >
                        ₹{PLAN.priceInr}
                        <span
                          style={{
                            fontSize: "0.9rem",
                            fontWeight: "400",
                            opacity: 0.85,
                          }}
                        >
                          / {PLAN.durationDays} days
                        </span>
                      </div>

                      <ul
                        style={{
                          listStyle: "none",
                          padding: 0,
                          margin: "0 0 1.5rem 0",
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.6rem",
                          flex: 1,
                        }}
                      >
                        {[
                          "Everything in Free",
                          "Unlimited searches per day",
                          "6 recommendations per search",
                          "No ads",
                        ].map((item) => (
                          <li
                            key={item}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              fontSize: "0.9rem",
                            }}
                          >
                            <Check size={14} style={{ flexShrink: 0 }} />
                            {item}
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => {
                          setShowPlanSelector(false);
                          setCurrentView("premium");
                        }}
                        style={{
                          width: "100%",
                          padding: "0.875rem",
                          background: "#fff",
                          border: "none",
                          borderRadius: "12px",
                          color: "#f5576c",
                          cursor: "pointer",
                          fontWeight: "800",
                          fontSize: "0.95rem",
                          transition: "transform 0.15s, box-shadow 0.15s",
                          boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-1px)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(0,0,0,0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 15px rgba(0,0,0,0.2)";
                        }}
                      >
                        🚀 Choose Premium
                      </button>
                    </div>
                  </div>

                  {/* Footer Note */}
                  <p
                    style={{
                      textAlign: "center",
                      marginTop: "1.5rem",
                      fontSize: "0.8rem",
                      opacity: 0.6,
                    }}
                  >
                    No credit card required for Free plan · Premium is a one-time pass with no auto-renewal
                  </p>
                </div>
              </div>
            )}
            {/* ═══════════════════════════════════════════════════════════ */}
            {/* LIKED MOVIES WELCOME POPUP */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {showLikedWelcome && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0,0,0,0.8)",
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10000,
                  padding: "1rem",
                }}
                onClick={() => {
                  setShowLikedWelcome(false);
                  localStorage.setItem("noryq-liked-welcome-seen", "true");
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    borderRadius: "24px",
                    padding: "2.5rem",
                    maxWidth: "500px",
                    width: "100%",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                    position: "relative",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Close button */}
                  <button
                    onClick={() => {
                      setShowLikedWelcome(false);
                      localStorage.setItem("noryq-liked-welcome-seen", "true");
                    }}
                    style={{
                      position: "absolute",
                      top: "1rem",
                      right: "1rem",
                      background: "rgba(102,126,234,0.1)",
                      border: "none",
                      borderRadius: "50%",
                      width: "32px",
                      height: "32px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#667eea",
                    }}
                  >
                    <X size={20} />
                  </button>

                  {/* Icon */}
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      background:
                        "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 1.5rem",
                    }}
                  >
                    <Heart size={40} color="#fff" fill="#fff" />
                  </div>

                  {/* Title */}
                  <h3
                    style={{
                      fontSize: "1.75rem",
                      fontWeight: "800",
                      color: "#1e1b4b",
                      textAlign: "center",
                      marginBottom: "1rem",
                    }}
                  >
                    Get Better Recommendations!
                  </h3>

                  {/* Description */}
                  <p
                    style={{
                      fontSize: "1rem",
                      color: "#6b7280",
                      textAlign: "center",
                      lineHeight: "1.6",
                      marginBottom: "2rem",
                    }}
                  >
                    Like movies and shows you love so our AI can recommend
                    content perfectly tailored to your taste!
                  </p>

                  {/* Actions */}
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <button
                      onClick={() => {
                        setShowLikedWelcome(false);
                        setCurrentView("liked");
                        localStorage.setItem(
                          "noryq-liked-welcome-seen",
                          "true",
                        );
                      }}
                      style={{
                        flex: 1,
                        padding: "1rem",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "12px",
                        fontWeight: "700",
                        cursor: "pointer",
                        fontSize: "1rem",
                      }}
                    >
                      Add Liked Movies
                    </button>
                  </div>
                </div>
              </div>
            )}
            <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800&display=swap');
          @keyframes float {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(5deg); }
          }
            /* Responsive Navigation */
          @media (max-width: 1024px) {
            .desktop-nav {
              display: none !important;
            }

            .mobile-menu-btn {
              display: flex !important;
              align-items: center;
              justify-content: center;
            }
          }

          @media (min-width: 1025px) {
            .mobile-menu {
              display: none !important;
            }
          }
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          /* Ensure logo doesn't shrink */
          img[alt="NORYQ"] {
            flex-shrink: 0;
            object-fit: contain;
          }
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateX(50%) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(50%) translateY(0);
            }
          }

          /* Mobile-specific fixes */
          @media (max-width: 768px) {
            /* Logo size on mobile */
            img[alt="NORYQ"] { height: 38px !important; }

            /* Hero section max-width on mobile */
            .noryq-hero-section { padding: 0 0.5rem !important; }

            /* Reduce hero section bottom margin */
            .noryq-hero-wrap { margin-bottom: 2rem !important; }

            /* Make buttons wrap gracefully */
            .noryq-hero-btns { flex-direction: column !important; align-items: stretch !important; gap: 0.75rem !important; }
            .noryq-hero-btns > button, .noryq-hero-btns > div { width: 100% !important; justify-content: center !important; }

            /* Limit modal inner padding */
            .noryq-limit-modal { padding: 1.75rem !important; }

            /* Reduce section headings in quiz container */
            .noryq-quiz-container { padding: 1rem !important; }
          }

          @media (max-width: 480px) {
            img[alt="NORYQ"] { height: 32px !important; }
          }
        `}</style>
            {!user && <ProductDemo />}
            <Footer onNavigate={setCurrentView} />
          </div>
        </div>
      </>
    );
  }

  // QUIZ VIEW
  if (currentView === "quiz") {
    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100%",
          fontFamily: '"DM Sans", sans-serif',
          color: "#fff",
          paddingTop: "2rem",
          boxSizing: "border-box",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated Background */}
        <AnimatedBackground variant="waves" />

        {/* Content Layer */}
        <div style={{ position: "relative", zIndex: 1 }}>
          <div
            style={{ maxWidth: "800px", margin: "0 auto" }}
            className="noryq-quiz-container"
          >
            {/* Navigation buttons - ADD THIS ENTIRE BLOCK */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "2rem",
              }}
            >
              <button
                onClick={() => {
                  setCurrentView("home");
                  setStep(1);
                  setUserPreferences({
                    mood: "",
                    contentType: "",
                    genres: [],
                    tone: "",
                    watchingWith: "",
                  });
                }}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  padding: "0.75rem 1.5rem",
                  borderRadius: "12px",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: "600",
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.background = "rgba(255,255,255,0.3)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.background = "rgba(255,255,255,0.2)")
                }
              >
                Home
              </button>

              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    border: "none",
                    padding: "0.75rem 1.5rem",
                    borderRadius: "12px",
                    color: "#fff",
                    cursor: "pointer",
                    fontWeight: "600",
                    backdropFilter: "blur(10px)",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.background = "rgba(255,255,255,0.3)")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.background = "rgba(255,255,255,0.2)")
                  }
                >
                  ← Back
                </button>
              )}
            </div>
            {/* END OF NAVIGATION BUTTONS */}
            {/* Progress bar */}
            <div style={{ marginBottom: "3rem" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "1rem",
                  fontSize: "0.875rem",
                  opacity: 0.8,
                }}
              >
                <span>
                  Step {step} of {totalSteps}
                </span>
                <span>{Math.round((step / totalSteps) * 100)}%</span>
              </div>
              <div
                style={{
                  height: "8px",
                  background: "rgba(255,255,255,0.2)",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    background: "#fff",
                    width: `${(step / totalSteps) * 100}%`,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
            {/* Advanced Filters Modal */}
            <FiltersModal
              show={showFiltersModal}
              onClose={() => setShowFiltersModal(false)}
              filters={advancedFilters}
              setFilters={setAdvancedFilters}
              isPremium={isPremium}
              onApply={() => {
                // In quiz: just close modal, filters will be applied when quiz completes
                setShowFiltersModal(false);
                showToast(
                  "Filters saved! Complete the quiz to see results.",
                  "success",
                );
              }}
            />
            {/* Floating Filter Button (FAB) */}
            <button
              onClick={() => setShowFiltersModal(true)}
              style={{
                position: "fixed",
                bottom: "2rem",
                right: "2rem",
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)", //isPremium
                //</div>? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                //: "rgba(255,255,255,0.2)",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.75rem",
                boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                transition: "all 0.3s ease",
                zIndex: 1000,
                backdropFilter: "blur(10px)",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "scale(1.1)";
                e.target.style.boxShadow = "0 6px 30px rgba(0,0,0,0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "scale(1)";
                e.target.style.boxShadow = "0 4px 20px rgba(0,0,0,0.3)";
              }}
              title={
                isPremium ? "Advanced Filters" : "Premium Feature (Locked)"
              }
            >
              <Pencil size={20} />
              {/* Active Filters Badge */}
              {Object.values(advancedFilters).some((v) =>
                Array.isArray(v) ? v.length > 0 : v !== null,
              ) && (
                <span
                  style={{
                    position: "absolute",
                    top: "-5px",
                    right: "-5px",
                    background: "#ff4757",
                    color: "#fff",
                    borderRadius: "50%",
                    width: "24px",
                    height: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.75rem",
                    fontWeight: "700",
                    border: "2px solid #667eea",
                  }}
                >
                  {
                    Object.values(advancedFilters).filter((v) =>
                      Array.isArray(v) ? v.length > 0 : v !== null,
                    ).length
                  }
                </span>
              )}
              {/* Lock Icon for Free Users */}
              {/*{!isPremium && (
                <span
                  style={{
                    position: "absolute",
                    top: "-5px",
                    left: "-5px",
                    background: "rgba(0,0,0,0.6)",
                    borderRadius: "50%",
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.625rem",
                  }}
                >
                  <Lock size={12} />
                </span>
              )}*/}
            </button>

            {/* Step 1: Mood */}
            {step === 1 && (
              <div>
                <h2
                  style={{
                    fontSize: "2.5rem",
                    fontWeight: "800",
                    marginBottom: "1rem",
                  }}
                  className="noryq-quiz-h2"
                >
                  How are you feeling?
                </h2>
                <p
                  style={{
                    fontSize: "1.125rem",
                    opacity: 0.9,
                    marginBottom: "2.5rem",
                  }}
                >
                  Choose the mood that best describes you right now
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "1.5rem",
                  }}
                  className="noryq-mood-grid"
                >
                  {moods.map((mood) => (
                    <button
                      key={mood.id}
                      onClick={() => {
                        setUserPreferences({
                          ...userPreferences,
                          mood: mood.label,
                        });
                        setStep(2);
                      }}
                      style={{
                        padding: "2rem",
                        background:
                          userPreferences.mood === mood.label
                            ? "rgba(255,255,255,0.3)"
                            : "rgba(255,255,255,0.15)",
                        backdropFilter: "blur(10px)",
                        border:
                          userPreferences.mood === mood.label
                            ? "2px solid #fff"
                            : "2px solid transparent",
                        borderRadius: "16px",
                        color: "#fff",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        textAlign: "center",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.background = "rgba(255,255,255,0.25)")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.background =
                          userPreferences.mood === mood.label
                            ? "rgba(255,255,255,0.3)"
                            : "rgba(255,255,255,0.15)")
                      }
                    >
                      <mood.icon
                        size={40}
                        style={{ marginBottom: "1rem", color: mood.color }}
                      />
                      <div style={{ fontWeight: "600", fontSize: "1.125rem" }}>
                        {mood.label}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Content Type */}
            {step === 2 && (
              <div>
                <h2
                  style={{
                    fontSize: "2.5rem",
                    fontWeight: "800",
                    marginBottom: "1rem",
                  }}
                  className="noryq-quiz-h2"
                >
                  What do you want to watch?
                </h2>
                <p
                  style={{
                    fontSize: "1.125rem",
                    opacity: 0.9,
                    marginBottom: "2.5rem",
                  }}
                >
                  Choose your preferred content type
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "1.5rem",
                  }}
                  className="noryq-content-grid"
                >
                  {contentTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => {
                        setUserPreferences({
                          ...userPreferences,
                          contentType: type.label,
                        });
                        setStep(3);
                      }}
                      style={{
                        padding: "2.5rem",
                        background:
                          userPreferences.contentType === type.label
                            ? "rgba(255,255,255,0.3)"
                            : "rgba(255,255,255,0.15)",
                        backdropFilter: "blur(10px)",
                        border:
                          userPreferences.contentType === type.label
                            ? "2px solid #fff"
                            : "2px solid transparent",
                        borderRadius: "16px",
                        color: "#fff",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        textAlign: "center",
                      }}
                    >
                      <type.icon size={48} style={{ marginBottom: "1rem" }} />
                      <div style={{ fontWeight: "600", fontSize: "1.25rem" }}>
                        {type.label}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Tone & Pace */}
            {step === 3 && (
              <div>
                <h2
                  style={{
                    fontSize: "2.5rem",
                    fontWeight: "800",
                    marginBottom: "1rem",
                  }}
                  className="noryq-quiz-h2"
                >
                  What tone are you after?
                </h2>
                <p
                  style={{
                    fontSize: "1.125rem",
                    opacity: 0.9,
                    marginBottom: "2.5rem",
                  }}
                >
                  Choose the pace and feel you want
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "1.5rem",
                  }}
                  className="noryq-duration-grid"
                >
                  {tones.map((tone) => (
                    <button
                      key={tone.id}
                      onClick={() => {
                        setUserPreferences({
                          ...userPreferences,
                          tone: tone.label,
                        });
                        setStep(4);
                      }}
                      style={{
                        padding: "2rem",
                        background:
                          userPreferences.tone === tone.label
                            ? "rgba(255,255,255,0.3)"
                            : "rgba(255,255,255,0.15)",
                        backdropFilter: "blur(10px)",
                        border:
                          userPreferences.tone === tone.label
                            ? "2px solid #fff"
                            : "2px solid transparent",
                        borderRadius: "16px",
                        color: "#fff",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        textAlign: "center",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "rgba(255,255,255,0.25)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          userPreferences.tone === tone.label
                            ? "rgba(255,255,255,0.3)"
                            : "rgba(255,255,255,0.15)")
                      }
                    >
                      <tone.icon
                        size={40}
                        style={{ marginBottom: "0.75rem", color: tone.color }}
                      />
                      <div
                        style={{
                          fontWeight: "700",
                          fontSize: "1.125rem",
                          marginBottom: "0.4rem",
                        }}
                      >
                        {tone.label}
                      </div>
                      <div style={{ opacity: 0.75, fontSize: "0.875rem" }}>
                        {tone.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Watching With */}
            {step === 4 && (
              <div>
                <h2
                  style={{
                    fontSize: "2.5rem",
                    fontWeight: "800",
                    marginBottom: "1rem",
                  }}
                  className="noryq-quiz-h2"
                >
                  Who's watching with you?
                </h2>
                <p
                  style={{
                    fontSize: "1.125rem",
                    opacity: 0.9,
                    marginBottom: "2.5rem",
                  }}
                >
                  This helps us pick the perfect vibe for your crew
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "1rem",
                  }}
                  className="noryq-vibe-grid"
                >
                  {watchingWithOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => {
                        setUserPreferences({
                          ...userPreferences,
                          watchingWith: option.label,
                        });
                        setStep(5);
                      }}
                      style={{
                        padding: "2rem 1.5rem",
                        background:
                          userPreferences.watchingWith === option.label
                            ? "rgba(255,255,255,0.3)"
                            : "rgba(255,255,255,0.15)",
                        backdropFilter: "blur(10px)",
                        border:
                          userPreferences.watchingWith === option.label
                            ? "2px solid #fff"
                            : "2px solid transparent",
                        borderRadius: "16px",
                        color: "#fff",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        textAlign: "center",
                        fontWeight: "600",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "rgba(255,255,255,0.25)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          userPreferences.watchingWith === option.label
                            ? "rgba(255,255,255,0.3)"
                            : "rgba(255,255,255,0.15)")
                      }
                    >
                      <option.icon
                        size={40}
                        style={{ marginBottom: "0.75rem", color: option.color }}
                      />
                      <div
                        style={{
                          fontWeight: "700",
                          fontSize: "1.125rem",
                          marginBottom: "0.25rem",
                        }}
                      >
                        {option.label}
                      </div>
                      <div style={{ opacity: 0.75, fontSize: "0.875rem" }}>
                        {option.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 5: Genres */}
            {step === 5 && (
              <div>
                <h2
                  style={{
                    fontSize: "2.5rem",
                    fontWeight: "800",
                    marginBottom: "1rem",
                  }}
                  className="noryq-quiz-h2"
                >
                  Any specific genres?
                </h2>
                <p
                  style={{
                    fontSize: "1.125rem",
                    opacity: 0.9,
                    marginBottom: "2.5rem",
                  }}
                >
                  Select all that interest you (optional)
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: "1rem",
                    marginBottom: "3rem",
                  }}
                  className="noryq-genre-grid"
                >
                  {[
                    "Action",
                    "Comedy",
                    "Drama",
                    "Horror",
                    "Sci-Fi",
                    "Romance",
                    "Thriller",
                    "Documentary",
                    "Animation",
                    "Mystery",
                    "Fantasy",
                    "Crime",
                  ].map((genre) => (
                    <button
                      key={genre}
                      onClick={() => {
                        if (userPreferences.genres.includes(genre)) {
                          setUserPreferences({
                            ...userPreferences,
                            genres: userPreferences.genres.filter(
                              (g) => g !== genre,
                            ),
                          });
                        } else {
                          setUserPreferences({
                            ...userPreferences,
                            genres: [...userPreferences.genres, genre],
                          });
                        }
                      }}
                      style={{
                        padding: "1rem",
                        background: userPreferences.genres.includes(genre)
                          ? "rgba(255,255,255,0.3)"
                          : "rgba(255,255,255,0.15)",
                        backdropFilter: "blur(10px)",
                        border: userPreferences.genres.includes(genre)
                          ? "2px solid #fff"
                          : "2px solid transparent",
                        borderRadius: "12px",
                        color: "#fff",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        textAlign: "center",
                        fontWeight: "600",
                      }}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
                <button
                  onClick={getRecommendations}
                  style={{
                    padding: "1rem 1rem",
                    fontSize: "1.125rem",
                    background: "#fff",
                    color: "#667eea",
                    border: "none",
                    borderRadius: "16px",
                    cursor: "pointer",
                    fontWeight: "700",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    margin: "0 auto",
                  }}
                >
                  Click To Proceed
                  <img
                    src="/favicon.ico"
                    alt="NORYQ"
                    style={{ height: "32px" }}
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // AD VIEW
  if (showAd) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#1a1a2e",
          fontFamily: '"DM Sans", sans-serif',
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
      >
        <div
          style={{
            maxWidth: "600px",
            textAlign: "center",
            background: "rgba(255,255,255,0.05)",
            padding: "3rem",
            borderRadius: "24px",
            backdropFilter: "blur(10px)",
          }}
        >
          <div
            style={{
              width: "80px",
              height: "80px",
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 2rem",
            }}
          >
            <Play size={40} />
          </div>
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: "800",
              marginBottom: "1rem",
            }}
          >
            Advertisement
          </h2>
          <p
            style={{ fontSize: "1.125rem", opacity: 0.8, marginBottom: "2rem" }}
          >
            Free users see ads to support the platform. Upgrade to Premium for
            an ad-free experience!
          </p>
          <div
            style={{
              padding: "2rem",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "16px",
              marginBottom: "2rem",
            }}
          >
            {!isPremium && !userIsPremium && (
              <HilltopAd
                scriptSrc="//untimely-hello.com/b.XwVisndgGElV0tYbWEcY/cenmS9/u/ZiUulikTP/THY_4/O_DxcT1ANQjbkzt/NljbgG4IN/zyU/3/Mxwb"
                width={300}
                height={250}
              />
            )}
          </div>
          <p style={{ fontSize: "0.875rem", opacity: 0.6 }}>
            Your recommendations will load in a moment...
          </p>
        </div>
      </div>
    );
  }

  // RESULTS VIEW
  if (currentView === "results") {
    if (isLoading) {
      return (
        <div
          style={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "28px",
          }}
        >
          <svg
            width="130"
            height="130"
            viewBox="0 0 180 180"
            xmlns="http://www.w3.org/2000/svg"
          >
            <style>{`
          .dot { fill: #ffffff; transform-box: fill-box; transform-origin: center; animation: ripple 2s cubic-bezier(0.45,0,0.55,1) infinite; }
          @keyframes ripple { 0%{transform:scale(1);opacity:1;} 30%{transform:scale(0.42);opacity:0.15;} 65%{transform:scale(1);opacity:1;} 100%{transform:scale(1);opacity:1;} }
          .g0{animation-delay:0.00s} .g1{animation-delay:0.22s} .g2{animation-delay:0.44s}}
        `}</style>
            <circle className="dot g2" cx="87.4" cy="6.9" r="4.82" />
            <circle className="dot g2" cx="51.0" cy="16.9" r="5.81" />
            <circle className="dot g2" cx="126.4" cy="16.9" r="13.55" />
            <circle className="dot g2" cx="21.1" cy="44.7" r="7.80" />
            <circle className="dot g2" cx="154.1" cy="45.6" r="13.59" />
            <circle className="dot g2" cx="12.9" cy="87.2" r="9.32" />
            <circle className="dot g2" cx="163.4" cy="82.8" r="13.54" />
            <circle className="dot g2" cx="22.7" cy="121.9" r="13.56" />
            <circle className="dot g2" cx="154.1" cy="121.9" r="13.56" />
            <circle className="dot g2" cx="51.8" cy="152.8" r="13.55" />
            <circle className="dot g2" cx="126.4" cy="152.8" r="13.58" />
            <circle className="dot g2" cx="89.7" cy="163.6" r="13.63" />
            <circle className="dot g1" cx="88.1" cy="44.5" r="7.86" />
            <circle className="dot g1" cx="55.5" cy="62.9" r="9.32" />
            <circle className="dot g1" cx="121.8" cy="62.9" r="13.59" />
            <circle className="dot g1" cx="55.4" cy="104.0" r="13.47" />
            <circle className="dot g1" cx="121.8" cy="103.9" r="13.59" />
            <circle className="dot g1" cx="89.7" cy="125.3" r="13.59" />
            <circle className="dot g0" cx="89.7" cy="87.0" r="13.55" />
          </svg>
          {/* ADD THIS between </svg> and the text div */}
          <div
            style={{
              width: "72px",
              height: "1px",
              background: "rgba(255,255,255,0.2)",
              borderRadius: "1px",
            }}
          >
            <div
              style={{
                height: "100%",
                background: "#ffffff",
                borderRadius: "1px",
                width: `${loadingProgress}%`,
                transition: "width 0.3s ease-out",
              }}
            />
          </div>
          <div style={{ textAlign: "center" }}>
            <h2
              style={{ fontSize: "2rem", fontWeight: "700", color: "#ffffff" }}
            >
              Finding your perfect matches...
            </h2>
            <p style={{ opacity: 0.8, marginTop: "1rem", color: "#ffffff" }}>
              Our AI is analyzing thousands of titles
            </p>
          </div>
          {/* ✅ Ad — only for non-premium users 
          {!isPremium && !userIsPremium && (
            <div style={{ marginTop: "1.5rem", textAlign: "center" }}>
              <HilltopAd
                scriptSrc="//untimely-hello.com/bgXqV/sfd.GLlT0PY/WecF/ieXmg9euvZhU/lRk-PiThYL4MOfDzcw2fM-DBEvtGNmjNgZ4lNxz/Y/wmNmQb"
                width={300}
                height={250}
              />
            </div>
          )}*/}
        </div>
      );
    }

    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100%",
          fontFamily: '"DM Sans", sans-serif',
          color: "#fff",
          paddingTop: "2rem",
          boxSizing: "border-box",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated Background */}
        <AnimatedBackground variant="waves" />
        {/* Hidden share canvases */}
        {hiddenCanvases}

        {/* Content Layer */}
        <div style={{ position: "relative", zIndex: 1 }}>
          <div
            style={{ maxWidth: "1200px", margin: "0 auto" }}
            className="noryq-quiz-container"
          >
            <button
              onClick={() => setCurrentView("home")}
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                padding: "0.75rem 1.5rem",
                borderRadius: "12px",
                color: "#fff",
                cursor: "pointer",
                marginBottom: "2rem",
                fontWeight: "600",
              }}
            >
              ← Back to Home
            </button>
            {/* Refine Results Button - For ALL users */}
            <div style={{ textAlign: "center", marginBottom: "2rem" }}>
              <button
                onClick={() => setShowFiltersModal(true)}
                style={{
                  padding: "1rem 2.5rem",
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)", //isPremium
                  //</div>? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                  //: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "12px",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: "700",
                  fontSize: "1.125rem",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                  transition: "all 0.3s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(0,0,0,0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 15px rgba(0,0,0,0.2)";
                }}
              >
                {/*{!isPremium && <Lock size={16} />}*/}
                {/*{isPremium && <Pencil size={18} />}*/}
                <Pencil size={18} />
                <span>
                  {/*{isPremium
                    ? "Refine Results with Filters"
                    : "Unlock Advanced Filters"}*/}
                  Refine Results with Filters
                </span>
                {Object.values(advancedFilters).some((v) =>
                  Array.isArray(v) ? v.length > 0 : v !== null,
                ) &&
                  isPremium && (
                    <span
                      style={{
                        background: "rgba(255,255,255,0.3)",
                        borderRadius: "20px",
                        padding: "0.25rem 0.75rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      {
                        Object.values(advancedFilters).filter((v) =>
                          Array.isArray(v) ? v.length > 0 : v !== null,
                        ).length
                      }{" "}
                      Active
                    </span>
                  )}
              </button>
            </div>
            {/* Advanced Filters Modal - RESULTS VIEW */}
            <FiltersModal
              show={showFiltersModal}
              onClose={() => setShowFiltersModal(false)}
              filters={advancedFilters}
              setFilters={setAdvancedFilters}
              isPremium={isPremium}
              onApply={() => {
                setShowFiltersModal(false);
                proceedWithRecommendation(); // ← This is correct for results view!
              }}
            />
            {/* Active Filters Badge */}
            {isPremium &&
              Object.values(advancedFilters).some((v) =>
                Array.isArray(v) ? v.length > 0 : v !== null,
              ) && (
                <div
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "12px",
                    padding: "1rem 1.5rem",
                    marginBottom: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "700",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                  >
                    <Pencil size={16} /> Active Filters:
                  </span>
                  {advancedFilters.minRating && (
                    <span
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        padding: "0.375rem 0.75rem",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                      }}
                    >
                      <Star size={14} style={{ marginRight: "0.2rem" }} />{" "}
                      {advancedFilters.minRating}+
                    </span>
                  )}
                  {advancedFilters.yearRange && (
                    <span
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        padding: "0.375rem 0.75rem",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                      }}
                    >
                      <Calendar size={14} style={{ marginRight: "0.2rem" }} />{" "}
                      {advancedFilters.yearRange}
                    </span>
                  )}
                  {advancedFilters.runtime && (
                    <span
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        padding: "0.375rem 0.75rem",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                      }}
                    >
                      <Timer size={14} style={{ marginRight: "0.2rem" }} />{" "}
                      {advancedFilters.runtime}
                    </span>
                  )}
                  {advancedFilters.language && (
                    <span
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        padding: "0.375rem 0.75rem",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                      }}
                    >
                      <Activity size={14} style={{ marginRight: "0.2rem" }} />{" "}
                      {advancedFilters.language}
                    </span>
                  )}
                  {advancedFilters.platform && (
                    <span
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        padding: "0.375rem 0.75rem",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                      }}
                    >
                      <Monitor size={14} style={{ marginRight: "0.2rem" }} />{" "}
                      {advancedFilters.platform}
                    </span>
                  )}

                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log("Edit Filters clicked!"); // Debug
                      setShowFiltersModal(true);
                    }}
                    type="button"
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      border: "none",
                      color: "#fff",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      marginLeft: "auto",
                      padding: "0.5rem 1rem",
                      borderRadius: "8px",
                      fontWeight: "600",
                      transition: "all 0.2s",
                      zIndex: 10,
                      position: "relative",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "rgba(255,255,255,0.3)";
                      e.target.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "rgba(255,255,255,0.2)";
                      e.target.style.transform = "translateY(0)";
                    }}
                  >
                    Edit Filters
                  </button>
                </div>
              )}

            <h2
              style={{
                fontSize: "2.5rem",
                fontWeight: "800",
                marginBottom: "1rem",
                textAlign: "center",
              }}
              className="noryq-results-heading"
            >
              Your Perfect Matches
            </h2>
            {/* Skip Quiz Badge */}
            {isPremium && recommendationSource === "smart" && (
              <div
                style={{
                  textAlign: "center",
                  marginBottom: "2rem",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.5rem 1.5rem",
                    background:
                      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                    borderRadius: "20px",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                  }}
                >
                  <Zap size={14} />
                  <span>Smart Picks - AI Powered</span>
                </span>
              </div>
            )}
            {recommendationSource === "smart" ? (
              <p
                style={{
                  textAlign: "center",
                  fontSize: "1.125rem",
                  opacity: 0.9,
                  marginBottom: "3rem",
                }}
              >
                Smart picks based on your watch history and viewing patterns
              </p>
            ) : (
              <p
                style={{
                  textAlign: "center",
                  fontSize: "1.125rem",
                  opacity: 0.9,
                  marginBottom: "3rem",
                }}
              >
                Based on your mood: <strong>{userPreferences.mood}</strong>
              </p>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "2rem",
              }}
              className="noryq-results-grid"
            >
              {recommendations.map((rec, idx) => {
                const pc = PLATFORM_COLORS[rec.platform] || {
                  bg: "rgba(102,126,234,0.1)",
                  border: "rgba(102,126,234,0.22)",
                  text: "#a78bfa",
                };
                return (
                  <React.Fragment>
                    <div
                      key={idx}
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        backdropFilter: "blur(24px)",
                        borderRadius: "20px",
                        overflow: "hidden",
                        border: "1px solid rgba(255,255,255,0.1)",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
                        transition:
                          "transform 0.35s cubic-bezier(0.34,1.2,0.64,1), box-shadow 0.35s ease, border-color 0.35s ease",
                        cursor: "default",
                        display: "flex",
                        flexDirection: "column",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-8px)";
                        e.currentTarget.style.boxShadow =
                          "0 20px 48px rgba(0,0,0,0.45), 0 0 0 1px rgba(102,126,234,0.3)";
                        e.currentTarget.style.borderColor =
                          "rgba(102,126,234,0.35)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 24px rgba(0,0,0,0.3)";
                        e.currentTarget.style.borderColor =
                          "rgba(255,255,255,0.1)";
                      }}
                    >
                      {/* POSTER */}
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          height: "360px",
                          overflow: "hidden",
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            transition: "transform 0.5s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.06)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                        >
                          <PosterImage
                            posterUrl={rec.posterUrl}
                            title={rec.title}
                            type={rec.type}
                          />
                        </div>

                        {/* Gradient overlay */}
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            pointerEvents: "none",
                            background:
                              "linear-gradient(to top, rgba(10,8,20,1) 0%, rgba(10,8,20,0.82) 28%, rgba(10,8,20,0.15) 58%, transparent 100%)",
                          }}
                        />

                        {/* Brand accent line */}
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "2px",
                            background:
                              "linear-gradient(90deg, transparent, #667eea 40%, #764ba2 60%, transparent)",
                          }}
                        />

                        {/* Top badges */}
                        <div
                          style={{
                            position: "absolute",
                            top: "1rem",
                            left: "1rem",
                            right: "1rem",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.35rem",
                              background: "rgba(10,8,20,0.72)",
                              backdropFilter: "blur(16px)",
                              padding: "0.38rem 0.75rem",
                              borderRadius: "8px",
                              border: "1px solid rgba(255,255,255,0.11)",
                            }}
                          >
                            {rec.type === "Movie" || rec.type === "Film" ? (
                              <Film
                                size={11}
                                color="#a78bfa"
                                strokeWidth={2.5}
                              />
                            ) : (
                              <Tv size={11} color="#a78bfa" strokeWidth={2.5} />
                            )}
                            <span
                              style={{
                                fontSize: "0.67rem",
                                fontWeight: "700",
                                letterSpacing: "1.5px",
                                textTransform: "uppercase",
                                color: "rgba(255,255,255,0.82)",
                              }}
                            >
                              {rec.type}
                            </span>
                          </div>

                          {rec.matchScore && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.3rem",
                                background:
                                  "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                padding: "0.38rem 0.75rem",
                                borderRadius: "8px",
                                boxShadow: "0 4px 14px rgba(240,147,251,0.35)",
                              }}
                            >
                              <Zap size={11} fill="#fff" strokeWidth={0} />
                              <span
                                style={{
                                  fontSize: "0.72rem",
                                  fontWeight: "800",
                                  color: "#fff",
                                }}
                              >
                                {rec.matchScore}% Match
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Title over poster */}
                        <div
                          style={{
                            position: "absolute",
                            bottom: "1.1rem",
                            left: "1.1rem",
                            right: "1.1rem",
                          }}
                        >
                          <h3
                            style={{
                              margin: "0 0 0.35rem",
                              fontSize: "1.45rem",
                              fontWeight: "800",
                              color: "#fff",
                              lineHeight: "1.15",
                              letterSpacing: "-0.3px",
                              textShadow: "0 2px 10px rgba(0,0,0,0.9)",
                            }}
                          >
                            {rec.title}
                          </h3>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.4rem",
                              flexWrap: "wrap",
                              marginTop: "0.2rem",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.75rem",
                                color: "rgba(255,255,255,0.5)",
                                fontWeight: "500",
                              }}
                            >
                              {rec.year}
                            </span>
                            <span
                              style={{
                                width: "3px",
                                height: "3px",
                                borderRadius: "50%",
                                background: "rgba(255,255,255,0.28)",
                                flexShrink: 0,
                              }}
                            />
                            {rec.genre
                              ? rec.genre.split("/").map((g, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      display: "inline-block",
                                      background: "rgba(167,139,250,0.12)",
                                      border:
                                        "1px solid rgba(167,139,250,0.25)",
                                      borderRadius: "20px",
                                      padding: "1px 8px",
                                      fontSize: "0.68rem",
                                      color: "#c4b5fd",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {g.trim()}
                                  </span>
                                ))
                              : null}
                          </div>
                        </div>
                      </div>

                      {/* CONTENT BODY */}
                      <div
                        style={{
                          padding: "1.1rem 1.25rem 1.25rem",
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.875rem",
                          flex: 1,
                        }}
                      >
                        {/* IMDb + Platform */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              padding: "0.30rem",
                              borderRadius: "8px",
                              background: "rgba(255, 255, 255, 0.2)",
                              alignItems: "center",
                              gap: "0.45rem",
                            }}
                          >
                            <div
                              style={{
                                background: "#F5C518",
                                borderRadius: "4px",
                                padding: "0.12rem 0.38rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.18rem",
                              }}
                            >
                              <Star size={9} fill="#000" strokeWidth={0} />
                              <span
                                style={{
                                  fontSize: "0.62rem",
                                  fontWeight: "900",
                                  color: "#000",
                                  letterSpacing: "0.3px",
                                }}
                              >
                                IMDb
                              </span>
                            </div>
                            <span
                              style={{
                                fontSize: "0.88rem",
                                fontWeight: "700",
                                color: "#F5C518",
                              }}
                            >
                              {rec.imdbRating &&
                              typeof rec.imdbRating === "string" &&
                              rec.imdbRating.includes("/")
                                ? rec.imdbRating.split("/")[0]
                                : rec.imdbRating || rec.rating}
                            </span>
                            <span
                              style={{
                                fontSize: "0.68rem",
                                color: "rgba(249, 244, 244, 1)",
                              }}
                            >
                              /10
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.38rem",
                              background: pc.bg,
                              border: `1px solid ${pc.border}`,
                              padding: "0.32rem 0.7rem",
                              borderRadius: "7px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.72rem",
                                fontWeight: "600",
                                color: pc.text,
                              }}
                            >
                              {rec.platform}
                            </span>
                          </div>
                        </div>

                        {/* AI Why */}
                        <div
                          style={{
                            padding: "0.8rem 0.9rem",
                            background: "rgb(43 78 236 / 6%)",
                            border: "1px solid rgba(102,126,234,0.14)",
                            borderLeft: "2px solid #667eea",
                            borderRadius: "0 9px 9px 0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "0.45rem",
                            }}
                          >
                            <Sparkles
                              size={22}
                              color="#bba6faff"
                              strokeWidth={2}
                              style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <p
                              style={{
                                margin: 0,
                                fontSize: "0.875rem", // was 0.79rem
                                lineHeight: "1.65",
                                color: "rgba(255,255,255,0.88)", // was 0.6 — much more readable
                                fontStyle: "normal", // remove italic
                                fontWeight: "500",
                              }}
                            >
                              {rec.why}
                            </p>
                          </div>
                        </div>
                        {/* Buttons */}
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.55rem",
                            marginTop: "auto",
                          }}
                        >
                          {/* Like + Save — side by side, equal width */}
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (checkIfLiked(rec.title)) {
                                  handleUnlikeMovie(rec.title);
                                } else {
                                  handleLikeMovie(rec);
                                }
                              }}
                              style={{
                                flex: 1,
                                padding: "0.7rem",
                                background: checkIfLiked(rec.title)
                                  ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                                  : "rgba(255,255,255,0.07)",
                                border: checkIfLiked(rec.title)
                                  ? "1px solid #f5576c"
                                  : "1px solid rgba(255,255,255,0.15)",
                                borderRadius: "10px",
                                color: "#fff",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "0.4rem",
                                fontWeight: "600",
                                fontSize: "0.82rem",
                                transition: "all 0.2s",
                              }}
                            >
                              <Heart
                                size={14}
                                fill={checkIfLiked(rec.title) ? "#fff" : "none"}
                                color="#fff"
                              />
                              {checkIfLiked(rec.title) ? "Liked" : "Like"}
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                addToMyList(rec);
                              }}
                              style={{
                                flex: 1,
                                padding: "0.7rem",
                                background: "rgba(255,255,255,0.07)",
                                border: "1px solid rgba(255,255,255,0.15)",
                                borderRadius: "10px",
                                color: "rgba(255,255,255,0.75)",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "0.4rem",
                                fontWeight: "600",
                                fontSize: "0.82rem",
                                transition: "all 0.2s",
                              }}
                            >
                              <PlusCircle
                                size={14}
                                color="rgba(255,255,255,0.75)"
                              />
                              My List
                            </button>
                          </div>

                          {/* Mark Watched — full width primary */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              addToWatchHistory(rec);
                            }}
                            style={{
                              width: "100%",
                              padding: "0.78rem",
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              border: "none",
                              borderRadius: "10px",
                              color: "#fff",
                              fontWeight: "700",
                              fontSize: "0.83rem",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "0.45rem",
                              boxShadow: "0 4px 14px rgba(102,126,234,0.35)",
                              transition: "all 0.22s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.filter = "brightness(1.12)";
                              e.currentTarget.style.transform =
                                "translateY(-1px)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.filter = "brightness(1)";
                              e.currentTarget.style.transform = "translateY(0)";
                            }}
                          >
                            <Check size={14} strokeWidth={2.5} />
                            Mark as Watched
                          </button>
                          {/* Share row */}
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            {/* WhatsApp icon button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                shareToWhatsApp(rec);
                              }}
                              style={{
                                width: "40px",
                                height: "40px",
                                flexShrink: 0,
                                borderRadius: "10px",
                                cursor: "pointer",
                                background: "rgba(37,211,102,0.1)",
                                border: "1px solid rgba(37,211,102,0.3)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background =
                                  "rgba(37,211,102,0.2)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background =
                                  "rgba(37,211,102,0.1)";
                              }}
                              title="Share on WhatsApp"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="#4ade80"
                              >
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                              </svg>
                            </button>

                            {/* Instagram icon button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                shareToInstagram(rec);
                              }}
                              style={{
                                width: "40px",
                                height: "40px",
                                flexShrink: 0,
                                borderRadius: "10px",
                                cursor: "pointer",
                                background: "rgba(225,48,108,0.1)",
                                border: "1px solid rgba(225,48,108,0.3)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background =
                                  "rgba(225,48,108,0.2)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background =
                                  "rgba(225,48,108,0.1)";
                              }}
                              title="Share on Instagram"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="#f472b6"
                              >
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                              </svg>
                            </button>

                            {/* Share — full width */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                shareRecommendation(rec);
                              }}
                              style={{
                                flex: 1,
                                height: "40px",
                                background: "rgba(255,255,255,0.07)",
                                border: "1px solid rgba(255,255,255,0.15)",
                                borderRadius: "10px",
                                color: "rgba(255,255,255,0.75)",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "0.4rem",
                                fontWeight: "600",
                                fontSize: "0.82rem",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background =
                                  "rgba(255,255,255,0.12)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background =
                                  "rgba(255,255,255,0.07)";
                              }}
                            >
                              <Share2 size={13} strokeWidth={2} />
                              Share
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            <div
              style={{
                textAlign: "center",
                marginTop: "3rem",
              }}
            >
              <button
                onClick={() => {
                  // ✅ CHECK IF USER HAS LIKED AT LEAST ONE MOVIE
                  if (user && likedMovies.length === 0) {
                    showToast(
                      "Please add at least one movie you like to get personalized recommendations!",
                      "info",
                    );
                    setCurrentView("liked");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    return;
                  }

                  if (!isPremium) {
                    setCurrentView("premium");
                  } else {
                    setCurrentView("quiz");
                    setStep(1);
                    setUserPreferences({
                      mood: "",
                      contentType: "",
                      genres: [],
                      tone: "",
                      watchingWith: "",
                    });
                  }
                }}
                style={{
                  padding: "1.25rem 3rem",
                  fontSize: "1.125rem",
                  background: "#fff",
                  color: "#667eea",
                  border: "none",
                  borderRadius: "16px",
                  cursor: "pointer",
                  fontWeight: "700",
                }}
              >
                {!isPremium ? "Upgrade for More" : "Get More Recommendations"}
              </button>
            </div>
          </div>
          {/* Toast Notification - ADD THIS ENTIRE BLOCK */}
          {toast && (
            <div
              style={{
                position: "fixed",
                top: "2rem",
                left: "50%",
                transform: "translateX(-50%)",
                background:
                  toast.type === "success"
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : toast.type === "info"
                      ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                      : "linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)",
                color: "#fff",
                padding: "1rem 2rem",
                borderRadius: "12px",
                boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
                zIndex: 10000,
                fontSize: "1rem",
                fontWeight: "600",
                animation: "slideDown 0.3s ease-out",
                maxWidth: "calc(100vw - 2rem)",
                textAlign: "center",
              }}
            >
              {toast.message}
            </div>
          )}
          {/* Auth Modal */}
          {showAuthModal && (
            <AuthModal
              onClose={() => setShowAuthModal(false)}
              onSuccess={(isNewUser) => {
                setShowAuthModal(false);
                loadUserData();
                if (isNewUser) {
                  setTimeout(() => setShowPlanSelector(true), 500);
                }
              }}
            />
          )}
          <Footer onNavigate={setCurrentView} />
          <FeedbackModal
            isOpen={showFeedback}
            onClose={() => setShowFeedback(false)}
            user={user}
            isPremium={isPremium || userIsPremium}
            db={firestoreDb}
            sessionIndex={feedbackSessionCount}
          />
        </div>
      </div>
    );
  }

  // ─── Library pages: History / My List / Liked ───
  const goHome = () => setCurrentView("home");
  const libraryAuthModal = showAuthModal ? (
    <AuthModal
      onClose={() => setShowAuthModal(false)}
      onSuccess={() => {
        setShowAuthModal(false);
        loadUserData();
      }}
    />
  ) : null;

  // Like / unlike from History or My List. Liking also drops the title from
  // My List, but only once the like has actually gone through.
  const toggleLikeFromLibrary = async (item) => {
    if (checkIfLiked(item.title)) {
      await handleUnlikeMovie(item.title);
      return;
    }
    const liked = await handleLikeMovie(item);
    if (liked) await removeFromMyList(item);
  };

  // Mark watched: only remove from My List if it really reached history.
  const markWatchedFromList = async (item) => {
    const inHistory = await addToWatchHistory(item, true);
    if (inHistory) await removeFromMyList(item);
  };

  // HISTORY VIEW
  if (currentView === "history") {
    return (
      <HistoryView
        items={watchHistory}
        user={user}
        isLiked={checkIfLiked}
        onToggleLike={toggleLikeFromLibrary}
        onRemove={removeFromWatchHistory}
        onBack={goHome}
        onSignIn={() => setShowAuthModal(true)}
        toast={toast}
        overlay={libraryAuthModal}
      />
    );
  }

  // PREMIUM VIEW
  if (currentView === "premium") {
    if (!user) {
      return <AuthModal isOpen={true} onClose={() => setCurrentView("home")} />;
    }
    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100%",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          fontFamily: '"DM Sans", sans-serif',
          color: "#fff",
          paddingTop: "2rem",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{ maxWidth: "1000px", margin: "0 auto", padding: "0 1rem" }}
          className="noryq-page-pad"
        >
          <button
            onClick={() => setCurrentView("home")}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "12px",
              color: "#fff",
              cursor: "pointer",
              marginBottom: "2rem",
              fontWeight: "600",
            }}
          >
            ← Back to Home
          </button>

          <div
            style={{
              textAlign: "center",
              marginBottom: "4rem",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                padding: "0.75rem 1.5rem",
                borderRadius: "12px",
                marginBottom: "1.5rem",
              }}
            >
              <Crown size={24} />
              <span style={{ fontWeight: "700", fontSize: "1.125rem" }}>
                Premium
              </span>
            </div>
            <h2
              style={{
                fontSize: "clamp(1.65rem, 5vw, 3rem)",
                fontWeight: "800",
                marginBottom: "1rem",
              }}
            >
              Upgrade Your Experience
            </h2>
            <p
              style={{
                fontSize: "1.25rem",
                opacity: 0.9,
              }}
            >
              Get unlimited recommendations and exclusive features
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "2rem",
              marginBottom: "3rem",
            }}
            className="noryq-plans-grid"
          >
            {/* Free Plan */}
            <div
              style={{
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                padding: "2rem",
              }}
            >
              <h3
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  marginBottom: "1rem",
                }}
              >
                Free
              </h3>
              <div
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "800",
                  marginBottom: "2rem",
                }}
              >
                ₹0
              </div>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                }}
              >
                {[
                  "2 recommendations per search",
                  "5 searches per day",
                  "Ads between searches",
                  "Basic watch history",
                  "Standard support",
                ].map((feature, idx) => (
                  <li
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      marginBottom: "1rem",
                      fontSize: "1rem",
                    }}
                  >
                    <Check size={20} style={{ flexShrink: 0 }} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Premium Plan */}
            <div
              style={{
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                borderRadius: "16px",
                padding: "2rem",
                position: "relative",
                boxShadow: "0 20px 60px rgba(240, 147, 251, 0.4)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-12px",
                  right: "20px",
                  background: "#FFD93D",
                  color: "#1a1a2e",
                  padding: "0.5rem 1rem",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: "700",
                }}
              >
                {PREMIUM_LIVE ? "30-day pass" : "Coming Soon!"}
                {/*Best Value*/}
              </div>
              <h3
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  marginBottom: "1rem",
                }}
              >
                Premium
              </h3>
              <div
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "800",
                  marginBottom: "2rem",
                }}
              >
                ₹{PLAN.priceInr}
                <span style={{ fontSize: "1rem", fontWeight: "400" }}>
                  / {PLAN.durationDays} days
                </span>
              </div>
              <p
                style={{
                  margin: "-1.4rem 0 1.5rem",
                  fontSize: "0.85rem",
                  opacity: 0.95,
                }}
              >
                One-time payment. Does not renew automatically.
              </p>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  marginBottom: "2rem",
                }}
              >
                {[
                  "6 recommendations per search",
                  "Unlimited searches per day",
                  "No ads",
                ].map((feature, idx) => (
                  <li
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      marginBottom: "1rem",
                      fontSize: "1rem",
                    }}
                  >
                    <Check size={20} style={{ flexShrink: 0 }} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => {
                  if (!PREMIUM_LIVE) {
                    setShowWaitlistModal(true);
                  } else if (user) {
                    setShowCheckout(true);
                  } else {
                    // The sign-in modal lives on the home view.
                    setCurrentView("home");
                    setShowAuthModal(true);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "1.25rem",
                  background: "#fff",
                  color: "#f5576c",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: "700",
                  fontSize: "1.125rem",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                }}
              >
                {PREMIUM_LIVE ? `Buy for ₹${PLAN.priceInr}` : "Join Waitlist"}
              </button>
            </div>
          </div>

          <div
            style={{
              textAlign: "center",
              opacity: 0.8,
              fontSize: "0.875rem",
            }}
          >
            <p>
              {/*Note: This is a demo. In production, this would integrate with
              Razorpay payment gateway.*/}
            </p>
          </div>
        </div>

        {/* Waitlist Modal */}
        <WaitlistModal />

        {PREMIUM_LIVE && showCheckout && (
          <RazorpayCheckout
            onCancel={() => setShowCheckout(false)}
            onSuccess={() => {
              setShowCheckout(false);
              setCurrentView("home");
            }}
          />
        )}

        <Footer onNavigate={setCurrentView} />
        {/* Toast Notification - ADD THIS ENTIRE BLOCK */}
        {toast && (
          <div
            style={{
              position: "fixed",
              top: "2rem",
              right: "50%",
              transform: "translateX(50%)",
              background:
                toast.type === "success"
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : toast.type === "info"
                    ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                    : "linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)",
              color: "#fff",
              padding: "1rem 2rem",
              borderRadius: "12px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
              zIndex: 10000,
              fontSize: "1rem",
              fontWeight: "600",
              animation: "slideDown 0.3s ease-out",
              maxWidth: "90%",
              textAlign: "center",
            }}
          >
            {toast.message}
          </div>
        )}
      </div>
    );
  }
  // CALENDAR VIEW - Premium Only
  if (currentView === "calendar") {
    // Redirect non-premium users to premium page
    if (!isPremium) {
      setCurrentView("premium");
      return null;
    }

    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#fff",
          padding: "2rem",
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        <div style={{ maxWidth: "600px", margin: "0 auto" }}>
          {/* Back Button */}
          <button
            onClick={() => setCurrentView("home")}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "12px",
              color: "#fff",
              cursor: "pointer",
              marginBottom: "2rem",
              fontWeight: "600",
              backdropFilter: "blur(10px)",
            }}
          >
            ← Back to Home
          </button>

          {/* Page Title */}
          <h2
            style={{
              fontSize: "2.5rem",
              fontWeight: "800",
              marginBottom: "2rem",
              textAlign: "center",
            }}
          >
            Your Streak Calendar
          </h2>

          {/* Full Streak Calendar */}
          <StreakCalendar
            streak={todayStreak}
            isPremium={isPremium}
            streakHistory={streakHistory}
          />

          {/* Stats Section */}
          <div
            style={{
              marginTop: "2rem",
              background: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              padding: "2rem",
            }}
          >
            <h3
              style={{
                fontSize: "1.5rem",
                fontWeight: "700",
                marginBottom: "1.5rem",
              }}
            >
              <TrendingUp size={28} style={{ marginRight: "0.5rem" }} /> Your
              Stats
            </h3>

            <div style={{ display: "grid", gap: "1rem" }}>
              {/* Current Streak */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "1rem",
                  background: "rgba(255,255,255,0.1)",
                  borderRadius: "12px",
                }}
              >
                <span style={{ opacity: 0.9 }}>Current Streak:</span>
                <span style={{ fontWeight: "700", fontSize: "1.1rem" }}>
                  <Flame
                    size={20}
                    style={{ marginRight: "0.25rem", verticalAlign: "middle" }}
                    color="#f5576c"
                  />{" "}
                  {todayStreak} days
                </span>
              </div>

              {/* Total Recommendations */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "1rem",
                  background: "rgba(255,255,255,0.1)",
                  borderRadius: "12px",
                }}
              >
                <span style={{ opacity: 0.9 }}>Total Recommendations:</span>
                <span style={{ fontWeight: "700", fontSize: "1.1rem" }}>
                  {recommendationCount}
                </span>
              </div>

              {/* Movies in My List */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "1rem",
                  background: "rgba(255,255,255,0.1)",
                  borderRadius: "12px",
                }}
              >
                <span style={{ opacity: 0.9 }}>Movies in My List:</span>
                <span style={{ fontWeight: "700", fontSize: "1.1rem" }}>
                  {myList.length}
                </span>
              </div>

              {/* Watch History */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "1rem",
                  background: "rgba(255,255,255,0.1)",
                  borderRadius: "12px",
                }}
              >
                <span style={{ opacity: 0.9 }}>Total Watched:</span>
                <span style={{ fontWeight: "700", fontSize: "1.1rem" }}>
                  {watchHistory.length}
                </span>
              </div>
            </div>
          </div>

          {/* Motivational Message */}
          <div
            style={{
              marginTop: "2rem",
              textAlign: "center",
              padding: "2rem",
              background: "rgba(255,255,255,0.1)",
              borderRadius: "16px",
              backdropFilter: "blur(10px)",
            }}
          >
            <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>
              {todayStreak >= 30 ? (
                <Trophy size={32} />
              ) : todayStreak >= 14 ? (
                <Award size={32} />
              ) : todayStreak >= 7 ? (
                <Pencil size={32} />
              ) : (
                <Award size={32} />
              )}
            </div>
            <p
              style={{
                fontSize: "1.1rem",
                lineHeight: "1.6",
                opacity: 0.9,
              }}
            >
              {todayStreak >= 30
                ? "Incredible! You've maintained a 30+ day streak! You're a NORYQ legend!"
                : todayStreak >= 14
                  ? "Amazing! Two weeks strong! Keep the momentum going!"
                  : todayStreak >= 7
                    ? "Great job! You've kept your streak for over a week!"
                    : todayStreak >= 3
                      ? "Awesome! You're building a great habit! Keep it up!"
                      : "You're on your way! Come back tomorrow to keep your streak alive!"}
            </p>
          </div>

          {/* Tips Section */}
          <div
            style={{
              marginTop: "2rem",
              padding: "1.5rem",
              background: "rgba(255,255,255,0.1)",
              borderRadius: "16px",
              backdropFilter: "blur(10px)",
            }}
          >
            <h4
              style={{
                fontSize: "1.2rem",
                fontWeight: "700",
                marginBottom: "1rem",
              }}
            >
              <Sparkles size={22} style={{ marginRight: "0.5rem" }} /> Streak
              Tips
            </h4>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                opacity: 0.9,
              }}
            >
              <li style={{ marginBottom: "0.5rem" }}>
                Get at least one recommendation daily to maintain your streak
              </li>
              <li style={{ marginBottom: "0.5rem" }}>
                Your streak updates when you successfully get recommendations
              </li>
              <li style={{ marginBottom: "0.5rem" }}>
                Missing a day will reset your streak to 0
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // TRENDING VIEW
  if (currentView === "trending") {
    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100%",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          fontFamily: '"DM Sans", sans-serif',
          color: "#fff",
          padding: "2rem",
          boxSizing: "border-box",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <button
            onClick={() => setCurrentView("home")}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "12px",
              color: "#fff",
              cursor: "pointer",
              marginBottom: "2rem",
              fontWeight: "600",
            }}
          >
            ← Back to Home
          </button>

          <h2
            style={{
              fontSize: "2.5rem",
              fontWeight: "800",
              marginBottom: "1rem",
              textAlign: "center",
            }}
          >
            <Flame
              size={28}
              color="#f5576c"
              style={{ marginRight: "0.5rem" }}
            />{" "}
            Trending Now
          </h2>
          <p
            style={{
              textAlign: "center",
              fontSize: "1.125rem",
              opacity: 0.9,
              marginBottom: "3rem",
            }}
          >
            What everyone's watching this week
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "2rem",
            }}
            className="noryq-results-grid"
          >
            {trendingRecommendations.map((rec, idx) => (
              <div
                key={idx}
                style={{
                  background: "rgba(255,255,255,0.04)",
                  backdropFilter: "blur(24px)",
                  borderRadius: "20px",
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.1)",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
                  transition:
                    "transform 0.35s cubic-bezier(0.34,1.2,0.64,1), box-shadow 0.35s ease, border-color 0.35s ease",
                  cursor: "default",
                  display: "flex",
                  flexDirection: "column",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.boxShadow =
                    "0 20px 48px rgba(0,0,0,0.45), 0 0 0 1px rgba(102,126,234,0.3)";
                  e.currentTarget.style.borderColor = "rgba(102,126,234,0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 24px rgba(0,0,0,0.3)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                }}
              >
                {/* POSTER */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "360px",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      transition: "transform 0.5s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.06)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <PosterImage
                      posterUrl={rec.posterUrl}
                      title={rec.title}
                      type={rec.type}
                    />
                  </div>

                  {/* Gradient overlay */}
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      pointerEvents: "none",
                      background:
                        "linear-gradient(to top, rgba(10,8,20,1) 0%, rgba(10,8,20,0.82) 28%, rgba(10,8,20,0.15) 58%, transparent 100%)",
                    }}
                  />

                  {/* Brand accent line */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "2px",
                      background:
                        "linear-gradient(90deg, transparent, #667eea 40%, #764ba2 60%, transparent)",
                    }}
                  />

                  {/* Top badges */}
                  <div
                    style={{
                      position: "absolute",
                      top: "1rem",
                      left: "1rem",
                      right: "1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.35rem",
                        background: "rgba(10,8,20,0.72)",
                        backdropFilter: "blur(16px)",
                        padding: "0.38rem 0.75rem",
                        borderRadius: "8px",
                        border: "1px solid rgba(255,255,255,0.11)",
                      }}
                    >
                      {rec.type === "Movie" || rec.type === "Film" ? (
                        <Film size={11} color="#a78bfa" strokeWidth={2.5} />
                      ) : (
                        <Tv size={11} color="#a78bfa" strokeWidth={2.5} />
                      )}
                      <span
                        style={{
                          fontSize: "0.67rem",
                          fontWeight: "700",
                          letterSpacing: "1.5px",
                          textTransform: "uppercase",
                          color: "rgba(255,255,255,0.82)",
                        }}
                      >
                        {rec.type}
                      </span>
                    </div>
                  </div>

                  {/* Title over poster */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: "1.1rem",
                      left: "1.1rem",
                      right: "1.1rem",
                    }}
                  >
                    <h3
                      style={{
                        margin: "0 0 0.35rem",
                        fontSize: "1.45rem",
                        fontWeight: "800",
                        color: "#fff",
                        lineHeight: "1.15",
                        letterSpacing: "-0.3px",
                        textShadow: "0 2px 10px rgba(0,0,0,0.9)",
                      }}
                    >
                      {rec.title}
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.45rem",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "rgba(255,255,255,0.5)",
                          fontWeight: "500",
                        }}
                      >
                        {rec.year}
                      </span>
                      <span
                        style={{
                          width: "3px",
                          height: "3px",
                          borderRadius: "50%",
                          background: "rgba(255,255,255,0.28)",
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "rgba(255,255,255,0.5)",
                          fontWeight: "500",
                        }}
                      >
                        {rec.genre}
                      </span>
                    </div>
                  </div>
                </div>

                {/* CONTENT BODY */}
                <div
                  style={{
                    padding: "1.1rem 1.25rem 1.25rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.875rem",
                    flex: 1,
                  }}
                >
                  {/* IMDb + Platform */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        padding: "0.30rem",
                        borderRadius: "8px",
                        background: "rgba(255, 255, 255, 0.2)",
                        alignItems: "center",
                        gap: "0.45rem",
                      }}
                    >
                      <div
                        style={{
                          background: "#F5C518",
                          borderRadius: "4px",
                          padding: "0.12rem 0.38rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.18rem",
                        }}
                      >
                        <Star size={9} fill="#000" strokeWidth={0} />
                        <span
                          style={{
                            fontSize: "0.62rem",
                            fontWeight: "900",
                            color: "#000",
                            letterSpacing: "0.3px",
                          }}
                        >
                          IMDb
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: "0.88rem",
                          fontWeight: "700",
                          color: "#F5C518",
                        }}
                      >
                        {rec.imdbRating &&
                        typeof rec.imdbRating === "string" &&
                        rec.imdbRating.includes("/")
                          ? rec.imdbRating.split("/")[0]
                          : rec.imdbRating || rec.rating}
                      </span>
                      <span
                        style={{
                          fontSize: "0.68rem",
                          color: "rgba(249, 244, 244, 1)",
                        }}
                      >
                        /10
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.38rem",
                        background: "rgba(2, 47, 247, 0.1)",
                        border: "1px solid rgba(102,126,234,0.22)",
                        padding: "0.32rem 0.7rem",
                        borderRadius: "7px",
                      }}
                    >
                      <Monitor size={11} color="#a78bfa" strokeWidth={2} />
                      <span
                        style={{
                          fontSize: "0.72rem",
                          fontWeight: "600",
                          color: "rgba(255,255,255,0.72)",
                        }}
                      >
                        {rec.platform}
                      </span>
                    </div>
                  </div>

                  {/* AI Why */}
                  <div
                    style={{
                      padding: "0.8rem 0.9rem",
                      background: "rgb(43 78 236 / 6%)",
                      border: "1px solid rgba(102,126,234,0.14)",
                      borderLeft: "2px solid #667eea",
                      borderRadius: "0 9px 9px 0",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.45rem",
                      }}
                    >
                      <Sparkles
                        size={12}
                        color="#a78bfa"
                        strokeWidth={2}
                        style={{ flexShrink: 0, marginTop: "2px" }}
                      />
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.79rem",
                          lineHeight: "1.6",
                          color: "rgba(255,255,255,0.6)",
                          fontStyle: "italic",
                          fontWeight: "400",
                        }}
                      >
                        {rec.why}
                      </p>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.55rem",
                      marginTop: "auto",
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToWatchHistory(rec);
                      }}
                      style={{
                        width: "100%",
                        padding: "0.78rem",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        border: "none",
                        borderRadius: "10px",
                        color: "#fff",
                        fontWeight: "700",
                        fontSize: "0.83rem",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.45rem",
                        boxShadow: "0 4px 14px rgba(102,126,234,0.35)",
                        transition: "all 0.22s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.filter = "brightness(1.12)";
                        e.currentTarget.style.boxShadow =
                          "0 8px 22px rgba(102,126,234,0.5)";
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.filter = "brightness(1)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 14px rgba(102,126,234,0.35)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <Check size={14} strokeWidth={2.5} />
                      <span>Mark as Watched</span>
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToMyList(rec);
                      }}
                      style={{
                        width: "100%",
                        padding: "0.78rem",
                        background: "transparent",
                        border: "1px solid rgba(255,255,255,0.13)",
                        borderRadius: "10px",
                        color: "rgba(255,255,255,0.65)",
                        fontWeight: "600",
                        fontSize: "0.83rem",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.45rem",
                        transition: "all 0.22s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          "rgba(255,255,255,0.07)";
                        e.currentTarget.style.borderColor =
                          "rgba(255,255,255,0.25)";
                        e.currentTarget.style.color = "#fff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.borderColor =
                          "rgba(255,255,255,0.13)";
                        e.currentTarget.style.color = "rgba(255,255,255,0.65)";
                      }}
                    >
                      <BookmarkCheck size={14} strokeWidth={2} />
                      <span>Save to My List</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Toast Notification - ADD THIS ENTIRE BLOCK */}
        {toast && (
          <div
            style={{
              position: "fixed",
              top: "2rem",
              right: "50%",
              transform: "translateX(50%)",
              background:
                toast.type === "success"
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : toast.type === "info"
                    ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                    : "linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)",
              color: "#fff",
              padding: "1rem 2rem",
              borderRadius: "12px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
              zIndex: 10000,
              fontSize: "1rem",
              fontWeight: "600",
              animation: "slideDown 0.3s ease-out",
              maxWidth: "90%",
              textAlign: "center",
            }}
          >
            {toast.message}
          </div>
        )}
        ;{/* Auth Modal */}
        {showAuthModal && (
          <AuthModal
            onClose={() => setShowAuthModal(false)}
            onSuccess={(isNewUser) => {
              setShowAuthModal(false);
              loadUserData();
              if (isNewUser) {
                setTimeout(() => setShowPlanSelector(true), 500);
              }
            }}
          />
        )}
        ;
      </div>
    );
  }

  // MY LIST VIEW
  if (currentView === "mylist") {
    return (
      <MyListView
        items={myList}
        isLiked={checkIfLiked}
        onToggleLike={toggleLikeFromLibrary}
        onMarkWatched={markWatchedFromList}
        onRemove={removeFromMyList}
        onBack={goHome}
        toast={toast}
        overlay={libraryAuthModal}
      />
    );
  }

  // LIKED VIEW
  if (currentView === "liked") {
    return (
      <LikedView
        movies={likedMovies}
        onUnlike={handleUnlikeMovie}
        onBack={goHome}
        toast={toast}
        overlay={libraryAuthModal}
        search={{
          query: likedSearchQuery,
          suggestions: searchSuggestions,
          open: showSuggestions,
          loading: isSearching,
          onQueryChange: setLikedSearchQuery,
          onSearch: searchMoviesFromTMDB,
          onFocus: () => {
            if (searchSuggestions.length > 0) setShowSuggestions(true);
          },
          onClose: handleClickOutside,
          onSelect: handleSelectSuggestion,
          onSubmit: () => {
            if (likedSearchQuery.trim()) handleLikedMovieSearch();
          },
        }}
      />
    );
  }

  // ============================================
  // STATS DASHBOARD VIEW - PREMIUM ONLY
  // ============================================
  if (currentView === "stats") {
    // Redirect non-premium users
    if (!user || !isPremium) {
      return (
        <div
          style={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
          }}
        >
          <div style={{ textAlign: "center", color: "#fff" }}>
            <Crown size={64} style={{ marginBottom: "1rem" }} />
            <h2 style={{ fontSize: "2rem", marginBottom: "1rem" }}>
              Premium Feature
            </h2>
            <p style={{ opacity: 0.9, marginBottom: "2rem" }}>
              Stats Dashboard is available for Premium members only
            </p>
            <button
              onClick={() => setCurrentView("premium")}
              style={{
                padding: "1rem 2rem",
                background: "#fff",
                color: "#667eea",
                border: "none",
                borderRadius: "12px",
                fontWeight: "700",
                cursor: "pointer",
              }}
            >
              Upgrade to Premium
            </button>
          </div>
        </div>
      );
    }

    // Calculate stats
    const totalWatched = watchHistory.length;
    const totalInList = myList.length;
    const totalRecommendations = recommendationCount;

    // Genre breakdown
    const genreCounts = {};
    watchHistory.forEach((item) => {
      if (item.genre) {
        genreCounts[item.genre] = (genreCounts[item.genre] || 0) + 1;
      }
    });
    const topGenres = Object.entries(genreCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    // Content type breakdown
    const movieCount = watchHistory.filter(
      (item) => item.type === "Movie",
    ).length;
    const seriesCount = watchHistory.filter(
      (item) => item.type === "Series" || item.type === "TV Show",
    ).length;

    // Calculate total watch time (estimate: movies = 2h, series = 8h)
    const estimatedHours = movieCount * 2 + seriesCount * 8;

    // Most productive day
    const dayBreakdown = {};
    watchHistory.forEach((item) => {
      if (item.watchedAt) {
        const day = new Date(item.watchedAt).toLocaleDateString("en-US", {
          weekday: "long",
        });
        dayBreakdown[day] = (dayBreakdown[day] || 0) + 1;
      }
    });
    const mostActiveDay = Object.entries(dayBreakdown).sort(
      (a, b) => b[1] - a[1],
    )[0];

    // Streak info
    const currentStreak = todayStreak;

    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          fontFamily: '"DM Sans", sans-serif',
          color: "#fff",
          padding: "2rem",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {/* Header */}
          <div style={{ marginBottom: "3rem", textAlign: "center" }}>
            <h1
              style={{
                fontSize: "3rem",
                fontWeight: "800",
                marginBottom: "0.5rem",
              }}
            >
              <TrendingUp size={28} style={{ marginRight: "0.5rem" }} /> Your
              Stats
            </h1>
            <p style={{ fontSize: "1.2rem", opacity: 0.9 }}>
              Your entertainment journey in numbers
            </p>
          </div>

          {/* Main Stats Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "1.5rem",
              marginBottom: "3rem",
            }}
            className="noryq-plans-grid"
          >
            {/* Total Watched */}
            <div
              style={{
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                padding: "2rem",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  marginBottom: "0.5rem",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Monitor size={48} style={{ opacity: 0.9 }} />
              </div>
              <div
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "800",
                  marginBottom: "0.5rem",
                }}
              >
                {totalWatched}
              </div>
              <div style={{ opacity: 0.8 }}>Total Watched</div>
            </div>

            {/* Recommendations */}
            <div
              style={{
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                padding: "2rem",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  marginBottom: "0.5rem",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Pencil size={48} style={{ opacity: 0.9 }} />
              </div>
              <div
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "800",
                  marginBottom: "0.5rem",
                }}
              >
                {totalRecommendations}
              </div>
              <div style={{ opacity: 0.8 }}>Recommendations</div>
            </div>

            {/* My List */}
            <div
              style={{
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                padding: "2rem",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  marginBottom: "0.5rem",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <BookmarkCheck size={48} style={{ opacity: 0.9 }} />
              </div>
              <div
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "800",
                  marginBottom: "0.5rem",
                }}
              >
                {totalInList}
              </div>
              <div style={{ opacity: 0.8 }}>In My List</div>
            </div>

            {/* Current Streak */}
            <div
              style={{
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                padding: "2rem",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  marginBottom: "0.5rem",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Flame size={48} color="#f5576c" style={{ opacity: 0.9 }} />
              </div>
              <div
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "800",
                  marginBottom: "0.5rem",
                }}
              >
                {currentStreak}
              </div>
              <div style={{ opacity: 0.8 }}>Day Streak</div>
            </div>
          </div>

          {/* Content Breakdown */}
          <div
            style={{
              background: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              padding: "2rem",
              marginBottom: "2rem",
            }}
          >
            <h2
              style={{
                fontSize: "1.75rem",
                fontWeight: "700",
                marginBottom: "1.5rem",
              }}
            >
              <Film size={24} style={{ marginRight: "0.5rem" }} /> Content
              Breakdown
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "2rem",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "2rem",
                    fontWeight: "700",
                    marginBottom: "0.5rem",
                  }}
                >
                  {movieCount}
                </div>
                <div style={{ opacity: 0.8 }}>Movies</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "2rem",
                    fontWeight: "700",
                    marginBottom: "0.5rem",
                  }}
                >
                  {seriesCount}
                </div>
                <div style={{ opacity: 0.8 }}>TV Shows/Series</div>
              </div>
            </div>

            <div
              style={{
                marginTop: "2rem",
                paddingTop: "2rem",
                borderTop: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              <div
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  marginBottom: "0.5rem",
                }}
              >
                ~{estimatedHours} hours
              </div>
              <div style={{ opacity: 0.8 }}>Estimated watch time</div>
            </div>
          </div>

          {/* Top Genres */}
          {topGenres.length > 0 && (
            <div
              style={{
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                padding: "2rem",
                marginBottom: "2rem",
              }}
            >
              <h2
                style={{
                  fontSize: "1.75rem",
                  fontWeight: "700",
                  marginBottom: "1.5rem",
                }}
              >
                <Layers
                  size={24}
                  style={{ marginRight: "0.5rem", flexShrink: 0 }}
                />{" "}
                Top Genres
              </h2>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                {topGenres.map(([genre, count], idx) => (
                  <div key={genre}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <span style={{ fontWeight: "600" }}>
                        {idx + 1}. {genre}
                      </span>
                      <span>
                        {count} {count === 1 ? "item" : "items"}
                      </span>
                    </div>
                    <div
                      style={{
                        height: "8px",
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: "4px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          background:
                            "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                          width: `${(count / totalWatched) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity Insights */}
          <div
            style={{
              background: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              padding: "2rem",
              marginBottom: "2rem",
            }}
          >
            <h2
              style={{
                fontSize: "1.75rem",
                fontWeight: "700",
                marginBottom: "1.5rem",
              }}
            >
              <Activity size={24} style={{ marginRight: "0.5rem" }} /> Activity
              Insights
            </h2>
            {mostActiveDay && (
              <div style={{ marginBottom: "1.5rem" }}>
                <div
                  style={{
                    fontSize: "1.2rem",
                    fontWeight: "600",
                    marginBottom: "0.5rem",
                  }}
                >
                  Most Active Day
                </div>
                <div style={{ fontSize: "2rem", fontWeight: "700" }}>
                  {mostActiveDay[0]}
                </div>
                <div style={{ opacity: 0.8 }}>
                  {mostActiveDay[1]}{" "}
                  {mostActiveDay[1] === 1 ? "watch" : "watches"}
                </div>
              </div>
            )}

            <div>
              <div
                style={{
                  fontSize: "1.2rem",
                  fontWeight: "600",
                  marginBottom: "0.5rem",
                }}
              >
                Average per Recommendation
              </div>
              <div style={{ fontSize: "2rem", fontWeight: "700" }}>
                {totalRecommendations > 0
                  ? (totalWatched / totalRecommendations).toFixed(1)
                  : 0}
              </div>
              <div style={{ opacity: 0.8 }}>
                items watched per recommendation session
              </div>
            </div>
          </div>

          {/* Motivational Card */}
          <div
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              borderRadius: "16px",
              padding: "2rem",
              textAlign: "center",
            }}
          >
            <h3
              style={{
                fontSize: "1.5rem",
                fontWeight: "700",
                marginBottom: "1rem",
              }}
            >
              {totalWatched >= 50
                ? "You're a NORYQ Legend!"
                : totalWatched >= 20
                  ? "Amazing Progress!"
                  : totalWatched >= 10
                    ? "You're on a Roll!"
                    : "Keep Discovering!"}
            </h3>
            <p style={{ fontSize: "1.1rem", opacity: 0.95 }}>
              {totalWatched >= 50
                ? "You've watched 50+ items! Your taste in entertainment is exceptional."
                : totalWatched >= 20
                  ? "You've discovered 20+ great titles. Keep up the momentum!"
                  : totalWatched >= 10
                    ? "10+ watches and counting! Your entertainment journey is thriving."
                    : "Every great journey starts with the first step. Keep exploring!"}
            </p>
          </div>
        </div>

        <Footer onNavigate={setCurrentView} />
        <InstallPrompt />
      </div>
    );
  }
};
export default EntertainmentRecommender;
